import React, { useState, useMemo, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea, BarChart, Bar, Legend } from 'recharts';
import { AlertTriangle, CheckCircle, TrendingUp, Settings, FileText, BarChart3, Database, Users, Home, Search, Filter, Activity, Download, Clock } from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// 전역 스타일
const globalStyle = {
  fontFamily: 'Arial, Helvetica, sans-serif',
};

const THEME = { primary: '#0066B3', primaryDark: '#1a2744', accent: '#00A0B0', success: '#10B981', warning: '#F59E0B', danger: '#DC2626' };

const PRODUCTS = ['C4F8', 'C3F6', 'CHF3', 'C2H2F4', 'CH2F2', 'PF3', 'CF4', 'COS'];
const COA_LIST = ['Air', '수분', '순도', '산도', 'Metal', 'Sulfur'];
const IMPURITIES = ['CH4','CO','CO2','H2','O2+Ar','N2','H2O','Total OFC','C3HF5','C3F8','HF','HCl','Fe','Na','Ca','Metal Total','H2S','CS2'];

const LIMITS = {
  // Air
  'CH4': { USL: 5, UCL: 3.75, avg: 0.15, mu3sigma: 0.45, coa: 'Air' },
  'CO': { USL: 3, UCL: 2.25, avg: 0.08, mu3sigma: 0.24, coa: 'Air' },
  'CO2': { USL: 5, UCL: 3.75, avg: 0.24, mu3sigma: 0.77, coa: 'Air' },
  'N2': { USL: 25, UCL: 18.75, avg: 2.31, mu3sigma: 8.44, coa: 'Air' },
  'O2+Ar': { USL: 10, UCL: 7.5, avg: 0.24, mu3sigma: 1.13, coa: 'Air' },
  'H2': { USL: 2, UCL: 1.5, avg: 0.05, mu3sigma: 0.15, coa: 'Air' },
  // 수분
  'H2O': { USL: 8, UCL: 6, avg: 1.24, mu3sigma: 2.64, coa: '수분' },
  'H2O(ppmv)': { USL: 3, UCL: 2.25, avg: 0.85, mu3sigma: 1.52, coa: '수분' },
  // 순도
  'Total OFC': { USL: 900, UCL: 675, avg: 149.42, mu3sigma: 166.91, coa: '순도' },
  'C3HF5': { USL: 300, UCL: 225, avg: 117.81, mu3sigma: 131.95, coa: '순도' },
  'C3F8': { USL: 200, UCL: 150, avg: 11.78, mu3sigma: 18.20, coa: '순도' },
  'OFC': { USL: 500, UCL: 375, avg: 85.5, mu3sigma: 102.3, coa: '순도' },
  // 산도
  'HF': { USL: 1, UCL: 0.75, avg: 0.12, mu3sigma: 0.28, coa: '산도' },
  'HCl': { USL: 1, UCL: 0.75, avg: 0.08, mu3sigma: 0.22, coa: '산도' },
  // Metal
  'Fe': { USL: 1, UCL: 0.75, avg: 0.05, mu3sigma: 0.12, coa: 'Metal' },
  'Na': { USL: 1, UCL: 0.75, avg: 0.03, mu3sigma: 0.09, coa: 'Metal' },
  'Metal Total': { USL: 15, UCL: 11.25, avg: 0.85, mu3sigma: 2.1, coa: 'Metal' },
  // Sulfur
  'H2S': { USL: 1, UCL: 0.75, avg: 0.02, mu3sigma: 0.06, coa: 'Sulfur' },
  'CS2': { USL: 1, UCL: 0.75, avg: 0.01, mu3sigma: 0.04, coa: 'Sulfur' },
};

const NELSON_RULES = [{id:1,name:'Rule 1'},{id:2,name:'Rule 2'},{id:3,name:'Rule 3'},{id:4,name:'Rule 4'},{id:5,name:'Rule 5'},{id:6,name:'Rule 6'},{id:7,name:'Rule 7'},{id:8,name:'Rule 8'}];
const SEC_RULES = [{id:'C1',name:'SEC-1',sev:'warning'},{id:'C2',name:'SEC-2',sev:'warning'},{id:'C3',name:'SEC-3',sev:'caution'},{id:'C4',name:'SEC-4',sev:'caution'},{id:'C5',name:'SEC-5',sev:'critical'}];

// LOT 정보
const LOT_INFO = [
  { id: '26Y3A5ZP31', date: '25/12/25', count: 22, color: '#FFFFFF' },
  { id: '26Y3A61C3I', date: '26/01/12', count: 15, color: '#E0F2FE' },
];

// 샘플 데이터 생성 (LOT별 실린더 번호 포함)
const genChartData = (avg, variance) => {
  const data = [];
  let idx = 0;
  
  // LOT 1: 26Y3A5ZP31 (22개)
  for (let i = 0; i < 22; i++) {
    idx++;
    const cylNo = (25819 + i).toString();
    data.push({
      idx,
      cylLabel: idx + '_21ENKE' + cylNo,
      y: avg + (Math.random() - 0.5) * variance * 0.3,
      rawY: avg * 0.8 + (Math.random() - 0.5) * variance * 0.2,
      lotId: '26Y3A5ZP31',
      lotDate: '25/12/25',
      cylNo: '21ENKE' + cylNo,
    });
  }
  
  // LOT 2: 26Y3A61C3I (15개) - 값이 좀 더 높음
  for (let i = 0; i < 15; i++) {
    idx++;
    const cylNo = (25714 + i).toString();
    data.push({
      idx,
      cylLabel: idx + '_21ENKE' + cylNo,
      y: avg * 1.8 + (Math.random() - 0.3) * variance * 0.3,
      rawY: avg * 2.2 + (Math.random() - 0.3) * variance * 0.3,
      lotId: '26Y3A61C3I',
      lotDate: '26/01/12',
      cylNo: '21ENKE' + cylNo,
    });
  }
  
  return data;
};

const Sidebar = ({menu,setMenu}) => {
  const items = [
    {id:'dashboard',name:'대시보드',I:Home},
    {id:'master',name:'기본정보관리',I:Database},
    {id:'monitoring',name:'모니터링',I:Activity},
    {id:'spc',name:'SPC 분석',I:BarChart3},
    {id:'trend',name:'품질경향분석',I:TrendingUp},
    {id:'summary',name:'품질 집계',I:BarChart3},
    {id:'improve',name:'개선대책',I:FileText},
    {id:'customer',name:'고객요구사항',I:Settings},
    {id:'permission',name:'사용자 권한관리',I:Users},
  ];
  return (
    <div className="w-52 min-h-screen flex flex-col" style={{backgroundColor:THEME.primaryDark}}>
      <div className="p-4 border-b border-slate-700">
        <div className="text-white font-bold text-lg"><span style={{color:THEME.primary}}>▶</span> DAIKIN</div>
        <div className="text-xs" style={{color:THEME.accent}}>SPC Trend System</div>
      </div>
      <nav className="flex-1 p-2">
        {items.map(it=>{const Icon=it.I;return(
          <button key={it.id} onClick={()=>setMenu(it.id)} className={'w-full flex items-center gap-2 px-3 py-2 rounded mb-1 text-sm '+(menu===it.id?'text-white':'text-slate-400 hover:bg-slate-700')} style={menu===it.id?{backgroundColor:THEME.primary}:{}}>
            <Icon size={16}/><span>{it.name}</span>
          </button>
        )})}
      </nav>
      <div className="p-3 text-xs text-slate-500">© 아큐라시스템</div>
    </div>
  );
};

const Header = ({title}) => (
  <header className="bg-white border-b px-5 py-2 flex items-center justify-between">
    <h1 className="font-semibold" style={{color:THEME.primaryDark}}>{title}</h1>
    <div className="flex items-center gap-3 text-sm">
      <span className="text-slate-500">2026년 1월 17일</span>
      <div className="flex items-center gap-2 px-2 py-1 bg-slate-50 rounded">
        <div className="w-6 h-6 rounded-full text-white text-xs flex items-center justify-center" style={{backgroundColor:THEME.primary}}>관</div>
        <span>관리자님</span>
      </div>
    </div>
  </header>
);

const Card = ({title,value,sub}) => (
  <div className="bg-white rounded-lg p-4 border">
    <p className="text-sm text-slate-500">{title}</p>
    <p className="text-2xl font-bold" style={{color:THEME.primaryDark}}>{value}</p>
    {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
  </div>
);

const Chart = ({title, lim, showReview}) => {
  const [scale, setScale] = useState('spec');
  const [zoom, setZoom] = useState(1);
  const [showRaw, setShowRaw] = useState(true);
  const [yMin, setYMin] = useState('');
  const [yMax, setYMax] = useState('');
  const data = useMemo(() => genChartData(lim.avg, lim.avg), [lim]);
  
  const dom = scale === 'custom' && yMin !== '' && yMax !== ''
    ? [parseFloat(yMin), parseFloat(yMax)]
    : scale === 'auto' 
      ? [Math.min(...data.map(d => d.y)) * 0.8, Math.max(...data.map(d => d.y)) * 1.2] 
      : [0, lim.USL * 1.2 / zoom];

  // LOT 영역 계산
  const lot1End = 22;
  const lot2Start = 23;
  const lot2End = 37;

  // X축 틱 포맷터
  const formatXAxis = (value) => {
    const item = data.find(d => d.idx === value);
    return item ? item.cylLabel : value;
  };

  return (
    <div className="bg-white rounded-lg border">
      {/* 상단 통계 테이블 */}
      <div className="border-b">
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="px-4 py-1.5 text-center border-r font-medium">UCL</th>
              <th className="px-4 py-1.5 text-center border-r font-medium">AVG</th>
              <th className="px-4 py-1.5 text-center border-r font-medium">μ+3σ</th>
              <th className="px-4 py-1.5 text-center font-medium">Cpk</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-4 py-1.5 text-center border-r">{lim.UCL}</td>
              <td className="px-4 py-1.5 text-center border-r">{lim.avg}</td>
              <td className="px-4 py-1.5 text-center border-r">{lim.mu3sigma}</td>
              <td className="px-4 py-1.5 text-center text-blue-600 font-medium">
                {((lim.USL - lim.avg) / (lim.mu3sigma - lim.avg)).toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* 차트 제목 */}
      <div className="px-4 py-2 text-center border-b">
        <span className="font-semibold text-lg">{title}</span>
      </div>

      {/* Y축 컨트롤 및 오버레이 옵션 */}
      <div className="px-4 py-2 border-b bg-slate-50 flex items-center justify-between text-xs">
        <div className="flex items-center gap-3">
          <span>Y축:</span>
          {['spec', 'auto', 'custom'].map(m => (
            <button 
              key={m} 
              onClick={() => setScale(m)} 
              className={'px-2 py-1 rounded ' + (scale === m ? 'bg-blue-600 text-white' : 'bg-white border')}
            >
              {m === 'spec' ? '규격' : m === 'auto' ? '자동' : '사용자'}
            </button>
          ))}
          {scale === 'custom' && (
            <div className="flex items-center gap-1 ml-2">
              <input 
                type="number" 
                placeholder="최소" 
                value={yMin} 
                onChange={e => setYMin(e.target.value)}
                className="w-14 px-1 py-0.5 border rounded text-center"
              />
              <span>~</span>
              <input 
                type="number" 
                placeholder="최대" 
                value={yMax} 
                onChange={e => setYMax(e.target.value)}
                className="w-14 px-1 py-0.5 border rounded text-center"
              />
            </div>
          )}
          {scale !== 'custom' && (
            <>
              <span className="ml-3">확대:</span>
              <button onClick={() => setZoom(z => Math.max(1, z - 0.5))} className="w-5 h-5 bg-white border rounded">-</button>
              <span className="w-8 text-center">{zoom * 100}%</span>
              <button onClick={() => setZoom(z => Math.min(5, z + 0.5))} className="w-5 h-5 bg-white border rounded">+</button>
            </>
          )}
        </div>
        
        {/* Raw 오버레이 체크박스 */}
        <label className="flex items-center gap-2 cursor-pointer px-3 py-1 rounded border bg-white hover:bg-slate-50">
          <input 
            type="checkbox" 
            checked={showRaw} 
            onChange={(e) => setShowRaw(e.target.checked)}
            className="rounded"
          />
          <span className="flex items-center gap-1">
            <span className="w-3 h-0.5 bg-green-500"></span>
            Raw_{title} 오버레이
          </span>
        </label>
      </div>

      {/* 차트 */}
      <div className="p-3">
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            
            {/* LOT 영역 구분 배경 */}
            <ReferenceArea x1={1} x2={lot1End} fill="#FFFFFF" fillOpacity={1} />
            <ReferenceArea x1={lot2Start} x2={lot2End} fill="#E0F2FE" fillOpacity={0.8} />
            
            <XAxis 
              dataKey="idx" 
              tick={{ fontSize: 8, angle: -45, textAnchor: 'end' }}
              tickFormatter={formatXAxis}
              interval={0}
              height={60}
            />
            <YAxis domain={dom} tick={{ fontSize: 10 }} />
            
            <Tooltip content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const d = payload[0].payload;
                return (
                  <div className="bg-white p-2 rounded shadow border text-xs">
                    <p className="font-medium">{d.cylNo}</p>
                    <p>측정값: {d.y.toFixed(2)}</p>
                    {showRaw && <p className="text-green-600">Raw: {d.rawY.toFixed(2)}</p>}
                    <p className="text-slate-500">LOT: {d.lotId}</p>
                  </div>
                );
              }
              return null;
            }} />
            
            {/* 관리선 */}
            <ReferenceLine y={lim.USL} stroke="#EF4444" strokeWidth={2} label={{ value: 'Spec', position: 'right', fontSize: 10 }} />
            <ReferenceLine y={lim.UCL} stroke="#1E40AF" strokeWidth={1.5} label={{ value: 'UCL', position: 'right', fontSize: 10 }} />
            <ReferenceLine y={lim.avg} stroke="#EAB308" strokeDasharray="5 5" />
            <ReferenceLine y={lim.mu3sigma} stroke="#EC4899" strokeDasharray="3 3" />
            
            {/* 데이터 라인 */}
            <Line type="monotone" dataKey="y" stroke="#1E40AF" strokeWidth={2} dot={{ r: 3, fill: '#1E40AF' }} name={title} />
            {showRaw && (
              <Line type="monotone" dataKey="rawY" stroke="#22C55E" strokeWidth={2} dot={{ r: 3, fill: '#22C55E' }} name={'Raw_' + title} />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* LOT 정보 표시 */}
      <div className="px-4 py-2 border-t flex justify-center gap-8 text-xs">
        <div className="text-center">
          <div className="font-medium">26Y3A5ZP31</div>
          <div className="text-slate-500">25/12/25</div>
        </div>
        <div className="text-center px-4 py-1 rounded" style={{ backgroundColor: '#E0F2FE' }}>
          <div className="font-medium">26Y3A61C3I</div>
          <div className="text-slate-500">26/01/12</div>
        </div>
      </div>

      {/* 범례 */}
      <div className="px-4 pb-2 flex justify-center gap-4 text-xs border-t pt-2">
        <span className="flex items-center gap-1"><span className="w-4 h-0.5 bg-blue-800"></span>{title}</span>
        <span className="flex items-center gap-1"><span className="w-4 h-0.5 bg-red-500"></span>Spec</span>
        <span className="flex items-center gap-1"><span className="w-4 h-0.5 bg-blue-900"></span>UCL</span>
        <span className="flex items-center gap-1"><span className="w-4 h-0.5 bg-yellow-500"></span>AVG</span>
        <span className="flex items-center gap-1"><span className="w-4 h-0.5 bg-pink-500"></span>μ+3σ</span>
        {showRaw && <span className="flex items-center gap-1"><span className="w-4 h-0.5 bg-green-500"></span>Raw_{title}</span>}
      </div>

      {/* 검토의견 (옵션) */}
      {showReview && (
        <div className="mx-4 mb-4 p-3 bg-blue-50 rounded border border-blue-200 text-sm">
          <div className="font-medium text-blue-800 mb-1">[검토의견]</div>
          <ol className="text-blue-700 space-y-1 text-xs list-decimal list-inside">
            <li>충전 후 분석 결과에서 불순물 농도는 IQC 대비 감소 경향을 보였으나, 이전 충전 이력 대비 다소 높은 수준으로 확인.</li>
            <li>IQC 단계에서부터 불순물 농도가 상대적으로 높게 확인된 점을 고려할 때, 원료 특성에 따른 영향 가능성이 높은 것으로 판단</li>
          </ol>
        </div>
      )}
    </div>
  );
};

// LOT 선택에 따라 데이터가 변경되는 차트 컴포넌트 (이상 감지 포함)
const ChartWithLot = ({title, lim, product, selectedLot, lots, nelsonRules = [], secRules = [], onDetailClick}) => {
  const [scale, setScale] = useState('spec');
  const [zoom, setZoom] = useState(1);
  const [showRaw, setShowRaw] = useState(true);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [yMin, setYMin] = useState('');
  const [yMax, setYMax] = useState('');
  
  // 선택된 LOT에 따라 데이터 생성 (이상 데이터 포함)
  const data = useMemo(() => {
    if (selectedLot === '전체') {
      return genChartDataWithAbnormal(lots, lim, product, title);
    } else {
      const lot = lots.find(l => l.id === selectedLot);
      if (lot) {
        return genSingleLotDataWithAbnormal(lot, lim, product, title);
      }
      return [];
    }
  }, [selectedLot, lots, lim, product, title]);

  // 선택된 룰에 해당하는지 체크하는 함수
  const isRuleSelected = (ruleStr) => {
    if (!ruleStr) return false;
    // OOS, OOC는 항상 표시 (규격/관리한계 초과)
    if (ruleStr === 'Spec 초과') return true;
    // Nelson Rule 체크
    for (let i = 1; i <= 8; i++) {
      if (ruleStr.includes(`Rule${i}:`) && nelsonRules.includes(i)) return true;
    }
    // SEC Rule 체크
    if (ruleStr.includes('SEC-')) {
      const match = ruleStr.match(/SEC-(R\d)/);
      if (match && secRules.includes(match[1])) return true;
    }
    return false;
  };

  // 이상 데이터 필터링 (선택된 룰 기준)
  const abnormalPoints = useMemo(() => {
    return data.filter(d => d.abnormalType && isRuleSelected(d.abnormalRule));
  }, [data, nelsonRules, secRules]);

  // 이상 유형별 카운트
  const abnormalCounts = useMemo(() => {
    const counts = { OOS: 0, OOC: 0, OOT: 0, SEC: 0 };
    abnormalPoints.forEach(p => {
      if (p.abnormalType) counts[p.abnormalType]++;
    });
    return counts;
  }, [abnormalPoints]);

  // LOT 영역 계산 (전체 선택 시)
  const lotAreas = useMemo(() => {
    if (selectedLot !== '전체') return [];
    const areas = [];
    let startIdx = 1;
    lots.forEach((lot, i) => {
      areas.push({
        x1: startIdx,
        x2: startIdx + lot.cyls - 1,
        lotId: lot.id,
        lotDate: lot.date,
        color: i % 2 === 0 ? '#FFFFFF' : '#E0F2FE',
      });
      startIdx += lot.cyls;
    });
    return areas;
  }, [selectedLot, lots]);

  const dom = scale === 'custom' && yMin !== '' && yMax !== ''
    ? [parseFloat(yMin), parseFloat(yMax)]
    : scale === 'auto' 
      ? [Math.min(...data.map(d => d.y)) * 0.8, Math.max(...data.map(d => d.y)) * 1.2] 
      : [0, lim.USL * 1.2 / zoom];

  const formatXAxis = (value) => {
    const item = data.find(d => d.idx === value);
    return item ? item.cylLabel : value;
  };

  // 이상 점 색상
  const getAbnormalColor = (type) => {
    switch(type) {
      case 'OOS': return '#DC2626'; // 빨강
      case 'OOC': return '#F59E0B'; // 주황
      case 'OOT': return '#3B82F6'; // 파랑
      case 'SEC': return '#06B6D4'; // 시안
      default: return '#1E40AF';
    }
  };

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-lg border p-8 text-center">
        <p className="text-slate-500">데이터가 없습니다</p>
      </div>
    );
  }

  const hasAbnormal = abnormalPoints.length > 0;

  return (
    <div className={'bg-white rounded-lg border ' + (hasAbnormal ? 'ring-2 ring-red-200' : '')}>
      {/* 이상 발생 알림 배너 */}
      {hasAbnormal && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-red-500" />
            <span className="text-sm font-medium text-red-700">이상 감지: {abnormalPoints.length}건</span>
            <div className="flex gap-2 ml-2">
              {abnormalCounts.OOS > 0 && (
                <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">OOS: {abnormalCounts.OOS}</span>
              )}
              {abnormalCounts.OOC > 0 && (
                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">OOC: {abnormalCounts.OOC}</span>
              )}
              {abnormalCounts.OOT > 0 && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">OOT: {abnormalCounts.OOT}</span>
              )}
              {abnormalCounts.SEC > 0 && (
                <span className="px-2 py-0.5 bg-cyan-100 text-cyan-700 rounded text-xs font-medium">SEC: {abnormalCounts.SEC}</span>
              )}
            </div>
          </div>
          <button 
            onClick={() => onDetailClick && onDetailClick({ item: title, product, lot: abnormalPoints[0]?.lotId, abnormalPoints })}
            className="text-xs text-red-600 hover:underline"
          >상세 보기 →</button>
        </div>
      )}

      {/* 상단 통계 테이블 */}
      <div className="border-b">
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="px-4 py-1.5 text-center border-r font-medium">UCL</th>
              <th className="px-4 py-1.5 text-center border-r font-medium">AVG</th>
              <th className="px-4 py-1.5 text-center border-r font-medium">μ+3σ</th>
              <th className="px-4 py-1.5 text-center font-medium">Cpk</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-4 py-1.5 text-center border-r">{lim.UCL}</td>
              <td className="px-4 py-1.5 text-center border-r">{lim.avg}</td>
              <td className="px-4 py-1.5 text-center border-r">{lim.mu3sigma}</td>
              <td className="px-4 py-1.5 text-center text-blue-600 font-medium">
                {((lim.USL - lim.avg) / (lim.mu3sigma - lim.avg)).toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* 차트 제목 */}
      <div className="px-4 py-2 text-center border-b flex items-center justify-center gap-2">
        <span className="font-semibold text-lg">{title}</span>
        <span className="text-xs text-slate-500">({product})</span>
        {hasAbnormal && (
          <span className="px-2 py-0.5 bg-red-500 text-white rounded text-xs animate-pulse">이상</span>
        )}
      </div>

      {/* Y축 컨트롤 및 오버레이 옵션 */}
      <div className="px-4 py-2 border-b bg-slate-50 flex items-center justify-between text-xs">
        <div className="flex items-center gap-3">
          <span>Y축:</span>
          {['spec', 'auto', 'custom'].map(m => (
            <button 
              key={m} 
              onClick={() => setScale(m)} 
              className={'px-2 py-1 rounded ' + (scale === m ? 'bg-blue-600 text-white' : 'bg-white border')}
            >
              {m === 'spec' ? '규격' : m === 'auto' ? '자동' : '사용자'}
            </button>
          ))}
          {scale === 'custom' && (
            <div className="flex items-center gap-1 ml-2">
              <input 
                type="number" 
                placeholder="최소" 
                value={yMin} 
                onChange={e => setYMin(e.target.value)}
                className="w-14 px-1 py-0.5 border rounded text-center"
              />
              <span>~</span>
              <input 
                type="number" 
                placeholder="최대" 
                value={yMax} 
                onChange={e => setYMax(e.target.value)}
                className="w-14 px-1 py-0.5 border rounded text-center"
              />
            </div>
          )}
          {scale !== 'custom' && (
            <>
              <span className="ml-3">확대:</span>
              <button onClick={() => setZoom(z => Math.max(1, z - 0.5))} className="w-5 h-5 bg-white border rounded">-</button>
              <span className="w-8 text-center">{zoom * 100}%</span>
              <button onClick={() => setZoom(z => Math.min(5, z + 0.5))} className="w-5 h-5 bg-white border rounded">+</button>
            </>
          )}
        </div>
        
        <label className="flex items-center gap-2 cursor-pointer px-3 py-1 rounded border bg-white hover:bg-slate-50">
          <input type="checkbox" checked={showRaw} onChange={(e) => setShowRaw(e.target.checked)} className="rounded"/>
          <span className="flex items-center gap-1">
            <span className="w-3 h-0.5 bg-green-500"></span>
            Raw 오버레이
          </span>
        </label>
      </div>

      {/* 차트 */}
      <div className="p-3">
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            
            {/* LOT 영역 구분 배경 (전체 선택 시) */}
            {lotAreas.map((area, i) => (
              <ReferenceArea key={i} x1={area.x1} x2={area.x2} fill={area.color} fillOpacity={0.8} />
            ))}
            
            <XAxis 
              dataKey="idx" 
              tick={{ fontSize: 7, angle: -45, textAnchor: 'end' }}
              tickFormatter={formatXAxis}
              interval={Math.floor(data.length / 15)}
              height={60}
            />
            <YAxis domain={dom} tick={{ fontSize: 10 }} />
            
            <Tooltip content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const d = payload[0].payload;
                const isAbnormalSelected = d.abnormalType && isRuleSelected(d.abnormalRule);
                return (
                  <div className={'p-2 rounded shadow border text-xs ' + (isAbnormalSelected ? 'bg-red-50 border-red-300' : 'bg-white')}>
                    <p className="font-medium">{d.cylNo}</p>
                    <p>측정값: <span className={isAbnormalSelected ? 'text-red-600 font-bold' : ''}>{d.y}</span></p>
                    {showRaw && <p className="text-green-600">Raw: {d.rawY}</p>}
                    <p className="text-slate-500">LOT: {d.lotId}</p>
                    {isAbnormalSelected && (
                      <div className="mt-1 pt-1 border-t border-red-200">
                        <p className={'font-medium ' + 
                          (d.abnormalType === 'OOS' ? 'text-red-600' : 
                           d.abnormalType === 'OOC' ? 'text-yellow-600' : 
                           d.abnormalType === 'OOT' ? 'text-blue-600' : 'text-cyan-600')}>
                          ⚠️ {d.abnormalType}: {d.abnormalRule}
                        </p>
                      </div>
                    )}
                  </div>
                );
              }
              return null;
            }} />
            
            {/* 관리선 */}
            <ReferenceLine y={lim.USL} stroke="#EF4444" strokeWidth={2} label={{ value: 'USL', position: 'right', fontSize: 10 }} />
            <ReferenceLine y={lim.UCL} stroke="#1E40AF" strokeWidth={1.5} label={{ value: 'UCL', position: 'right', fontSize: 10 }} />
            <ReferenceLine y={lim.avg} stroke="#EAB308" strokeDasharray="5 5" />
            <ReferenceLine y={lim.mu3sigma} stroke="#EC4899" strokeDasharray="3 3" />
            
            {/* 데이터 라인 */}
            <Line 
              type="monotone" 
              dataKey="y" 
              stroke="#1E40AF" 
              strokeWidth={2} 
              dot={(props) => {
                const { cx, cy, payload } = props;
                // 선택된 룰에 해당하는 이상만 표시
                const isAbnormalSelected = payload.abnormalType && isRuleSelected(payload.abnormalRule);
                if (isAbnormalSelected) {
                  return (
                    <g key={payload.idx}>
                      <circle cx={cx} cy={cy} r={6} fill={getAbnormalColor(payload.abnormalType)} stroke="#fff" strokeWidth={2} />
                      <circle cx={cx} cy={cy} r={10} fill="none" stroke={getAbnormalColor(payload.abnormalType)} strokeWidth={1} strokeDasharray="2 2" />
                    </g>
                  );
                }
                return <circle key={payload.idx} cx={cx} cy={cy} r={2} fill="#1E40AF" />;
              }}
            />
            {showRaw && (
              <Line type="monotone" dataKey="rawY" stroke="#22C55E" strokeWidth={2} dot={{ r: 2, fill: '#22C55E' }} />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* 이상 발생 목록 (이상이 있을 때만 표시) */}
      {hasAbnormal && (
        <div className="mx-3 mb-3 p-3 bg-red-50 rounded border border-red-200">
          <div className="text-sm font-medium text-red-700 mb-2">이상 발생 목록</div>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {abnormalPoints.map((p, i) => (
              <div key={i} className="flex items-center justify-between text-xs p-1.5 bg-white rounded border">
                <div className="flex items-center gap-2">
                  <span className={'px-1.5 py-0.5 rounded font-medium text-white ' + 
                    (p.abnormalType === 'OOS' ? 'bg-red-500' : 
                     p.abnormalType === 'OOC' ? 'bg-yellow-500' : 
                     p.abnormalType === 'OOT' ? 'bg-blue-500' : 'bg-cyan-500')}>
                    {p.abnormalType}
                  </span>
                  <span className="font-medium">{p.cylNo}</span>
                  <span className="text-red-600 font-bold">{p.y}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-500">
                  <span>{p.abnormalRule}</span>
                  <span>LOT: {p.lotId}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* LOT 정보 표시 */}
      <div className="px-4 py-2 border-t flex justify-center gap-4 text-xs flex-wrap">
        {selectedLot === '전체' ? (
          lots.map((lot, i) => (
            <div key={lot.id} className="text-center px-3 py-1 rounded" style={{ backgroundColor: i % 2 === 0 ? '#FFFFFF' : '#E0F2FE', border: '1px solid #E2E8F0' }}>
              <div className="font-medium">{lot.id}</div>
              <div className="text-slate-500">{lot.date} ({lot.cyls})</div>
            </div>
          ))
        ) : (
          <div className="text-center px-3 py-1 rounded bg-blue-50 border border-blue-200">
            <div className="font-medium">{selectedLot}</div>
            <div className="text-slate-500">
              {lots.find(l => l.id === selectedLot)?.date} ({lots.find(l => l.id === selectedLot)?.cyls} CYL)
            </div>
          </div>
        )}
      </div>

      {/* 범례 */}
      <div className="px-4 pb-2 flex justify-center gap-4 text-xs border-t pt-2 flex-wrap">
        <span className="flex items-center gap-1"><span className="w-4 h-0.5 bg-blue-800"></span>{title}</span>
        <span className="flex items-center gap-1"><span className="w-4 h-0.5 bg-red-500"></span>Spec</span>
        <span className="flex items-center gap-1"><span className="w-4 h-0.5 bg-blue-900"></span>UCL</span>
        <span className="flex items-center gap-1"><span className="w-4 h-0.5 bg-yellow-500"></span>AVG</span>
        <span className="flex items-center gap-1"><span className="w-4 h-0.5 bg-pink-500"></span>μ+3σ</span>
        {showRaw && <span className="flex items-center gap-1"><span className="w-4 h-0.5 bg-green-500"></span>Raw</span>}
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500"></span>OOS</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-yellow-500"></span>OOC</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-blue-500"></span>OOT</span>
      </div>
    </div>
  );
};

const Interlock = ({onClose}) => (
  <div className="rounded-lg border overflow-hidden" style={{borderColor:THEME.danger}}>
    <div className="px-4 py-2 text-white flex items-center gap-2" style={{backgroundColor:THEME.danger}}>
      <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
      <span className="font-medium text-sm">INTERLOCK 발동</span>
    </div>
    <div className="bg-white p-4 flex items-start justify-between">
      <div className="flex gap-3">
        <AlertTriangle size={20} className="text-red-500"/>
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2 py-0.5 rounded text-xs text-white" style={{backgroundColor:THEME.warning}}>OOC</span>
            <span className="font-semibold">H2O</span>
            <span className="text-red-600 font-bold">6.85</span>
            <span className="text-slate-400 text-sm">/ UCL:6.00</span>
          </div>
          <div className="text-sm text-slate-500">LOT:26Y3A61C3I / CYL:21ENKE25839</div>
          <div className="flex gap-2 mt-2">
            <span className="px-2 py-0.5 bg-slate-100 rounded text-xs flex items-center gap-1"><CheckCircle size={10} className="text-green-500"/>Hold</span>
            <span className="px-2 py-0.5 bg-slate-100 rounded text-xs flex items-center gap-1"><CheckCircle size={10} className="text-green-500"/>MES</span>
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={onClose} className="px-3 py-1.5 text-sm text-white rounded bg-blue-600 hover:bg-blue-700">승인해제</button>
        <button onClick={onClose} className="px-3 py-1.5 text-sm border rounded hover:bg-slate-50">조치해제</button>
      </div>
    </div>
  </div>
);

const Stats = () => (
  <div className="bg-white rounded-lg border">
    <div className="px-4 py-2 border-b bg-slate-50"><span className="font-medium text-sm">LOT별 통계</span></div>
    <table className="w-full text-sm">
      <thead className="bg-slate-100">
        <tr>{['Item','USL','UCL','평균','μ+3σ','Cpk','판정'].map(h=><th key={h} className="px-3 py-2 text-xs text-slate-600 text-left">{h}</th>)}</tr>
      </thead>
      <tbody>
        {Object.entries(LIMITS).map(([n,l])=>(
          <tr key={n} className="border-t hover:bg-slate-50">
            <td className="px-3 py-2 font-medium">{n}</td>
            <td className="px-3 py-2">{l.USL}</td>
            <td className="px-3 py-2">{l.UCL}</td>
            <td className="px-3 py-2">{l.avg}</td>
            <td className="px-3 py-2">{l.mu3sigma}</td>
            <td className="px-3 py-2 text-blue-600">{((l.USL-l.avg)/(3*(l.mu3sigma-l.avg)/3)).toFixed(1)}</td>
            <td className="px-3 py-2"><span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">OK</span></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Dashboard = ({ onNavigate, onInterlockLog }) => {
  const [interlocks, setInterlocks] = useState([
    { id: 1, type: 'OOC', item: 'H2O', value: 6.85, limit: 6.0, lot: '26Y3A61C3I', cyl: '21ENKE25839', hold: true, mes: true }
  ]);
  
  // 모달 상태
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedInterlock, setSelectedInterlock] = useState(null);
  const [approvalType, setApprovalType] = useState('파트장');
  const [approver, setApprover] = useState('');
  const [handler, setHandler] = useState('');
  const [reason, setReason] = useState('');
  
  // Interlock 시뮬레이션 - 새 이벤트 추가
  const simulateInterlock = () => {
    const items = ['H2O', 'N2', 'CO2', 'Total OFC', 'O2+Ar', 'CH4'];
    const types = ['OOC', 'OOS', 'OOT'];
    const lots = ['26Y3A61C3I', '26Y3A5ZP31', '26Y3C10B3M', '26Y3G16I3U'];
    const cyls = ['21ENKE25839', '21ENKE25728', '21ENKE26010', '21ENKE25835'];
    
    const randomItem = items[Math.floor(Math.random() * items.length)];
    const randomType = types[Math.floor(Math.random() * types.length)];
    const randomLot = lots[Math.floor(Math.random() * lots.length)];
    const randomCyl = cyls[Math.floor(Math.random() * cyls.length)];
    const baseValue = Math.random() * 10;
    const limitValue = baseValue * 0.85;
    
    const newInterlock = {
      id: Date.now(),
      type: randomType,
      item: randomItem,
      value: parseFloat(baseValue.toFixed(2)),
      limit: parseFloat(limitValue.toFixed(2)),
      lot: randomLot,
      cyl: randomCyl,
      hold: true,
      mes: false
    };
    
    setInterlocks([newInterlock, ...interlocks]);
  };
  
  // 승인해제 모달 열기
  const openApprovalModal = (il) => {
    setSelectedInterlock(il);
    setApprovalType('파트장');
    setApprover('');
    setReason('');
    setShowApprovalModal(true);
  };
  
  // 조치해제 모달 열기
  const openActionModal = (il) => {
    setSelectedInterlock(il);
    setHandler('');
    setReason('');
    setShowActionModal(true);
  };
  
  // 승인해제 확인
  const confirmApproval = () => {
    if (!approver.trim()) {
      alert('승인자를 입력해주세요.');
      return;
    }
    onInterlockLog({
      type: selectedInterlock.type,
      item: selectedInterlock.item,
      lot: selectedInterlock.lot,
      cyl: selectedInterlock.cyl,
      value: selectedInterlock.value,
      action: '승인해제',
      approvalType,
      approver,
      handler: approver,
      reason
    });
    setInterlocks(interlocks.filter(i => i.id !== selectedInterlock.id));
    setShowApprovalModal(false);
  };
  
  // 조치해제 확인
  const confirmAction = () => {
    if (!handler.trim()) {
      alert('담당자를 입력해주세요.');
      return;
    }
    onInterlockLog({
      type: selectedInterlock.type,
      item: selectedInterlock.item,
      lot: selectedInterlock.lot,
      cyl: selectedInterlock.cyl,
      value: selectedInterlock.value,
      action: '조치해제',
      approvalType: '',
      approver: '',
      handler,
      reason
    });
    setInterlocks(interlocks.filter(i => i.id !== selectedInterlock.id));
    setShowActionModal(false);
  };
  
  // 최근 이상 발생 현황
  const [recentIssues, setRecentIssues] = useState([
    { time: '14:32', type: 'OOC', item: 'H2O', lot: '26Y3A61C3I', value: 6.85, limit: 6.0, status: 'hold' },
    { time: '11:15', type: 'OOT', item: 'N2', lot: '26Y3A61C3I', value: 8.2, limit: 10, status: 'monitor' },
    { time: '09:45', type: 'SEC', item: 'Total OFC', lot: '26Y3A5ZP31', value: 165, limit: 166.91, status: 'cleared' },
  ]);

  // 금일 생산 현황
  const todayProduction = [
    { product: 'C4F8', lots: 3, cyls: 25, pass: 24, status: 'running' },
    { product: 'CHF3', lots: 2, cyls: 18, pass: 18, status: 'running' },
    { product: 'CF4', lots: 2, cyls: 15, pass: 15, status: 'complete' },
    { product: 'C3F6', lots: 1, cyls: 8, pass: 8, status: 'complete' },
  ];

  // CoA별 현황
  const coaStatus = [
    { coa: 'Air', checked: 48, issues: 1 },
    { coa: '수분', checked: 48, issues: 2 },
    { coa: '순도', checked: 48, issues: 0 },
    { coa: '산도', checked: 48, issues: 0 },
    { coa: 'Metal', checked: 48, issues: 0 },
    { coa: 'Sulfur', checked: 48, issues: 0 },
  ];

  return (
    <div className="space-y-4">
      {/* Interlock 알림 - 여러 개 표시 */}
      {interlocks.map(il => (
        <div key={il.id} className="bg-red-600 text-white rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></span>
              <span className="font-bold">INTERLOCK 발동</span>
            </div>
            <div className="flex gap-2">
              <button onClick={() => openApprovalModal(il)} className="px-3 py-1 bg-white text-red-600 rounded text-sm font-medium hover:bg-red-50">승인해제</button>
              <button onClick={() => openActionModal(il)} className="px-3 py-1 bg-red-700 text-white rounded text-sm hover:bg-red-800">조치해제</button>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <AlertTriangle size={24}/>
            <div>
              <div className="flex items-center gap-2">
                <span className={'px-2 py-0.5 rounded text-xs font-bold ' + 
                  (il.type === 'OOS' ? 'bg-red-800' : il.type === 'OOC' ? 'bg-yellow-500 text-yellow-900' : 'bg-blue-500')}>
                  {il.type}
                </span>
                <span className="font-bold text-lg">{il.item}</span>
                <span className="text-red-200">{il.value}</span>
                <span className="text-red-300 text-sm">/ UCL:{il.limit}</span>
              </div>
              <p className="text-sm text-red-200 mt-1">LOT:{il.lot} / CYL:{il.cyl}</p>
            </div>
          </div>
          <div className="flex gap-4 mt-3 text-sm">
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={il.hold} readOnly className="rounded"/>
              <span>Hold</span>
            </label>
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={il.mes} readOnly className="rounded"/>
              <span>MES</span>
            </label>
          </div>
        </div>
      ))}
      
      {/* 요약 카드 */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-white rounded-lg p-4 border">
          <p className="text-sm text-slate-500">금일 LOT</p>
          <p className="text-2xl font-bold" style={{color: THEME.primaryDark}}>8</p>
          <p className="text-xs text-green-600">+2 vs 어제</p>
        </div>
        <div className="bg-white rounded-lg p-4 border">
          <p className="text-sm text-slate-500">금일 실린더</p>
          <p className="text-2xl font-bold" style={{color: THEME.primaryDark}}>66</p>
          <p className="text-xs text-slate-400">4개 품목</p>
        </div>
        <div className="bg-white rounded-lg p-4 border">
          <p className="text-sm text-slate-500">합격률</p>
          <p className="text-2xl font-bold text-green-600">98.5%</p>
          <p className="text-xs text-slate-400">65/66</p>
        </div>
        <div className="bg-white rounded-lg p-4 border">
          <p className="text-sm text-slate-500">품질 이상</p>
          <p className="text-2xl font-bold text-yellow-600">3</p>
          <p className="text-xs text-slate-400">OOC:1 OOT:1 SEC:1</p>
        </div>
        <div className="bg-white rounded-lg p-4 border">
          <p className="text-sm text-slate-500">Interlock</p>
          <p className="text-2xl font-bold text-red-600">{interlocks.length}</p>
          <p className="text-xs text-slate-400">{interlocks.length > 0 ? 'Hold 중' : '정상'}</p>
        </div>
      </div>

      {/* 시뮬레이션 버튼 */}
      <div className="bg-white rounded-lg p-4 border flex items-center justify-between">
        <div>
          <p className="font-medium text-sm">Interlock 시뮬레이션</p>
          <p className="text-xs text-slate-500">불량 발생 시 알림 테스트</p>
        </div>
        <button onClick={simulateInterlock} className="px-4 py-2 text-white text-sm rounded bg-red-600 hover:bg-red-700 flex items-center gap-2">
          <AlertTriangle size={14}/>시뮬레이션
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* 최근 이상 발생 현황 */}
        <div className="bg-white rounded-lg border">
          <div onClick={() => onNavigate('improvement')} className="px-4 py-3 border-b flex items-center justify-between cursor-pointer hover:bg-slate-50">
            <span className="font-medium text-sm">최근 이상 발생 현황</span>
            <span className="text-xs text-slate-500">금일</span>
          </div>
          <div className="divide-y">
            {recentIssues.map((issue, i) => (
              <div key={i} className="px-4 py-3 flex items-center justify-between hover:bg-slate-50">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-400">{issue.time}</span>
                  <span className={'px-2 py-0.5 rounded text-xs font-medium ' + 
                    (issue.type === 'OOC' ? 'bg-yellow-100 text-yellow-700' : 
                     issue.type === 'OOS' ? 'bg-red-100 text-red-700' : 
                     issue.type === 'OOT' ? 'bg-blue-100 text-blue-700' : 'bg-cyan-100 text-cyan-700')}>
                    {issue.type}
                  </span>
                  <span className="font-medium text-sm">{issue.item}</span>
                  <span className="text-red-600 font-medium">{issue.value}</span>
                  <span className="text-xs text-slate-400">/ {issue.limit}</span>
                </div>
                <span className={'px-2 py-0.5 rounded text-xs ' + 
                  (issue.status === 'hold' ? 'bg-red-100 text-red-600' : 
                   issue.status === 'monitor' ? 'bg-yellow-100 text-yellow-600' : 'bg-green-100 text-green-600')}>
                  {issue.status === 'hold' ? 'Hold' : issue.status === 'monitor' ? '모니터링' : '해제'}
                </span>
              </div>
            ))}
          </div>
          <div className="px-4 py-2 border-t bg-slate-50">
            <button onClick={() => onNavigate('improvement')} className="text-xs text-blue-600 hover:underline">전체 보기 →</button>
          </div>
        </div>

        {/* 금일 생산 현황 */}
        <div className="bg-white rounded-lg border">
          <div onClick={() => onNavigate('summary')} className="px-4 py-3 border-b flex items-center justify-between cursor-pointer hover:bg-slate-50">
            <span className="font-medium text-sm">금일 생산 현황</span>
            <span className="text-xs text-slate-500">{todayProduction.reduce((a, b) => a + b.lots, 0)} LOT / {todayProduction.reduce((a, b) => a + b.cyls, 0)} CYL</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                {['품목', 'LOT', '실린더', '합격', '상태'].map(h => (
                  <th key={h} className="px-3 py-2 text-xs text-slate-500 text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {todayProduction.map((row, i) => (
                <tr key={i} className="border-t hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium">{row.product}</td>
                  <td className="px-3 py-2">{row.lots}</td>
                  <td className="px-3 py-2">{row.cyls}</td>
                  <td className="px-3 py-2 text-green-600">{row.pass}</td>
                  <td className="px-3 py-2">
                    <span className={'px-2 py-0.5 rounded text-xs ' + 
                      (row.status === 'running' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600')}>
                      {row.status === 'running' ? '진행중' : '완료'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* CoA 분류별 검사 현황 */}
      <div className="bg-white rounded-lg border">
        <div onClick={() => onNavigate('spc')} className="px-4 py-3 border-b cursor-pointer hover:bg-slate-50">
          <span className="font-medium text-sm">CoA 분류별 검사 현황 (금일)</span>
        </div>
        <div className="grid grid-cols-6 divide-x">
          {coaStatus.map(coa => (
            <div key={coa.coa} className="p-4 text-center hover:bg-slate-50">
              <p className="text-sm font-medium mb-1">{coa.coa}</p>
              <p className="text-lg font-bold" style={{color: THEME.primaryDark}}>{coa.checked}</p>
              <p className="text-xs text-slate-400">검사 완료</p>
              {coa.issues > 0 ? (
                <p className="text-xs text-red-500 mt-1">이상 {coa.issues}건</p>
              ) : (
                <p className="text-xs text-green-500 mt-1">정상</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 빠른 링크 */}
      <div className="grid grid-cols-4 gap-4">
        <button onClick={() => onNavigate('spc')} className="bg-white rounded-lg p-4 border hover:bg-slate-50 text-left">
          <BarChart3 size={20} className="text-blue-600 mb-2"/>
          <p className="font-medium text-sm">SPC 분석</p>
          <p className="text-xs text-slate-500">품질 경향 분석</p>
        </button>
        <button onClick={() => onNavigate('spc')} className="bg-white rounded-lg p-4 border hover:bg-slate-50 text-left">
          <FileText size={20} className="text-green-600 mb-2"/>
          <p className="font-medium text-sm">검토의견</p>
          <p className="text-xs text-slate-500">3건 미처리</p>
        </button>
        <button onClick={() => onNavigate('summary')} className="bg-white rounded-lg p-4 border hover:bg-slate-50 text-left">
          <Database size={20} className="text-purple-600 mb-2"/>
          <p className="font-medium text-sm">품질 집계</p>
          <p className="text-xs text-slate-500">월간 리포트</p>
        </button>
        <button onClick={() => onNavigate('master')} className="bg-white rounded-lg p-4 border hover:bg-slate-50 text-left">
          <Settings size={20} className="text-slate-600 mb-2"/>
          <p className="font-medium text-sm">기본정보관리</p>
          <p className="text-xs text-slate-500">Rule 설정</p>
        </button>
      </div>

      {/* 승인해제 모달 */}
      {showApprovalModal && selectedInterlock && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <AlertTriangle className="text-yellow-500"/>
              승인해제
            </h3>
            <div className="mb-4 p-3 bg-red-50 rounded border border-red-200 text-sm">
              <p><span className="text-slate-500">항목:</span> <strong>{selectedInterlock.item}</strong></p>
              <p><span className="text-slate-500">LOT:</span> {selectedInterlock.lot}</p>
              <p><span className="text-slate-500">CYL:</span> {selectedInterlock.cyl}</p>
              <p><span className="text-slate-500">측정값:</span> <span className="text-red-600 font-bold">{selectedInterlock.value}</span></p>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">승인 유형</label>
                <select value={approvalType} onChange={e => setApprovalType(e.target.value)} className="w-full px-3 py-2 border rounded">
                  <option value="파트장">파트장</option>
                  <option value="경영진">경영진</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">승인자 <span className="text-red-500">*</span></label>
                <input type="text" value={approver} onChange={e => setApprover(e.target.value)} placeholder="승인자 이름 입력" className="w-full px-3 py-2 border rounded"/>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">사유</label>
                <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="해제 사유 입력" className="w-full px-3 py-2 border rounded" rows={2}/>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowApprovalModal(false)} className="flex-1 px-4 py-2 border rounded hover:bg-slate-50">취소</button>
              <button onClick={confirmApproval} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">확인</button>
            </div>
          </div>
        </div>
      )}

      {/* 조치해제 모달 */}
      {showActionModal && selectedInterlock && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <CheckCircle className="text-green-500"/>
              조치해제
            </h3>
            <div className="mb-4 p-3 bg-red-50 rounded border border-red-200 text-sm">
              <p><span className="text-slate-500">항목:</span> <strong>{selectedInterlock.item}</strong></p>
              <p><span className="text-slate-500">LOT:</span> {selectedInterlock.lot}</p>
              <p><span className="text-slate-500">CYL:</span> {selectedInterlock.cyl}</p>
              <p><span className="text-slate-500">측정값:</span> <span className="text-red-600 font-bold">{selectedInterlock.value}</span></p>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">조치 담당자 <span className="text-red-500">*</span></label>
                <input type="text" value={handler} onChange={e => setHandler(e.target.value)} placeholder="담당자 이름 입력" className="w-full px-3 py-2 border rounded"/>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">조치 내용</label>
                <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="조치 내용 입력" className="w-full px-3 py-2 border rounded" rows={2}/>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowActionModal(false)} className="flex-1 px-4 py-2 border rounded hover:bg-slate-50">취소</button>
              <button onClick={confirmAction} className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">확인</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 불순물 항목 목록 (CoA 분류별)
const IMPURITY_BY_COA = {
  'Air': [
    {name:'CH4', usl:5, lsl:0},
    {name:'CO', usl:3, lsl:0},
    {name:'CO2', usl:5, lsl:0},
    {name:'H2', usl:2, lsl:0},
    {name:'THC(CH4)', usl:5, lsl:0},
    {name:'O2+Ar', usl:5, lsl:0},
    {name:'N2', usl:10, lsl:0},
    {name:'THC as CH4', usl:5, lsl:0},
  ],
  '수분': [
    {name:'H2O(ppmv)', usl:3, lsl:0},
    {name:'H2O(ppmw)', usl:5, lsl:0},
  ],
  '순도': [
    {name:'Purity(99.9Vol%)', usl:100, lsl:99.9},
    {name:'Purity(99.99Vol%)', usl:100, lsl:99.99},
    {name:'Purity(99.999Vol%)', usl:100, lsl:99.999},
    {name:'OFC', usl:500, lsl:0},
    {name:'Total OFC', usl:900, lsl:0},
    {name:'C3HF5', usl:300, lsl:0},
    {name:'C3F8', usl:200, lsl:0},
    {name:'o-C3F6', usl:100, lsl:0},
  ],
  '산도': [
    {name:'Acidity as HF', usl:1, lsl:0},
    {name:'Acidity as HCl', usl:1, lsl:0},
    {name:'HF', usl:1, lsl:0},
    {name:'HCl', usl:1, lsl:0},
  ],
  'Metal': [
    {name:'Ag', usl:1, lsl:0},
    {name:'Al', usl:1, lsl:0},
    {name:'Ca', usl:1, lsl:0},
    {name:'Cr', usl:1, lsl:0},
    {name:'Cu', usl:1, lsl:0},
    {name:'Fe', usl:1, lsl:0},
    {name:'K', usl:1, lsl:0},
    {name:'Na', usl:1, lsl:0},
    {name:'Ni', usl:1, lsl:0},
    {name:'Metal Total', usl:15, lsl:0},
  ],
  'Sulfur': [
    {name:'H2S', usl:1, lsl:0},
    {name:'CS2', usl:1, lsl:0},
  ],
};

// Nelson Rule 상세
const NELSON_DETAIL = [
  {id:1, name:'Rule 1', desc:'1개 데이터가 관리한계선(3σ) 초과'},
  {id:2, name:'Rule 2', desc:'연속 9개 데이터가 중심선 한쪽에 위치'},
  {id:3, name:'Rule 3', desc:'연속 6개 데이터가 증가 또는 감소'},
  {id:4, name:'Rule 4', desc:'연속 14개 데이터가 교대로 증감'},
  {id:5, name:'Rule 5', desc:'연속 3개 중 2개가 2σ 밖에 위치'},
  {id:6, name:'Rule 6', desc:'연속 5개 중 4개가 1σ 밖에 위치'},
  {id:7, name:'Rule 7', desc:'연속 15개 데이터가 1σ 내에 위치'},
  {id:8, name:'Rule 8', desc:'연속 8개 데이터가 1σ 밖에 위치'},
];

// 고객 Rule (SEC) 상세
const SEC_DETAIL = [
  {id:'R1', name:'SEC-R1', desc:'연속 N개 데이터가 지속적으로 상승 또는 하강', param:'연속 N값', defaultVal:7},
  {id:'R2', name:'SEC-R2', desc:'2σ 경고한계선(Warning Limit) 초과 감지', param:'경고 σ값', defaultVal:2},
  {id:'R3', name:'SEC-R3', desc:'규격 한계의 90% 도달 시 사전 경고', param:'규격근접 %', defaultVal:90},
  {id:'R4', name:'SEC-R4', desc:'이동평균값이 관리 기준을 초과', param:'이동평균 N값', defaultVal:5},
  {id:'R5', name:'SEC-R5', desc:'공정능력지수(Cpk)가 목표값 미만', param:'목표 Cpk', defaultVal:1.33},
];

const Master = () => {
  const [tab,setTab] = useState('prod');
  const [selCoA, setSelCoA] = useState('Air');
  const [selItem, setSelItem] = useState('CH4');
  const [nelsonRules, setNelsonRules] = useState({});
  const [secRules, setSecRules] = useState({R2:true, R5:true});
  const [secParams, setSecParams] = useState({R1:7, R2:2, R3:90, R4:5, R5:1.33});
  
  // 편집 가능한 데이터 상태
  const [products, setProducts] = useState([...PRODUCTS]);
  const [impurities, setImpurities] = useState([...IMPURITIES]);
  const [limits, setLimits] = useState({...LIMITS});
  
  // 모달 상태
  const [modal, setModal] = useState({ type: null, mode: null, data: null });
  const [formData, setFormData] = useState({});
  const [impFilter, setImpFilter] = useState('전체');
  const [impSearch, setImpSearch] = useState('');
  const [saveMessage, setSaveMessage] = useState('');

  const toggleNelson = (id) => setNelsonRules(p=>({...p,[id]:!p[id]}));
  const toggleSec = (id) => setSecRules(p=>({...p,[id]:!p[id]}));
  
  // CoA 변경 시 첫 번째 불순물 선택
  const handleCoAChange = (coa) => {
    setSelCoA(coa);
    const items = IMPURITY_BY_COA[coa];
    if (items && items.length > 0) {
      setSelItem(items[0].name);
    }
  };

  // 품명 CRUD
  const openProductModal = (mode, data = null) => {
    setModal({ type: 'product', mode, data });
    setFormData(mode === 'edit' ? { name: data } : { name: '' });
  };
  
  const saveProduct = () => {
    if (!formData.name?.trim()) return;
    if (modal.mode === 'add') {
      setProducts([...products, formData.name.trim()]);
    } else if (modal.mode === 'edit') {
      setProducts(products.map(p => p === modal.data ? formData.name.trim() : p));
    }
    setModal({ type: null, mode: null, data: null });
  };
  
  const deleteProduct = (name) => {
    if (confirm(`"${name}" 품명을 삭제하시겠습니까?`)) {
      setProducts(products.filter(p => p !== name));
    }
  };

  // 불순물 CRUD
  const openImpurityModal = (mode, data = null) => {
    setModal({ type: 'impurity', mode, data });
    setFormData(mode === 'edit' ? { name: data } : { name: '' });
  };
  
  const saveImpurity = () => {
    if (!formData.name?.trim()) return;
    if (modal.mode === 'add') {
      setImpurities([...impurities, formData.name.trim()]);
    } else if (modal.mode === 'edit') {
      setImpurities(impurities.map(m => m === modal.data ? formData.name.trim() : m));
    }
    setModal({ type: null, mode: null, data: null });
  };
  
  const deleteImpurity = (name) => {
    if (confirm(`"${name}" 불순물을 삭제하시겠습니까?`)) {
      setImpurities(impurities.filter(m => m !== name));
    }
  };

  // 규격 CRUD
  const openLimitModal = (mode, name = null) => {
    setModal({ type: 'limit', mode, data: name });
    if (mode === 'edit' && name) {
      setFormData({ name, ...limits[name] });
    } else {
      setFormData({ name: '', USL: '', UCL: '', avg: '', mu3sigma: '', coa: 'Air' });
    }
  };
  
  const saveLimit = () => {
    if (!formData.name?.trim()) return;
    const newLimits = { ...limits };
    if (modal.mode === 'edit' && modal.data !== formData.name) {
      delete newLimits[modal.data];
    }
    newLimits[formData.name] = {
      USL: parseFloat(formData.USL) || 0,
      UCL: parseFloat(formData.UCL) || 0,
      avg: parseFloat(formData.avg) || 0,
      mu3sigma: parseFloat(formData.mu3sigma) || 0,
      coa: formData.coa || 'Air'
    };
    setLimits(newLimits);
    setModal({ type: null, mode: null, data: null });
  };

  // Rule 설정 저장
  const saveRuleSettings = () => {
    setSaveMessage('Rule 설정이 저장되었습니다.');
    setTimeout(() => setSaveMessage(''), 3000);
  };
  
  const cancelRuleSettings = () => {
    setNelsonRules({});
    setSecRules({R2:true, R5:true});
    setSecParams({R1:7, R2:2, R3:90, R4:5, R5:1.33});
  };

  // 불순물 필터링
  const filteredImpurities = impurities.filter(m => {
    if (impFilter !== '전체') {
      const limitInfo = limits[m];
      if (!limitInfo || limitInfo.coa !== impFilter) return false;
    }
    if (impSearch && !m.toLowerCase().includes(impSearch.toLowerCase())) return false;
    return true;
  });

  // 모달 컴포넌트
  const renderModal = () => {
    if (!modal.type) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setModal({ type: null, mode: null, data: null })}>
        <div className="bg-white rounded-lg p-6 w-96 max-w-[90%]" onClick={e => e.stopPropagation()}>
          <h3 className="text-lg font-bold mb-4">
            {modal.type === 'product' && (modal.mode === 'add' ? '품명 추가' : '품명 수정')}
            {modal.type === 'impurity' && (modal.mode === 'add' ? '불순물 추가' : '불순물 수정')}
            {modal.type === 'limit' && (modal.mode === 'add' ? '관리규격 추가' : '관리규격 수정')}
          </h3>
          
          {(modal.type === 'product' || modal.type === 'impurity') && (
            <div className="mb-4">
              <label className="block text-sm text-slate-600 mb-1">이름</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border rounded"
                placeholder={modal.type === 'product' ? '품명 입력' : '불순물명 입력'}
                autoFocus
              />
            </div>
          )}
          
          {modal.type === 'limit' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-slate-600 mb-1">항목명</label>
                <input type="text" value={formData.name || ''} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full px-3 py-2 border rounded" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-slate-600 mb-1">USL</label>
                  <input type="number" value={formData.USL || ''} onChange={e => setFormData({ ...formData, USL: e.target.value })} className="w-full px-3 py-2 border rounded" />
                </div>
                <div>
                  <label className="block text-sm text-slate-600 mb-1">UCL</label>
                  <input type="number" value={formData.UCL || ''} onChange={e => setFormData({ ...formData, UCL: e.target.value })} className="w-full px-3 py-2 border rounded" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-slate-600 mb-1">AVG</label>
                  <input type="number" value={formData.avg || ''} onChange={e => setFormData({ ...formData, avg: e.target.value })} className="w-full px-3 py-2 border rounded" step="0.01" />
                </div>
                <div>
                  <label className="block text-sm text-slate-600 mb-1">μ+3σ</label>
                  <input type="number" value={formData.mu3sigma || ''} onChange={e => setFormData({ ...formData, mu3sigma: e.target.value })} className="w-full px-3 py-2 border rounded" step="0.01" />
                </div>
              </div>
              <div>
                <label className="block text-sm text-slate-600 mb-1">CoA 분류</label>
                <select value={formData.coa || 'Air'} onChange={e => setFormData({ ...formData, coa: e.target.value })} className="w-full px-3 py-2 border rounded">
                  {COA_LIST.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          )}
          
          <div className="flex gap-2 mt-6">
            <button onClick={() => { 
              if (modal.type === 'product') saveProduct();
              else if (modal.type === 'impurity') saveImpurity();
              else if (modal.type === 'limit') saveLimit();
            }} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">저장</button>
            <button onClick={() => setModal({ type: null, mode: null, data: null })} className="flex-1 px-4 py-2 border rounded hover:bg-slate-50">취소</button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg border">
      {renderModal()}
      {saveMessage && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50 flex items-center gap-2">
          <CheckCircle size={16} />{saveMessage}
        </div>
      )}
      <div className="flex border-b">
        {[['prod','품명'],['imp','불순물'],['lim','규격'],['rule','Rule 설정'],['il','Interlock']].map(([k,v])=>(
          <button key={k} onClick={()=>setTab(k)} className={'px-5 py-2 text-sm '+(tab===k?'bg-blue-50 text-blue-600 border-b-2 border-blue-600':'text-slate-500')}>{v}</button>
        ))}
      </div>
      <div className="p-4">
        {tab==='prod' && (
          <div>
            <div className="flex justify-between mb-3"><span className="font-medium">품명 ({products.length}개)</span><button onClick={() => openProductModal('add')} className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">+ 추가</button></div>
            <table className="w-full text-sm">
              <thead className="bg-slate-100"><tr><th className="px-3 py-2 text-left">No</th><th className="px-3 py-2 text-left">품명</th><th className="px-3 py-2 text-center">관리</th></tr></thead>
              <tbody>{products.map((p,i)=><tr key={p} className="border-t"><td className="px-3 py-2">{i+1}</td><td className="px-3 py-2 font-medium">{p}</td><td className="px-3 py-2 text-center"><button onClick={() => openProductModal('edit', p)} className="text-blue-600 text-xs mr-2 hover:underline">수정</button><button onClick={() => deleteProduct(p)} className="text-red-600 text-xs hover:underline">삭제</button></td></tr>)}</tbody>
            </table>
          </div>
        )}
        {tab==='imp' && (
          <div>
            <div className="flex justify-between items-center mb-3">
              <span className="font-medium">불순물 ({filteredImpurities.length}개)</span>
              <div className="flex gap-2">
                <select value={impFilter} onChange={e => setImpFilter(e.target.value)} className="px-2 py-1 text-sm border rounded"><option>전체</option>{COA_LIST.map(c=><option key={c}>{c}</option>)}</select>
                <input value={impSearch} onChange={e => setImpSearch(e.target.value)} placeholder="검색..." className="px-2 py-1 text-sm border rounded w-32"/>
                <button onClick={() => openImpurityModal('add')} className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">+ 추가</button>
              </div>
            </div>
            <div className="border rounded max-h-64 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0"><tr><th className="px-3 py-2 text-left">No</th><th className="px-3 py-2 text-left">불순물</th><th className="px-3 py-2 text-center">사용</th><th className="px-3 py-2 text-center">관리</th></tr></thead>
                <tbody>{filteredImpurities.map((m,i)=><tr key={m} className="border-t hover:bg-slate-50"><td className="px-3 py-2 text-slate-500">{i+1}</td><td className="px-3 py-2 font-medium">{m}</td><td className="px-3 py-2 text-center"><span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span></td><td className="px-3 py-2 text-center"><button onClick={() => openImpurityModal('edit', m)} className="text-blue-600 text-xs mr-2 hover:underline">수정</button><button onClick={() => deleteImpurity(m)} className="text-red-600 text-xs hover:underline">삭제</button></td></tr>)}</tbody>
              </table>
            </div>
          </div>
        )}
        {tab==='lim' && (
          <div>
            <div className="flex justify-between mb-3"><span className="font-medium">관리규격</span><button onClick={() => openLimitModal('add')} className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">+ 추가</button></div>
            <table className="w-full text-sm">
              <thead className="bg-slate-100"><tr>{['항목','USL','UCL','AVG','μ+3σ','관리'].map(h=><th key={h} className="px-3 py-2 text-left text-xs">{h}</th>)}</tr></thead>
              <tbody>{Object.entries(limits).map(([n,l])=><tr key={n} className="border-t"><td className="px-3 py-2 font-medium">{n}</td><td className="px-3 py-2">{l.USL}</td><td className="px-3 py-2">{l.UCL}</td><td className="px-3 py-2">{l.avg}</td><td className="px-3 py-2">{l.mu3sigma}</td><td className="px-3 py-2"><button onClick={() => openLimitModal('edit', n)} className="text-blue-600 text-xs hover:underline">수정</button></td></tr>)}</tbody>
            </table>
          </div>
        )}
        {tab==='rule' && (
          <div>
            {/* 상단: CoA 분류 선택 탭 */}
            <div className="flex gap-2 mb-4 pb-4 border-b">
              {COA_LIST.map(coa=>(
                <button
                  key={coa}
                  onClick={()=>handleCoAChange(coa)}
                  className={'px-4 py-2 rounded-lg text-sm font-medium transition-colors '+(selCoA===coa?'bg-blue-600 text-white':'bg-slate-100 text-slate-600 hover:bg-slate-200')}
                >
                  {coa}
                  <span className={'ml-1.5 text-xs '+(selCoA===coa?'text-blue-200':'text-slate-400')}>({IMPURITY_BY_COA[coa]?.length || 0})</span>
                </button>
              ))}
            </div>

            <div className="flex gap-6">
              {/* 좌측: 불순물 항목 목록 */}
              <div className="w-56 flex-shrink-0">
                <h4 className="font-medium text-sm mb-3">불순물 항목 목록</h4>
                <div className="border rounded max-h-80 overflow-y-auto">
                  {(IMPURITY_BY_COA[selCoA] || []).map(item=>(
                    <button
                      key={item.name}
                      onClick={()=>setSelItem(item.name)}
                      className={'w-full text-left px-3 py-2.5 border-b last:border-b-0 transition-colors '+(selItem===item.name?'bg-blue-600 text-white':'hover:bg-slate-50')}
                    >
                      <div className="font-medium text-sm">{item.name}</div>
                      <div className={'text-xs '+(selItem===item.name?'text-blue-100':'text-slate-400')}>USL: {item.usl} / LSL: {item.lsl}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* 우측: Rule 설정 */}
              <div className="flex-1">
                <h4 className="font-medium text-sm mb-4">Rule 설정 - {selItem}</h4>
                
                {/* Nelson Rules */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-slate-700">Nelson Rules</span>
                    <div className="flex gap-2">
                      <button onClick={()=>setNelsonRules(NELSON_DETAIL.reduce((a,r)=>({...a,[r.id]:true}),{}))} className="text-xs text-blue-600 hover:underline">전체 선택</button>
                      <button onClick={()=>setNelsonRules({})} className="text-xs text-slate-500 hover:underline">전체 해제</button>
                    </div>
                  </div>
                  <div className="space-y-1">
                    {NELSON_DETAIL.map(rule=>(
                      <label key={rule.id} className="flex items-start gap-3 p-2 rounded hover:bg-slate-50 cursor-pointer">
                        <input type="checkbox" checked={!!nelsonRules[rule.id]} onChange={()=>toggleNelson(rule.id)} className="mt-0.5"/>
                        <div>
                          <span className="font-medium text-sm">{rule.name}</span>
                          <span className="text-xs text-slate-500 ml-2">{rule.desc}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* 고객 요구 Rules (SEC) */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium" style={{color:THEME.accent}}>고객 요구 Rules (SEC)</span>
                    <div className="flex gap-2">
                      <button onClick={()=>setSecRules(SEC_DETAIL.reduce((a,r)=>({...a,[r.id]:true}),{}))} className="text-xs text-blue-600 hover:underline">전체 선택</button>
                      <button onClick={()=>setSecRules({})} className="text-xs text-slate-500 hover:underline">전체 해제</button>
                    </div>
                  </div>
                  <div className="space-y-1">
                    {SEC_DETAIL.map(rule=>(
                      <label key={rule.id} className="flex items-start gap-3 p-2 rounded hover:bg-slate-50 cursor-pointer">
                        <input type="checkbox" checked={!!secRules[rule.id]} onChange={()=>toggleSec(rule.id)} className="mt-0.5"/>
                        <div>
                          <span className={'font-medium text-sm px-1.5 py-0.5 rounded mr-2 '+(secRules[rule.id]?'bg-cyan-100 text-cyan-700':'bg-slate-100 text-slate-600')}>{rule.name}</span>
                          <span className="text-xs text-slate-500">{rule.desc}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* 파라미터 설정 */}
                <div className="border-t pt-4">
                  <h5 className="text-sm font-medium mb-3">파라미터 설정</h5>
                  <div className="grid grid-cols-3 gap-4">
                    {SEC_DETAIL.map(rule=>(
                      <div key={rule.id}>
                        <label className="block text-xs text-slate-500 mb-1">{rule.name} {rule.param}</label>
                        <input
                          type="number"
                          value={secParams[rule.id]}
                          onChange={e=>setSecParams(p=>({...p,[rule.id]:parseFloat(e.target.value)||0}))}
                          className="w-full px-3 py-2 border rounded text-sm"
                          step={rule.id==='R5'?0.01:1}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* 버튼 */}
                <div className="flex gap-2 mt-6">
                  <button onClick={saveRuleSettings} className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center gap-2">
                    <Settings size={14}/>저장
                  </button>
                  <button onClick={cancelRuleSettings} className="px-4 py-2 border text-sm rounded hover:bg-slate-50 flex items-center gap-2">
                    <Activity size={14}/>취소
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {tab==='il' && (
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded flex items-center justify-between"><div><p className="font-medium">Interlock 시스템</p><p className="text-sm text-slate-500">품질 이상 시 공정 제어</p></div><label className="flex items-center gap-2"><input type="checkbox" defaultChecked/>활성화</label></div>
            <div className="grid grid-cols-2 gap-3">
              {[['OOS','Out of Spec',THEME.danger],['OOC','Out of Control',THEME.warning],['OOT','Out of Trend',THEME.primary],['SEC','SEC Rule',THEME.accent]].map(([t,l,c])=>(
                <div key={t} className="p-3 border rounded">
                  <div className="flex items-center gap-2 mb-2"><span className="px-2 py-0.5 rounded text-xs text-white" style={{backgroundColor:c}}>{t}</span><span className="text-sm">{l}</span></div>
                  <div className="space-y-1 text-sm"><label className="flex items-center gap-2"><input type="checkbox" defaultChecked/>적용</label><label className="flex items-center gap-2 ml-4"><input type="checkbox" defaultChecked/>Hold</label></div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// 품명별 LOT 샘플 데이터
const PRODUCT_LOTS = {
  'C4F8': [
    { id: '26Y3A61C3I', date: '2026-01-12', cyls: 15, status: 'complete' },
    { id: '26Y3A5ZP31', date: '2025-12-25', cyls: 22, status: 'complete' },
    { id: '26Y3A4KM21', date: '2025-12-18', cyls: 18, status: 'complete' },
  ],
  'C3F6': [
    { id: '26Y3B12D4K', date: '2026-01-15', cyls: 12, status: 'complete' },
    { id: '26Y3B08A2J', date: '2026-01-08', cyls: 20, status: 'complete' },
  ],
  'CHF3': [
    { id: '26Y3C15E7L', date: '2026-01-14', cyls: 25, status: 'complete' },
    { id: '26Y3C10B3M', date: '2026-01-10', cyls: 18, status: 'complete' },
    { id: '26Y3C05A1N', date: '2026-01-05', cyls: 22, status: 'complete' },
  ],
  'C2H2F4': [
    { id: '26Y3D11F8P', date: '2026-01-11', cyls: 16, status: 'complete' },
    { id: '26Y3D06C4Q', date: '2026-01-06', cyls: 14, status: 'complete' },
  ],
  'CH2F2': [
    { id: '26Y3E14G9R', date: '2026-01-14', cyls: 20, status: 'running' },
    { id: '26Y3E09D5S', date: '2026-01-09', cyls: 17, status: 'complete' },
  ],
  'PF3': [
    { id: '26Y3F13H2T', date: '2026-01-13', cyls: 10, status: 'complete' },
  ],
  'CF4': [
    { id: '26Y3G16I3U', date: '2026-01-16', cyls: 28, status: 'running' },
    { id: '26Y3G12E6V', date: '2026-01-12', cyls: 24, status: 'complete' },
    { id: '26Y3G08B2W', date: '2026-01-08', cyls: 22, status: 'complete' },
  ],
  'COS': [
    { id: '26Y3H15J4X', date: '2026-01-15', cyls: 8, status: 'complete' },
    { id: '26Y3H10F1Y', date: '2026-01-10', cyls: 12, status: 'complete' },
  ],
};

// LOT별 차트 데이터 생성 함수 (이상 데이터 포함)
const genLotChartData = (lotInfo, avg, variance, lim) => {
  const data = [];
  const cylCount = lotInfo.cyls;
  const baseNum = parseInt(lotInfo.id.slice(-3), 36) % 1000 + 25000;
  
  for (let i = 0; i < cylCount; i++) {
    const cylNo = (baseNum + i).toString();
    let y = avg + (Math.random() - 0.5) * variance * 0.5;
    let rawY = avg * 0.9 + (Math.random() - 0.5) * variance * 0.4;
    
    data.push({
      idx: i + 1,
      cylLabel: (i + 1) + '_21ENKE' + cylNo,
      y,
      rawY,
      lotId: lotInfo.id,
      lotDate: lotInfo.date,
      cylNo: '21ENKE' + cylNo,
    });
  }
  return data;
};

// 다중 LOT 차트 데이터 생성 (이상 데이터 포함)
const genMultiLotChartData = (lots, avg, variance, lim, itemName) => {
  const data = [];
  let idx = 0;
  
  lots.forEach((lot, lotIndex) => {
    const baseNum = parseInt(lot.id.slice(-3), 36) % 1000 + 25000;
    
    for (let i = 0; i < lot.cyls; i++) {
      idx++;
      const cylNo = (baseNum + i).toString();
      const lotAvgOffset = (lotIndex - lots.length / 2) * variance * 0.1;
      let y = avg + lotAvgOffset + (Math.random() - 0.5) * variance * 0.4;
      let rawY = avg * 0.85 + lotAvgOffset + (Math.random() - 0.5) * variance * 0.3;
      
      data.push({
        idx,
        cylLabel: idx + '_21ENKE' + cylNo,
        y,
        rawY,
        lotId: lot.id,
        lotDate: lot.date,
        cylNo: '21ENKE' + cylNo,
        lotIndex,
        violation: null,
      });
    }
  });
  return data;
};

// 이상 발생 샘플 데이터 (특정 품명/항목에 대해 미리 정의된 이상 데이터)
// Nelson Rules 설명:
// Rule 1: 1개 데이터가 관리한계선(3σ) 초과
// Rule 2: 연속 9개 데이터가 중심선 한쪽에 위치
// Rule 3: 연속 6개 데이터가 증가 또는 감소 추세
// Rule 4: 연속 14개 데이터가 교대로 증감
// Rule 5: 연속 3개 중 2개가 2σ 밖에 위치
// Rule 6: 연속 5개 중 4개가 1σ 밖에 위치
// Rule 7: 연속 15개 데이터가 1σ 내에 위치 (변동 부족)
// Rule 8: 연속 8개 데이터가 1σ 밖에 위치 (양쪽)
const ABNORMAL_DATA = {
  'C4F8': {
    'H2O': [
      // OOS: 규격 초과
      { lotId: '26Y3A61C3I', cylIdx: 12, value: 8.5, rawValue: 8.2, type: 'OOS', rule: 'Spec 초과' },
      // OOC: 관리한계 초과 (Rule 1 - 3σ 초과)
      { lotId: '26Y3A61C3I', cylIdx: 8, value: 6.85, rawValue: 6.5, type: 'OOC', rule: 'Rule1: 3σ 초과' },
      // Nelson Rule 3: 연속 6개 데이터 증가
      { lotId: '26Y3A5ZP31', cylIdx: 5, value: 2.1, rawValue: 1.9, type: 'OOT', rule: 'Rule3: 연속상승' },
      { lotId: '26Y3A5ZP31', cylIdx: 6, value: 2.4, rawValue: 2.2, type: 'OOT', rule: 'Rule3: 연속상승' },
      { lotId: '26Y3A5ZP31', cylIdx: 7, value: 2.8, rawValue: 2.5, type: 'OOT', rule: 'Rule3: 연속상승' },
      { lotId: '26Y3A5ZP31', cylIdx: 8, value: 3.2, rawValue: 2.9, type: 'OOT', rule: 'Rule3: 연속상승' },
      { lotId: '26Y3A5ZP31', cylIdx: 9, value: 3.6, rawValue: 3.3, type: 'OOT', rule: 'Rule3: 연속상승' },
      { lotId: '26Y3A5ZP31', cylIdx: 10, value: 4.1, rawValue: 3.8, type: 'OOT', rule: 'Rule3: 연속상승' },
    ],
    'CH4': [
      // Nelson Rule 1: 3σ 초과
      { lotId: '26Y3A61C3I', cylIdx: 5, value: 4.2, rawValue: 4.0, type: 'OOC', rule: 'Rule1: 3σ 초과' },
      // Nelson Rule 4: 연속 교대 증감 (지그재그 패턴)
      { lotId: '26Y3A5ZP31', cylIdx: 2, value: 0.22, rawValue: 0.20, type: 'OOT', rule: 'Rule4: 교대증감' },
      { lotId: '26Y3A5ZP31', cylIdx: 3, value: 0.10, rawValue: 0.08, type: 'OOT', rule: 'Rule4: 교대증감' },
      { lotId: '26Y3A5ZP31', cylIdx: 4, value: 0.25, rawValue: 0.23, type: 'OOT', rule: 'Rule4: 교대증감' },
      { lotId: '26Y3A5ZP31', cylIdx: 5, value: 0.08, rawValue: 0.06, type: 'OOT', rule: 'Rule4: 교대증감' },
      { lotId: '26Y3A5ZP31', cylIdx: 6, value: 0.28, rawValue: 0.26, type: 'OOT', rule: 'Rule4: 교대증감' },
      { lotId: '26Y3A5ZP31', cylIdx: 7, value: 0.06, rawValue: 0.04, type: 'OOT', rule: 'Rule4: 교대증감' },
      { lotId: '26Y3A5ZP31', cylIdx: 8, value: 0.30, rawValue: 0.28, type: 'OOT', rule: 'Rule4: 교대증감' },
    ],
    'N2': [
      // SEC Rule: 경고한계 근접
      { lotId: '26Y3A5ZP31', cylIdx: 15, value: 16.5, rawValue: 15.8, type: 'SEC', rule: 'SEC-R3: 규격근접 90%' },
      { lotId: '26Y3A5ZP31', cylIdx: 16, value: 17.2, rawValue: 16.5, type: 'SEC', rule: 'SEC-R3: 규격근접 90%' },
      // Nelson Rule 5: 연속 3개 중 2개가 2σ 밖
      { lotId: '26Y3A61C3I', cylIdx: 3, value: 12.5, rawValue: 12.0, type: 'OOT', rule: 'Rule5: 2σ 구간' },
      { lotId: '26Y3A61C3I', cylIdx: 4, value: 2.8, rawValue: 2.5, type: 'OOT', rule: 'Rule5: 2σ 구간' },
      { lotId: '26Y3A61C3I', cylIdx: 5, value: 13.2, rawValue: 12.8, type: 'OOT', rule: 'Rule5: 2σ 구간' },
    ],
    'CO': [
      // Nelson Rule 6: 연속 5개 중 4개가 1σ 밖
      { lotId: '26Y3A61C3I', cylIdx: 1, value: 1.8, rawValue: 1.6, type: 'OOT', rule: 'Rule6: 1σ 구간' },
      { lotId: '26Y3A61C3I', cylIdx: 2, value: 0.12, rawValue: 0.10, type: 'OOT', rule: 'Rule6: 1σ 구간' },
      { lotId: '26Y3A61C3I', cylIdx: 3, value: 1.9, rawValue: 1.7, type: 'OOT', rule: 'Rule6: 1σ 구간' },
      { lotId: '26Y3A61C3I', cylIdx: 4, value: 1.75, rawValue: 1.55, type: 'OOT', rule: 'Rule6: 1σ 구간' },
      { lotId: '26Y3A61C3I', cylIdx: 5, value: 0.15, rawValue: 0.13, type: 'OOT', rule: 'Rule6: 1σ 구간' },
    ],
    'O2+Ar': [
      // Nelson Rule 8: 연속 8개가 1σ 밖 (양쪽)
      { lotId: '26Y3A5ZP31', cylIdx: 10, value: 5.5, rawValue: 5.2, type: 'OOT', rule: 'Rule8: 양측 1σ' },
      { lotId: '26Y3A5ZP31', cylIdx: 11, value: 0.05, rawValue: 0.03, type: 'OOT', rule: 'Rule8: 양측 1σ' },
      { lotId: '26Y3A5ZP31', cylIdx: 12, value: 5.8, rawValue: 5.5, type: 'OOT', rule: 'Rule8: 양측 1σ' },
      { lotId: '26Y3A5ZP31', cylIdx: 13, value: 0.03, rawValue: 0.02, type: 'OOT', rule: 'Rule8: 양측 1σ' },
      { lotId: '26Y3A5ZP31', cylIdx: 14, value: 5.2, rawValue: 4.9, type: 'OOT', rule: 'Rule8: 양측 1σ' },
      { lotId: '26Y3A5ZP31', cylIdx: 15, value: 0.08, rawValue: 0.06, type: 'OOT', rule: 'Rule8: 양측 1σ' },
      { lotId: '26Y3A5ZP31', cylIdx: 16, value: 5.0, rawValue: 4.7, type: 'OOT', rule: 'Rule8: 양측 1σ' },
      { lotId: '26Y3A5ZP31', cylIdx: 17, value: 0.06, rawValue: 0.04, type: 'OOT', rule: 'Rule8: 양측 1σ' },
    ],
  },
  'CHF3': {
    'H2O': [
      { lotId: '26Y3C15E7L', cylIdx: 18, value: 7.2, rawValue: 6.8, type: 'OOC', rule: 'Rule1: 3σ 초과' },
      { lotId: '26Y3C15E7L', cylIdx: 19, value: 6.5, rawValue: 6.2, type: 'OOC', rule: 'Rule1: 3σ 초과' },
    ],
    'Total OFC': [
      { lotId: '26Y3C10B3M', cylIdx: 10, value: 720, rawValue: 690, type: 'OOC', rule: 'Rule1: 3σ 초과' },
      // Nelson Rule 5: 2σ 구간
      { lotId: '26Y3C10B3M', cylIdx: 11, value: 550, rawValue: 520, type: 'OOT', rule: 'Rule5: 2σ 구간' },
      { lotId: '26Y3C10B3M', cylIdx: 12, value: 160, rawValue: 150, type: 'OOT', rule: 'Rule5: 2σ 구간' },
      { lotId: '26Y3C10B3M', cylIdx: 13, value: 580, rawValue: 550, type: 'OOT', rule: 'Rule5: 2σ 구간' },
    ],
    'N2': [
      // Nelson Rule 7: 연속 15개가 1σ 내 (변동 부족 - Hugging)
      { lotId: '26Y3C15E7L', cylIdx: 1, value: 2.35, rawValue: 2.30, type: 'OOT', rule: 'Rule7: 변동부족' },
      { lotId: '26Y3C15E7L', cylIdx: 2, value: 2.32, rawValue: 2.28, type: 'OOT', rule: 'Rule7: 변동부족' },
      { lotId: '26Y3C15E7L', cylIdx: 3, value: 2.30, rawValue: 2.26, type: 'OOT', rule: 'Rule7: 변동부족' },
      { lotId: '26Y3C15E7L', cylIdx: 4, value: 2.33, rawValue: 2.29, type: 'OOT', rule: 'Rule7: 변동부족' },
      { lotId: '26Y3C15E7L', cylIdx: 5, value: 2.31, rawValue: 2.27, type: 'OOT', rule: 'Rule7: 변동부족' },
      { lotId: '26Y3C15E7L', cylIdx: 6, value: 2.29, rawValue: 2.25, type: 'OOT', rule: 'Rule7: 변동부족' },
      { lotId: '26Y3C15E7L', cylIdx: 7, value: 2.34, rawValue: 2.30, type: 'OOT', rule: 'Rule7: 변동부족' },
      { lotId: '26Y3C15E7L', cylIdx: 8, value: 2.30, rawValue: 2.26, type: 'OOT', rule: 'Rule7: 변동부족' },
    ],
  },
  'CF4': {
    'CO2': [
      // Nelson Rule 2: 연속 9개가 중심선 한쪽
      { lotId: '26Y3G16I3U', cylIdx: 10, value: 0.35, rawValue: 0.32, type: 'OOT', rule: 'Rule2: 중심선 편향' },
      { lotId: '26Y3G16I3U', cylIdx: 11, value: 0.38, rawValue: 0.35, type: 'OOT', rule: 'Rule2: 중심선 편향' },
      { lotId: '26Y3G16I3U', cylIdx: 12, value: 0.42, rawValue: 0.39, type: 'OOT', rule: 'Rule2: 중심선 편향' },
      { lotId: '26Y3G16I3U', cylIdx: 13, value: 0.36, rawValue: 0.33, type: 'OOT', rule: 'Rule2: 중심선 편향' },
      { lotId: '26Y3G16I3U', cylIdx: 14, value: 0.40, rawValue: 0.37, type: 'OOT', rule: 'Rule2: 중심선 편향' },
      { lotId: '26Y3G16I3U', cylIdx: 15, value: 0.37, rawValue: 0.34, type: 'OOT', rule: 'Rule2: 중심선 편향' },
      { lotId: '26Y3G16I3U', cylIdx: 16, value: 0.44, rawValue: 0.41, type: 'OOT', rule: 'Rule2: 중심선 편향' },
      { lotId: '26Y3G16I3U', cylIdx: 17, value: 0.39, rawValue: 0.36, type: 'OOT', rule: 'Rule2: 중심선 편향' },
      { lotId: '26Y3G16I3U', cylIdx: 18, value: 0.41, rawValue: 0.38, type: 'OOT', rule: 'Rule2: 중심선 편향' },
    ],
    'O2+Ar': [
      { lotId: '26Y3G12E6V', cylIdx: 20, value: 8.5, rawValue: 8.1, type: 'OOC', rule: 'Rule1: 3σ 초과' },
    ],
    'CH4': [
      // Nelson Rule 3: 연속 6개 감소
      { lotId: '26Y3G16I3U', cylIdx: 1, value: 0.45, rawValue: 0.42, type: 'OOT', rule: 'Rule3: 연속하락' },
      { lotId: '26Y3G16I3U', cylIdx: 2, value: 0.38, rawValue: 0.35, type: 'OOT', rule: 'Rule3: 연속하락' },
      { lotId: '26Y3G16I3U', cylIdx: 3, value: 0.30, rawValue: 0.28, type: 'OOT', rule: 'Rule3: 연속하락' },
      { lotId: '26Y3G16I3U', cylIdx: 4, value: 0.22, rawValue: 0.20, type: 'OOT', rule: 'Rule3: 연속하락' },
      { lotId: '26Y3G16I3U', cylIdx: 5, value: 0.15, rawValue: 0.13, type: 'OOT', rule: 'Rule3: 연속하락' },
      { lotId: '26Y3G16I3U', cylIdx: 6, value: 0.08, rawValue: 0.06, type: 'OOT', rule: 'Rule3: 연속하락' },
    ],
  },
  'C3F6': {
    'H2O': [
      // Nelson Rule 1: 규격 초과
      { lotId: '26Y3B12D4K', cylIdx: 8, value: 8.8, rawValue: 8.5, type: 'OOS', rule: 'Spec 초과' },
    ],
    'CO2': [
      // Nelson Rule 2: 중심선 편향 (모두 평균 위)
      { lotId: '26Y3B08A2J', cylIdx: 5, value: 0.55, rawValue: 0.52, type: 'OOT', rule: 'Rule2: 중심선 편향' },
      { lotId: '26Y3B08A2J', cylIdx: 6, value: 0.58, rawValue: 0.55, type: 'OOT', rule: 'Rule2: 중심선 편향' },
      { lotId: '26Y3B08A2J', cylIdx: 7, value: 0.52, rawValue: 0.49, type: 'OOT', rule: 'Rule2: 중심선 편향' },
      { lotId: '26Y3B08A2J', cylIdx: 8, value: 0.60, rawValue: 0.57, type: 'OOT', rule: 'Rule2: 중심선 편향' },
      { lotId: '26Y3B08A2J', cylIdx: 9, value: 0.54, rawValue: 0.51, type: 'OOT', rule: 'Rule2: 중심선 편향' },
      { lotId: '26Y3B08A2J', cylIdx: 10, value: 0.56, rawValue: 0.53, type: 'OOT', rule: 'Rule2: 중심선 편향' },
      { lotId: '26Y3B08A2J', cylIdx: 11, value: 0.59, rawValue: 0.56, type: 'OOT', rule: 'Rule2: 중심선 편향' },
      { lotId: '26Y3B08A2J', cylIdx: 12, value: 0.53, rawValue: 0.50, type: 'OOT', rule: 'Rule2: 중심선 편향' },
      { lotId: '26Y3B08A2J', cylIdx: 13, value: 0.57, rawValue: 0.54, type: 'OOT', rule: 'Rule2: 중심선 편향' },
    ],
  },
};

// 이상 데이터를 적용한 차트 데이터 생성
const genChartDataWithAbnormal = (lots, lim, product, itemName) => {
  const data = [];
  let idx = 0;
  const abnormalList = ABNORMAL_DATA[product]?.[itemName] || [];
  
  lots.forEach((lot, lotIndex) => {
    const baseNum = parseInt(lot.id.slice(-3), 36) % 1000 + 25000;
    
    for (let i = 0; i < lot.cyls; i++) {
      idx++;
      const cylNo = (baseNum + i).toString();
      const lotAvgOffset = (lotIndex - lots.length / 2) * lim.avg * 0.1;
      
      // 기본 정상 데이터
      let y = lim.avg + lotAvgOffset + (Math.random() - 0.5) * lim.avg * 0.3;
      let rawY = lim.avg * 0.85 + lotAvgOffset + (Math.random() - 0.5) * lim.avg * 0.25;
      let abnormalType = null;
      let abnormalRule = null;
      
      // 이상 데이터 확인 및 적용
      const abnormal = abnormalList.find(a => a.lotId === lot.id && a.cylIdx === (i + 1));
      if (abnormal) {
        y = abnormal.value;
        rawY = abnormal.rawValue;
        abnormalType = abnormal.type;
        abnormalRule = abnormal.rule;
      }
      
      // 이상 판정 (실시간 계산)
      let detectedType = abnormalType;
      let detectedRule = abnormalRule;
      
      if (!detectedType) {
        if (y > lim.USL) {
          detectedType = 'OOS';
          detectedRule = 'Spec 초과';
        } else if (y > lim.UCL) {
          detectedType = 'OOC';
          detectedRule = 'UCL 초과';
        }
      }
      
      data.push({
        idx,
        cylLabel: idx + '_21ENKE' + cylNo,
        y: parseFloat(y.toFixed(3)),
        rawY: parseFloat(rawY.toFixed(3)),
        lotId: lot.id,
        lotDate: lot.date,
        cylNo: '21ENKE' + cylNo,
        lotIndex,
        abnormalType: detectedType,
        abnormalRule: detectedRule,
      });
    }
  });
  return data;
};

// 단일 LOT 이상 데이터 생성
const genSingleLotDataWithAbnormal = (lot, lim, product, itemName) => {
  const data = [];
  const baseNum = parseInt(lot.id.slice(-3), 36) % 1000 + 25000;
  const abnormalList = ABNORMAL_DATA[product]?.[itemName] || [];
  
  for (let i = 0; i < lot.cyls; i++) {
    const cylNo = (baseNum + i).toString();
    
    let y = lim.avg + (Math.random() - 0.5) * lim.avg * 0.3;
    let rawY = lim.avg * 0.85 + (Math.random() - 0.5) * lim.avg * 0.25;
    let abnormalType = null;
    let abnormalRule = null;
    
    const abnormal = abnormalList.find(a => a.lotId === lot.id && a.cylIdx === (i + 1));
    if (abnormal) {
      y = abnormal.value;
      rawY = abnormal.rawValue;
      abnormalType = abnormal.type;
      abnormalRule = abnormal.rule;
    }
    
    let detectedType = abnormalType;
    let detectedRule = abnormalRule;
    
    if (!detectedType) {
      if (y > lim.USL) {
        detectedType = 'OOS';
        detectedRule = 'Spec 초과';
      } else if (y > lim.UCL) {
        detectedType = 'OOC';
        detectedRule = 'UCL 초과';
      }
    }
    
    data.push({
      idx: i + 1,
      cylLabel: (i + 1) + '_21ENKE' + cylNo,
      y: parseFloat(y.toFixed(3)),
      rawY: parseFloat(rawY.toFixed(3)),
      lotId: lot.id,
      lotDate: lot.date,
      cylNo: '21ENKE' + cylNo,
      abnormalType: detectedType,
      abnormalRule: detectedRule,
    });
  }
  return data;
};

// 모니터링 컴포넌트
const Monitoring = ({ interlockLogs = [] }) => {
  const [activeTab, setActiveTab] = useState('realtime');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date().toLocaleTimeString('ko-KR'));
  
  // 실시간 MES 데이터 (시뮬레이션)
  const [mesData, setMesData] = useState([
    { id: 1, time: '14:32:15', lot: '26Y3A61C3I', product: 'C4F8', cyl: '21ENKE25839', stage: 'FQC', item: 'H2O', value: 1.25, status: 'normal' },
    { id: 2, time: '14:31:42', lot: '26Y3A61C3I', product: 'C4F8', cyl: '21ENKE25838', stage: 'FQC', item: 'N2', value: 2.31, status: 'normal' },
    { id: 3, time: '14:30:58', lot: '26Y3A61C3I', product: 'C4F8', cyl: '21ENKE25837', stage: 'FQC', item: 'Total OFC', value: 165, status: 'warning' },
    { id: 4, time: '14:29:33', lot: '26Y3A5ZP31', product: 'CHF3', cyl: '21ENKE26010', stage: 'IQC', item: 'H2O', value: 0.85, status: 'normal' },
    { id: 5, time: '14:28:15', lot: '26Y3A5ZP31', product: 'CHF3', cyl: '21ENKE26009', stage: 'IQC', item: 'CO2', value: 0.22, status: 'normal' },
    { id: 6, time: '14:27:02', lot: '26Y3C10B3M', product: 'CF4', cyl: '21ENKE25950', stage: 'FQC', item: 'O2+Ar', value: 6.85, status: 'critical' },
  ]);
  
  // 항목별 현황 데이터
  const itemStatus = [
    { item: 'H2O', coa: '수분', lastValue: 1.25, avg: 1.18, trend: 'up', status: 'normal', count: 48, usl: 8, ucl: 6 },
    { item: 'N2', coa: 'Air', lastValue: 2.31, avg: 2.45, trend: 'down', status: 'normal', count: 48, usl: 25, ucl: 18.75 },
    { item: 'Total OFC', coa: '순도', lastValue: 165, avg: 149, trend: 'up', status: 'warning', count: 48, usl: 900, ucl: 675 },
    { item: 'O2+Ar', coa: 'Air', lastValue: 6.85, avg: 0.24, trend: 'up', status: 'critical', count: 47, usl: 10, ucl: 7.5 },
    { item: 'CO2', coa: 'Air', lastValue: 0.22, avg: 0.24, trend: 'stable', status: 'normal', count: 48, usl: 5, ucl: 3.75 },
    { item: 'CH4', coa: 'Air', lastValue: 0.15, avg: 0.15, trend: 'stable', status: 'normal', count: 48, usl: 5, ucl: 3.75 },
    { item: 'HF', coa: '산도', lastValue: 0.08, avg: 0.12, trend: 'down', status: 'normal', count: 45, usl: 1, ucl: 0.75 },
    { item: 'Metal Total', coa: 'Metal', lastValue: 0.65, avg: 0.85, trend: 'down', status: 'normal', count: 42, usl: 15, ucl: 11.25 },
  ];
  
  // 장비 상태
  const equipmentStatus = [
    { name: 'GC-1 (Air분석)', status: 'running', lastCheck: '14:30' },
    { name: 'GC-2 (순도분석)', status: 'running', lastCheck: '14:28' },
    { name: 'FTMS-1 (수분분석)', status: 'running', lastCheck: '14:25' },
    { name: 'ICP-MS (Metal)', status: 'idle', lastCheck: '13:45' },
  ];
  
  // 데이터 갱신 시뮬레이션
  const refreshData = () => {
    setLastUpdate(new Date().toLocaleTimeString('ko-KR'));
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'normal': return <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">정상</span>;
      case 'warning': return <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">주의</span>;
      case 'critical': return <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">이상</span>;
      default: return null;
    }
  };
  
  const getTrendIcon = (trend) => {
    switch(trend) {
      case 'up': return <span className="text-red-500">↑</span>;
      case 'down': return <span className="text-green-500">↓</span>;
      case 'stable': return <span className="text-slate-400">→</span>;
      default: return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* 헤더 */}
      <div className="bg-white rounded-lg p-4 border flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            <span className="text-sm font-medium">MES 연동 상태: 정상</span>
          </div>
          <span className="text-xs text-slate-400">마지막 갱신: {lastUpdate}</span>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} className="rounded"/>
            자동 갱신
          </label>
          <button onClick={refreshData} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center gap-1">
            <Activity size={14}/>
            새로고침
          </button>
        </div>
      </div>

      {/* 탭 */}
      <div className="bg-white rounded-lg border">
        <div className="flex border-b">
          <button onClick={() => setActiveTab('realtime')} className={'px-6 py-3 text-sm font-medium ' + (activeTab === 'realtime' ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:bg-slate-50')}>
            실시간 현황
          </button>
          <button onClick={() => setActiveTab('items')} className={'px-6 py-3 text-sm font-medium ' + (activeTab === 'items' ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:bg-slate-50')}>
            항목별 현황
          </button>
          <button onClick={() => setActiveTab('equipment')} className={'px-6 py-3 text-sm font-medium ' + (activeTab === 'equipment' ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:bg-slate-50')}>
            장비 현황
          </button>
          <button onClick={() => setActiveTab('interlock')} className={'px-6 py-3 text-sm font-medium ' + (activeTab === 'interlock' ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:bg-slate-50')}>
            Interlock 로그
          </button>
        </div>

        {/* 실시간 현황 탭 */}
        {activeTab === 'realtime' && (
          <div className="p-4">
            <div className="mb-4 grid grid-cols-4 gap-4">
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-xs text-slate-500">금일 검사</p>
                <p className="text-2xl font-bold text-slate-800">156</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-xs text-slate-500">정상</p>
                <p className="text-2xl font-bold text-green-600">152</p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-3 text-center">
                <p className="text-xs text-slate-500">주의</p>
                <p className="text-2xl font-bold text-yellow-600">3</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <p className="text-xs text-slate-500">이상</p>
                <p className="text-2xl font-bold text-red-600">1</p>
              </div>
            </div>
            
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  {['시간', 'LOT', '품목', 'CYL', '단계', '항목', '측정값', '상태'].map(h => (
                    <th key={h} className="px-3 py-2 text-left text-xs font-medium text-slate-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {mesData.map(row => (
                  <tr key={row.id} className={'border-t hover:bg-slate-50 ' + (row.status === 'critical' ? 'bg-red-50' : row.status === 'warning' ? 'bg-yellow-50' : '')}>
                    <td className="px-3 py-2 text-slate-500">{row.time}</td>
                    <td className="px-3 py-2 font-medium">{row.lot}</td>
                    <td className="px-3 py-2">{row.product}</td>
                    <td className="px-3 py-2 text-xs">{row.cyl}</td>
                    <td className="px-3 py-2">
                      <span className={'px-2 py-0.5 rounded text-xs ' + (row.stage === 'FQC' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600')}>
                        {row.stage}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-medium">{row.item}</td>
                    <td className={'px-3 py-2 font-medium ' + (row.status === 'critical' ? 'text-red-600' : row.status === 'warning' ? 'text-yellow-600' : '')}>{row.value}</td>
                    <td className="px-3 py-2">{getStatusBadge(row.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 항목별 현황 탭 */}
        {activeTab === 'items' && (
          <div className="p-4">
            <div className="grid grid-cols-2 gap-4">
              {itemStatus.map(item => (
                <div key={item.item} className={'p-4 rounded-lg border ' + (item.status === 'critical' ? 'border-red-300 bg-red-50' : item.status === 'warning' ? 'border-yellow-300 bg-yellow-50' : 'bg-white')}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-lg">{item.item}</span>
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{item.coa}</span>
                    </div>
                    {getStatusBadge(item.status)}
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-xs text-slate-500">최근 측정값</p>
                      <p className="font-bold text-lg flex items-center gap-1">
                        {item.lastValue} {getTrendIcon(item.trend)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">평균</p>
                      <p className="font-medium">{item.avg}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">검사 건수</p>
                      <p className="font-medium">{item.count}건</p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t">
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>0</span>
                      <span>UCL: {item.ucl}</span>
                      <span>USL: {item.usl}</span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full mt-1 relative overflow-hidden">
                      <div className="absolute left-0 top-0 h-full bg-green-400" style={{width: `${(item.ucl / item.usl) * 100}%`}}></div>
                      <div className="absolute top-0 h-full w-1 bg-blue-600" style={{left: `${Math.min((item.lastValue / item.usl) * 100, 100)}%`}}></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 장비 현황 탭 */}
        {activeTab === 'equipment' && (
          <div className="p-4">
            <div className="grid grid-cols-2 gap-4">
              {equipmentStatus.map(eq => (
                <div key={eq.name} className="p-4 bg-white rounded-lg border flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={'w-3 h-3 rounded-full ' + (eq.status === 'running' ? 'bg-green-500 animate-pulse' : 'bg-slate-300')}></div>
                    <div>
                      <p className="font-medium">{eq.name}</p>
                      <p className="text-xs text-slate-500">마지막 점검: {eq.lastCheck}</p>
                    </div>
                  </div>
                  <span className={'px-2 py-1 rounded text-xs font-medium ' + (eq.status === 'running' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600')}>
                    {eq.status === 'running' ? '가동중' : '대기'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Interlock 로그 탭 */}
        {activeTab === 'interlock' && (
          <div className="p-4">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle size={18} className="text-red-500"/>
                <span className="font-medium">Interlock 해제 이력</span>
                <span className="text-xs text-slate-500">({interlockLogs.length}건)</span>
              </div>
            </div>
            {interlockLogs.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <AlertTriangle size={40} className="mx-auto text-slate-300 mb-2"/>
                <p>Interlock 해제 이력이 없습니다</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {['일시', '유형', '항목', 'LOT', 'CYL', '측정값', '조치', '승인유형', '승인자', '담당자', '사유'].map(h => (
                      <th key={h} className="px-3 py-2 text-left text-xs font-medium text-slate-600">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {interlockLogs.map(log => (
                    <tr key={log.id} className="border-t hover:bg-slate-50">
                      <td className="px-3 py-2 text-xs text-slate-500">{log.datetime}</td>
                      <td className="px-3 py-2">
                        <span className={'px-1.5 py-0.5 rounded text-xs font-medium ' + 
                          (log.type === 'OOS' ? 'bg-red-100 text-red-700' : 
                           log.type === 'OOC' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700')}>
                          {log.type}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-medium">{log.item}</td>
                      <td className="px-3 py-2">{log.lot}</td>
                      <td className="px-3 py-2 text-xs">{log.cyl}</td>
                      <td className="px-3 py-2 text-red-600 font-medium">{log.value}</td>
                      <td className="px-3 py-2">
                        <span className={'px-2 py-0.5 rounded text-xs font-medium ' + 
                          (log.action === '승인해제' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700')}>
                          {log.action}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs">{log.approvalType || '-'}</td>
                      <td className="px-3 py-2">{log.approver || '-'}</td>
                      <td className="px-3 py-2 font-medium">{log.handler}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{log.reason || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// 부적합집계 상세 컴포넌트 (Cp/Cpk 월별 상세 데이터)
const RejectManagement = () => {
  const [rejectSubTab, setRejectSubTab] = useState('status');
  
  // Cp/Cpk 월별 데이터 (제품/상품 - Pure Gas)
  const cpCpkData = [
    { gas: 'C4F8 (C318EH)', param: 'H2O', target: 2.0, months: [2.85, 3.12, 2.95, 3.25, 2.78, 3.45, 3.12, 2.89, 3.02, 2.98, 3.15, 3.22] },
    { gas: 'C4F8 (C318EH)', param: 'N2', target: 2.0, months: [3.21, 3.45, 3.18, 3.56, 3.22, 3.78, 3.55, 3.32, 3.41, 3.38, 3.52, 3.61] },
    { gas: 'CHF3 (23EH)', param: 'H2O', target: 2.0, months: [2.54, 2.78, 2.65, 2.89, 2.45, 3.12, 2.88, 2.56, 2.72, 2.68, 2.85, 2.92] },
    { gas: 'CHF3 (23EH)', param: 'N2', target: 2.0, months: [3.02, 3.25, 3.08, 3.35, 3.01, 3.52, 3.28, 3.05, 3.18, 3.15, 3.32, 3.40] },
    { gas: 'CH2F2 (HFC-32EH)', param: 'H2O', target: 2.0, months: [2.15, 2.35, 2.22, 2.48, 2.12, 2.68, 2.45, 2.18, 2.32, 2.28, 2.45, 2.52] },
    { gas: 'CH2F2 (HFC-32EH)', param: 'N2', target: 2.0, months: [2.88, 3.12, 2.95, 3.22, 2.85, 3.45, 3.18, 2.92, 3.05, 3.02, 3.18, 3.25] },
    { gas: 'C2H2F4 (HFC-134aEH)', param: 'H2O', target: 2.0, months: [2.42, 2.65, 2.52, 2.78, 2.38, 2.95, 2.72, 2.45, 2.58, 2.55, 2.72, 2.78] },
    { gas: 'C2H2F4 (HFC-134aEH)', param: 'Acidity', target: 2.0, months: [2.68, 2.92, 2.75, 3.05, 2.65, 3.22, 2.98, 2.72, 2.85, 2.82, 2.98, 3.05] },
    { gas: 'C3F6 (C3F6EH)', param: 'H2O', target: 2.0, months: [2.25, 2.48, 2.35, 2.62, 2.22, 2.78, 2.55, 2.28, 2.42, 2.38, 2.55, 2.62] },
    { gas: 'CF4', param: 'H2O', target: 2.0, months: [2.55, 2.78, 2.65, 2.92, 2.52, 3.08, 2.85, 2.58, 2.72, 2.68, 2.85, 2.92] },
    { gas: 'COS (COSEH)', param: 'H2O', target: 2.0, months: [3.45, 3.68, 3.55, 3.82, 3.42, 3.98, 3.75, 3.48, 3.62, 3.58, 3.75, 3.82] },
  ];
  
  // 특수가스 Cpk 데이터 (C4F6 등)
  const specialGasCpkData = [
    { gas: 'C4F6', param: 'H2O', target: 1.33, months: [1.74, 1.85, 1.72, 1.92, 1.68, 2.05, 1.88, 1.75, 1.82, 1.78, 1.88, 1.95] },
    { gas: 'C4F6', param: 'N2', target: 1.33, months: [1.55, 1.68, 1.52, 1.75, 1.48, 1.85, 1.68, 1.55, 1.62, 1.58, 1.68, 1.75] },
    { gas: 'C4F8 (Sample)', param: 'H2O', target: 1.33, months: [1.65, 1.78, 1.62, 1.85, 1.58, 1.95, 1.78, 1.65, 1.72, 1.68, 1.78, 1.85] },
    { gas: 'C2H2F4 (Sample)', param: 'H2O', target: 1.33, months: [1.48, 1.62, 1.45, 1.68, 1.42, 1.78, 1.62, 1.48, 1.55, 1.52, 1.62, 1.68] },
    { gas: 'CH2F2 (반제품)', param: 'H2O', target: 1.33, months: [1.72, 1.85, 1.68, 1.92, 1.65, 2.02, 1.85, 1.72, 1.78, 1.75, 1.85, 1.92] },
    { gas: 'C4F8 (반제품)', param: 'H2O', target: 1.33, months: [1.58, 1.72, 1.55, 1.78, 1.52, 1.88, 1.72, 1.58, 1.65, 1.62, 1.72, 1.78] },
  ];
  
  // 상품(Goods) 부적합 데이터
  const goodsRejectData = [
    { gas: 'C4F8 (C318EH)', param: 'H2O', qty: 84, ins: 84, rej: 0, planPct: 2.14, rejPct: 0, ocQty: 0, ocPct: 0, cpk: 2.35 },
    { gas: 'C4F8 (C318EH)', param: 'N2', qty: 84, ins: 84, rej: 0, planPct: 2.14, rejPct: 0, ocQty: 0, ocPct: 0, cpk: 2.45 },
    { gas: 'CHF3 (23EH)', param: 'H2O', qty: 45, ins: 45, rej: 0, planPct: 2.14, rejPct: 0, ocQty: 0, ocPct: 0, cpk: 2.12 },
    { gas: 'CH2F2 (HFC-32EH)', param: 'H2O', qty: 32, ins: 32, rej: 0, planPct: 2.14, rejPct: 0, ocQty: 0, ocPct: 0, cpk: 2.28 },
    { gas: 'C2H2F4 (HFC-134aEH)', param: 'H2O', qty: 28, ins: 28, rej: 0, planPct: 2.14, rejPct: 0, ocQty: 0, ocPct: 3.00, cpk: 2.00 },
    { gas: 'C3F6 (C3F6EH)', param: 'H2O', qty: 22, ins: 22, rej: 0, planPct: 2.14, rejPct: 0, ocQty: 0, ocPct: 3.00, cpk: 2.15 },
    { gas: 'CF4', param: 'H2O', qty: 18, ins: 18, rej: 0, planPct: 2.14, rejPct: 0, ocQty: 0, ocPct: 3.00, cpk: 1.92 },
    { gas: 'COS (COSEH)', param: 'H2O', qty: 15, ins: 15, rej: 0, planPct: 2.14, rejPct: 0, ocQty: 0, ocPct: 3.00, cpk: 2.05 },
  ];
  
  // 원료(Source) 부적합 데이터
  const sourceRejectData = [
    { gas: 'C4F8 (C318_Dongyue)', param: 'H2O', qty: 24, ins: 24, rej: 0, planPct: 2.13, rejPct: 0, ocQty: 0, ocPct: 0, cpk: 2.00 },
    { gas: 'C4F8 (C318_DNCS)', param: 'H2O', qty: 20, ins: 20, rej: 0, planPct: 2.13, rejPct: 0, ocQty: 0, ocPct: 0, cpk: 2.15 },
    { gas: 'C4F8 (C318EH_DILY)', param: 'H2O', qty: 18, ins: 18, rej: 0, planPct: 2.13, rejPct: 0, ocQty: 0, ocPct: 0, cpk: 2.00 },
    { gas: 'CH2F2 (HFC-32EH)', param: 'H2O', qty: 15, ins: 15, rej: 0, planPct: 2.13, rejPct: 0, ocQty: 0, ocPct: 0, cpk: 2.08 },
    { gas: 'C2H2F4 (HFC-134a_DRF)', param: 'H2O', qty: 12, ins: 12, rej: 0, planPct: 2.13, rejPct: 0, ocQty: 0, ocPct: 0, cpk: 2.00 },
    { gas: 'C2H2F4 (HFC-134a_DILY)', param: 'H2O', qty: 12, ins: 12, rej: 0, planPct: 2.13, rejPct: 0, ocQty: 0, ocPct: 0, cpk: 1.95 },
    { gas: 'COS (COSEH)', param: 'H2O', qty: 10, ins: 10, rej: 0, planPct: 2.13, rejPct: 0, ocQty: 0, ocPct: 0, cpk: 2.00 },
    { gas: 'C3F6 (C3F6_DILY)', param: 'H2O', qty: 8, ins: 8, rej: 0, planPct: 2.13, rejPct: 0, ocQty: 0, ocPct: 0, cpk: 2.00 },
    { gas: 'C2H2F4 (HFC-134a_MEX)', param: 'H2O', qty: 6, ins: 6, rej: 0, planPct: 2.13, rejPct: 0, ocQty: 0, ocPct: 0, cpk: 1.92 },
  ];
  
  // 월별 불량율 트렌드
  const monthlyRejectTrend = [
    { month: '1월', goods: 0.5, source: 0.8, outgoing: 2.8 },
    { month: '2월', goods: 0.3, source: 0.6, outgoing: 3.2 },
    { month: '3월', goods: 0.8, source: 1.2, outgoing: 2.5 },
    { month: '4월', goods: 0.4, source: 0.5, outgoing: 2.1 },
    { month: '5월', goods: 0.2, source: 0.3, outgoing: 1.8 },
    { month: '6월', goods: 0.6, source: 0.9, outgoing: 2.3 },
    { month: '7월', goods: 0.3, source: 0.4, outgoing: 1.5 },
    { month: '8월', goods: 0.1, source: 0.2, outgoing: 0.8 },
    { month: '9월', goods: 0, source: 0, outgoing: 0.38 },
  ];
  
  // 부적합 내역 (Outgoing)
  const defectDetails = [
    { type: '출전표 오류기', do: '', rej: 4, planPct: 0.25, rejPct: 0.19 },
    { type: '성적서 오류', do: '', rej: 3, planPct: 0.23, rejPct: 0.11 },
    { type: '도색불량', do: '', rej: 2, planPct: 0.32, rejPct: 0.08 },
    { type: 'Label 및 Sticker불량', do: '', rej: 1, planPct: 2.23, rejPct: 0.04 },
    { type: 'Valve 불량', do: '2,625', rej: 0, planPct: 0.04, rejPct: 0 },
    { type: 'Leak 불량', do: '', rej: 0, planPct: 0.05, rejPct: 0 },
    { type: '용기 불량', do: '', rej: 0, planPct: 0.03, rejPct: 0 },
    { type: '농도 불량', do: '', rej: 0, planPct: 0.04, rejPct: 0 },
    { type: '충전량 불량', do: '', rej: 0, planPct: 0.06, rejPct: 0 },
    { type: '기타', do: '', rej: 0, planPct: 0.08, rejPct: 0 },
  ];
  
  const months = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];
  
  const getCpkColor = (value, target) => {
    if (value >= target * 1.5) return 'bg-green-100 text-green-800';
    if (value >= target) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };
  
  const goodsTotal = { qty: 114, ins: 114, rej: 0, rejPct: 0 };
  const sourceTotal = { qty: 117, ins: 117, rej: 0, rejPct: 2.13 };
  
  return (
    <div className="p-4">
      {/* 서브탭 */}
      <div className="flex gap-2 mb-4">
        <button onClick={() => setRejectSubTab('status')} className={'px-4 py-2 rounded text-sm font-medium ' + (rejectSubTab === 'status' ? 'bg-blue-600 text-white' : 'bg-slate-100 hover:bg-slate-200')}>
          Reject 현황
        </button>
        <button onClick={() => setRejectSubTab('goods')} className={'px-4 py-2 rounded text-sm font-medium ' + (rejectSubTab === 'goods' ? 'bg-blue-600 text-white' : 'bg-slate-100 hover:bg-slate-200')}>
          Goods Reject (상품)
        </button>
        <button onClick={() => setRejectSubTab('source')} className={'px-4 py-2 rounded text-sm font-medium ' + (rejectSubTab === 'source' ? 'bg-blue-600 text-white' : 'bg-slate-100 hover:bg-slate-200')}>
          Source Reject (원료)
        </button>
        <button onClick={() => setRejectSubTab('outgoing')} className={'px-4 py-2 rounded text-sm font-medium ' + (rejectSubTab === 'outgoing' ? 'bg-blue-600 text-white' : 'bg-slate-100 hover:bg-slate-200')}>
          Outgoing Reject
        </button>
      </div>
      
      {/* Reject 현황 */}
      {rejectSubTab === 'status' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-lg">2026년 SPC 자료 (Cp/Cpk)</h3>
            <div className="flex gap-4 text-sm">
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-500 rounded"></span> Pure Gas: Cp {'>'} 2.0 목표</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-500 rounded"></span> 특수가스: Cpk {'>'} 1.33 목표</span>
            </div>
          </div>
          
          {/* Pure Gas Table */}
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="bg-slate-100 px-4 py-2 font-medium text-sm">Pure Gas Cp/Cpk 현황</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-2 py-2 text-left font-medium">Gas Name</th>
                    <th className="px-2 py-2 text-left font-medium">항목</th>
                    <th className="px-2 py-2 text-center font-medium">목표</th>
                    {months.map(m => <th key={m} className="px-2 py-2 text-center font-medium">{m}</th>)}
                    <th className="px-2 py-2 text-center font-medium">평균</th>
                  </tr>
                </thead>
                <tbody>
                  {cpCpkData.map((row, idx) => {
                    const avg = (row.months.reduce((a, b) => a + b, 0) / row.months.length).toFixed(2);
                    return (
                      <tr key={idx} className="border-t hover:bg-slate-50">
                        <td className="px-2 py-1.5 font-medium">{row.gas}</td>
                        <td className="px-2 py-1.5">{row.param}</td>
                        <td className="px-2 py-1.5 text-center">{row.target}</td>
                        {row.months.map((v, i) => (
                          <td key={i} className="px-2 py-1.5 text-center">
                            <span className={`px-1.5 py-0.5 rounded text-xs ${getCpkColor(v, row.target)}`}>{v.toFixed(2)}</span>
                          </td>
                        ))}
                        <td className="px-2 py-1.5 text-center font-bold">{avg}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* 특수가스 Table */}
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="bg-slate-100 px-4 py-2 font-medium text-sm">특수가스/반제품/샘플 Cpk 현황 (C4F6, 반제품, Sample) - Cpk {'>'} 1.33 목표</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-2 py-2 text-left font-medium">Gas Name</th>
                    <th className="px-2 py-2 text-left font-medium">항목</th>
                    <th className="px-2 py-2 text-center font-medium">목표</th>
                    {months.map(m => <th key={m} className="px-2 py-2 text-center font-medium">{m}</th>)}
                    <th className="px-2 py-2 text-center font-medium">평균</th>
                  </tr>
                </thead>
                <tbody>
                  {specialGasCpkData.map((row, idx) => {
                    const avg = (row.months.reduce((a, b) => a + b, 0) / row.months.length).toFixed(2);
                    return (
                      <tr key={idx} className="border-t hover:bg-slate-50">
                        <td className="px-2 py-1.5 font-medium">{row.gas}</td>
                        <td className="px-2 py-1.5">{row.param}</td>
                        <td className="px-2 py-1.5 text-center">{row.target}</td>
                        {row.months.map((v, i) => (
                          <td key={i} className="px-2 py-1.5 text-center">
                            <span className={`px-1.5 py-0.5 rounded text-xs ${getCpkColor(v, row.target)}`}>{v.toFixed(2)}</span>
                          </td>
                        ))}
                        <td className="px-2 py-1.5 text-center font-bold">{avg}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Cp 트렌드 차트 */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-lg border p-4">
              <h4 className="font-medium text-sm mb-3">월별 Cp 현황 (Pure Gas)</h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={months.map((m, i) => ({ month: m, C4F8: cpCpkData[0].months[i], CHF3: cpCpkData[2].months[i], CF4: cpCpkData[4].months[i] }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis domain={[1.5, 4]} tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: '11px' }} />
                  <Legend wrapperStyle={{ fontSize: '10px' }} />
                  <ReferenceLine y={2.0} stroke="#22c55e" strokeDasharray="5 5" label={{ value: '목표 2.0', fontSize: 10, fill: '#22c55e' }} />
                  <Line type="monotone" dataKey="C4F8" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="CHF3" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="CF4" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-lg border p-4">
              <h4 className="font-medium text-sm mb-3">월별 Cpk 현황 (특수가스/반제품)</h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={months.map((m, i) => ({ month: m, 'C4F6': specialGasCpkData[0].months[i], 'CH2F2반제품': specialGasCpkData[4].months[i], 'C4F8반제품': specialGasCpkData[5].months[i] }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis domain={[1, 2.5]} tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: '11px' }} />
                  <Legend wrapperStyle={{ fontSize: '10px' }} />
                  <ReferenceLine y={1.33} stroke="#22c55e" strokeDasharray="5 5" label={{ value: '목표 1.33', fontSize: 10, fill: '#22c55e' }} />
                  <Line type="monotone" dataKey="C4F6" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="CH2F2반제품" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="C4F8반제품" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
      
      {/* Goods Reject */}
      {rejectSubTab === 'goods' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold">1.2 Goods Reject (상품 부적합)</h3>
            <span className="text-sm text-slate-500">2026년 9월 실적</span>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <div className="bg-white rounded-lg border overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Gas Name</th>
                      <th className="px-3 py-2 text-left">Control Parameter</th>
                      <th className="px-3 py-2 text-center">Q'ty</th>
                      <th className="px-3 py-2 text-center">Ins.</th>
                      <th className="px-3 py-2 text-center">Rej</th>
                      <th className="px-3 py-2 text-center">Plan(%)</th>
                      <th className="px-3 py-2 text-center">X Rej</th>
                      <th className="px-3 py-2 text-center">OC Q'ty</th>
                      <th className="px-3 py-2 text-center">OC(%)</th>
                      <th className="px-3 py-2 text-center">Cp/Cpk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {goodsRejectData.map((row, idx) => (
                      <tr key={idx} className="border-t hover:bg-slate-50">
                        <td className="px-3 py-1.5 font-medium">{row.gas}</td>
                        <td className="px-3 py-1.5">{row.param}</td>
                        <td className="px-3 py-1.5 text-center">{row.qty}</td>
                        <td className="px-3 py-1.5 text-center">{row.ins}</td>
                        <td className="px-3 py-1.5 text-center">{row.rej}</td>
                        <td className="px-3 py-1.5 text-center">{row.planPct}%</td>
                        <td className="px-3 py-1.5 text-center">{row.rejPct}%</td>
                        <td className="px-3 py-1.5 text-center">{row.ocQty}</td>
                        <td className="px-3 py-1.5 text-center">{row.ocPct}%</td>
                        <td className="px-3 py-1.5 text-center font-medium">{row.cpk}</td>
                      </tr>
                    ))}
                    <tr className="border-t bg-yellow-50 font-bold">
                      <td className="px-3 py-2" colSpan="2">Good Total</td>
                      <td className="px-3 py-2 text-center">{goodsTotal.qty}</td>
                      <td className="px-3 py-2 text-center">{goodsTotal.ins}</td>
                      <td className="px-3 py-2 text-center">{goodsTotal.rej}</td>
                      <td className="px-3 py-2 text-center">2.14%</td>
                      <td className="px-3 py-2 text-center">0.00%</td>
                      <td className="px-3 py-2 text-center">0</td>
                      <td className="px-3 py-2 text-center">3.00%</td>
                      <td className="px-3 py-2 text-center">2.00</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className="bg-white rounded-lg border p-4">
              <h4 className="font-medium text-sm mb-3">Goods 불량율 (0%)</h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={monthlyRejectTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis domain={[0, 10]} tick={{ fontSize: 10 }} tickFormatter={v => `${v}%`} />
                  <Tooltip contentStyle={{ fontSize: '11px' }} formatter={(v) => `${v}%`} />
                  <ReferenceLine y={2.14} stroke="#22c55e" strokeDasharray="5 5" label={{ value: '목표: 2.14%', fontSize: 10, fill: '#22c55e' }} />
                  <Line type="monotone" dataKey="goods" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} name="상품 불량율" />
                </LineChart>
              </ResponsiveContainer>
              <div className="text-center text-sm text-green-600 font-medium mt-2">
                상품,원료 모두 불량율이 0%임.
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Source Reject */}
      {rejectSubTab === 'source' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold">1.3 Source Reject (원료 부적합)</h3>
            <span className="text-sm text-slate-500">2026년 9월 실적</span>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <div className="bg-white rounded-lg border overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Gas Name</th>
                      <th className="px-3 py-2 text-left">Control Parameter</th>
                      <th className="px-3 py-2 text-center">Q'ty</th>
                      <th className="px-3 py-2 text-center">Ins.</th>
                      <th className="px-3 py-2 text-center">Rej</th>
                      <th className="px-3 py-2 text-center">Plan(%)</th>
                      <th className="px-3 py-2 text-center">X Rej</th>
                      <th className="px-3 py-2 text-center">OC Q'ty</th>
                      <th className="px-3 py-2 text-center">OC(%)</th>
                      <th className="px-3 py-2 text-center">Cp/Cpk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sourceRejectData.map((row, idx) => (
                      <tr key={idx} className="border-t hover:bg-slate-50">
                        <td className="px-3 py-1.5 font-medium">{row.gas}</td>
                        <td className="px-3 py-1.5">{row.param}</td>
                        <td className="px-3 py-1.5 text-center">{row.qty}</td>
                        <td className="px-3 py-1.5 text-center">{row.ins}</td>
                        <td className="px-3 py-1.5 text-center">{row.rej > 0 ? <span className="text-red-600 font-bold">{row.rej}</span> : row.rej}</td>
                        <td className="px-3 py-1.5 text-center">{row.planPct}%</td>
                        <td className="px-3 py-1.5 text-center">{row.rejPct > 0 ? <span className="text-red-600">{row.rejPct}%</span> : `${row.rejPct}%`}</td>
                        <td className="px-3 py-1.5 text-center">{row.ocQty}</td>
                        <td className="px-3 py-1.5 text-center">{row.ocPct}%</td>
                        <td className="px-3 py-1.5 text-center font-medium">{row.cpk}</td>
                      </tr>
                    ))}
                    <tr className="border-t bg-yellow-50 font-bold">
                      <td className="px-3 py-2" colSpan="2">Raw Total</td>
                      <td className="px-3 py-2 text-center">{sourceTotal.qty}</td>
                      <td className="px-3 py-2 text-center">{sourceTotal.ins}</td>
                      <td className="px-3 py-2 text-center">0</td>
                      <td className="px-3 py-2 text-center">2.13%</td>
                      <td className="px-3 py-2 text-center">0.00%</td>
                      <td className="px-3 py-2 text-center">0</td>
                      <td className="px-3 py-2 text-center">3.00%</td>
                      <td className="px-3 py-2 text-center">0.00</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className="bg-white rounded-lg border p-4">
              <h4 className="font-medium text-sm mb-3">Source 불량율 (0%)</h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={monthlyRejectTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis domain={[0, 60]} tick={{ fontSize: 10 }} tickFormatter={v => `${v}%`} />
                  <Tooltip contentStyle={{ fontSize: '11px' }} formatter={(v) => `${v}%`} />
                  <ReferenceLine y={2.13} stroke="#22c55e" strokeDasharray="5 5" label={{ value: '목표: 2.13%', fontSize: 10, fill: '#22c55e' }} />
                  <Line type="monotone" dataKey="source" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4 }} name="원료 불량율" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
      
      {/* Outgoing Reject */}
      {rejectSubTab === 'outgoing' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold">2. Outgoing Reject 현황</h3>
            <span className="text-sm text-slate-500">2026년 실적</span>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Total 출하불량 Trend */}
            <div className="bg-white rounded-lg border p-4">
              <h4 className="font-medium text-sm mb-3">2.1 Total 출하불량 실적</h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={monthlyRejectTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis domain={[0, 4]} tick={{ fontSize: 10 }} tickFormatter={v => `${v}%`} />
                  <Tooltip contentStyle={{ fontSize: '11px' }} formatter={(v) => `${v}%`} />
                  <ReferenceLine y={3.38} stroke="#22c55e" strokeDasharray="5 5" label={{ value: '목표: 3.38%', fontSize: 10, fill: '#22c55e' }} />
                  <Line type="monotone" dataKey="outgoing" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} name="출하 불량율" />
                </LineChart>
              </ResponsiveContainer>
              <div className="text-center mt-2">
                <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                  9월 불량율: 0.38%
                </span>
              </div>
            </div>
            
            {/* 월별 부적합 트렌드 (Stacked Bar) */}
            <div className="bg-white rounded-lg border p-4">
              <h4 className="font-medium text-sm mb-3">2026년 월별 부적합 트렌드</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={[
                  { month: '1월', 출전표오류: 12, 성적서오류: 8, 도색불량: 5, Label불량: 3, 기타: 2 },
                  { month: '2월', 출전표오류: 15, 성적서오류: 10, 도색불량: 7, Label불량: 4, 기타: 3 },
                  { month: '3월', 출전표오류: 18, 성적서오류: 12, 도색불량: 8, Label불량: 5, 기타: 4 },
                  { month: '4월', 출전표오류: 14, 성적서오류: 9, 도색불량: 6, Label불량: 3, 기타: 2 },
                  { month: '5월', 출전표오류: 11, 성적서오류: 7, 도색불량: 4, Label불량: 2, 기타: 1 },
                  { month: '6월', 출전표오류: 16, 성적서오류: 11, 도색불량: 7, Label불량: 4, 기타: 3 },
                  { month: '7월', 출전표오류: 10, 성적서오류: 6, 도색불량: 3, Label불량: 2, 기타: 1 },
                  { month: '8월', 출전표오류: 6, 성적서오류: 4, 도색불량: 2, Label불량: 1, 기타: 0 },
                  { month: '9월', 출전표오류: 4, 성적서오류: 3, 도색불량: 2, Label불량: 1, 기타: 0 },
                ]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: '11px' }} />
                  <Legend wrapperStyle={{ fontSize: '10px' }} />
                  <Bar dataKey="출전표오류" stackId="a" fill="#22c55e" />
                  <Bar dataKey="성적서오류" stackId="a" fill="#84cc16" />
                  <Bar dataKey="도색불량" stackId="a" fill="#facc15" />
                  <Bar dataKey="Label불량" stackId="a" fill="#f97316" />
                  <Bar dataKey="기타" stackId="a" fill="#a855f7" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          {/* 부적합 내역 테이블 */}
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="bg-slate-100 px-4 py-2 font-medium text-sm">2.2 9월 원인별 출하불량 비율</div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">부적합 내역</th>
                  <th className="px-4 py-2 text-center font-medium">Do</th>
                  <th className="px-4 py-2 text-center font-medium">Rej</th>
                  <th className="px-4 py-2 text-center font-medium">Plan(%)</th>
                  <th className="px-4 py-2 text-center font-medium">Rej(%)</th>
                </tr>
              </thead>
              <tbody>
                {defectDetails.map((row, idx) => (
                  <tr key={idx} className="border-t hover:bg-slate-50">
                    <td className="px-4 py-2">{row.type}</td>
                    <td className="px-4 py-2 text-center">{row.do}</td>
                    <td className="px-4 py-2 text-center">{row.rej > 0 ? <span className="text-red-600 font-bold">{row.rej}</span> : row.rej}</td>
                    <td className="px-4 py-2 text-center">{row.planPct}%</td>
                    <td className="px-4 py-2 text-center">{row.rejPct > 0 ? <span className="text-red-600">{row.rejPct}%</span> : `${row.rejPct}%`}</td>
                  </tr>
                ))}
                <tr className="border-t bg-yellow-50 font-bold">
                  <td className="px-4 py-2">Total</td>
                  <td className="px-4 py-2 text-center"></td>
                  <td className="px-4 py-2 text-center">10</td>
                  <td className="px-4 py-2 text-center">3.38%</td>
                  <td className="px-4 py-2 text-center">0.38%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const SPC = () => {
  const [nr,setNr] = useState([1,2,3]);
  const [cr,setCr] = useState(['C1','C5']);
  const [activeTab, setActiveTab] = useState('spc');
  const [selectedCoA, setSelectedCoA] = useState('Air');
  const [selectedProduct, setSelectedProduct] = useState('C4F8');
  const [selectedLot, setSelectedLot] = useState('전체');
  const [histogramItem, setHistogramItem] = useState('H2O');
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [showCauseRegisterModal, setShowCauseRegisterModal] = useState(false);
  const [showCauseEditModal, setShowCauseEditModal] = useState(false);
  const [showCauseDeleteModal, setShowCauseDeleteModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showHistoryDeleteModal, setShowHistoryDeleteModal] = useState(false);
  const [historyDeleteIdx, setHistoryDeleteIdx] = useState(null);
  const [historyFormData, setHistoryFormData] = useState({ action: '', by: '' });
  const [causeFormData, setCauseFormData] = useState({
    matNo: '', matName: 'C4F8', item: 'H2O', content: '',
    causes: { spec: '', material: '', equipment: '', method: '', environment: '' },
    assignee: '', reviewDept: ''
  });
  const [causeAnalysisList, setCauseAnalysisList] = useState([
    {
      id: 'SPC-2026-001',
      date: '2026-01-15',
      matNo: '26Y3A61C3I',
      matName: 'C4F8',
      item: 'H2O',
      content: 'C4F8 제품 수분(H2O) 함량 USL 초과 발생, 규격 5.0ppm 대비 6.85ppm 검출',
      status: 'inProgress',
      causes: {
        spec: 'H2O 함량 USL(5.0ppm) 초과로 제품 순도 규격 미달, 고객사 요구 순도 99.999% 충족 불가',
        material: '원료 가스 순도 99.9999% 확인 완료, 입고 검사 성적서 이상 없음',
        equipment: '정제탑 Molecular Sieve 흡착제 교체 주기 도래 (6개월 경과), 흡착 효율 저하 의심',
        method: 'GC-MS 분석법 정상, 검량선 R²=0.999 확인, 분석 표준물질 유효기간 내',
        environment: '클린룸 온도 23±1°C, 습도 35% 유지 중, 환경 요인 정상',
      },
      assignee: '박병욱',
      reviewDept: '품질관리팀',
      isReviewed: false,
      history: [
        { date: '2026-01-15 14:32', action: '[SPC] H2O 항목 USL Over 발생 (LOT: 26Y3A61C3I)', by: '시스템' },
      ]
    },
    {
      id: 'SPC-2026-002',
      date: '2026-01-14',
      matNo: '26Y3C10B3M',
      matName: 'CHF3',
      item: 'Total OFC',
      content: 'OFC 관리한계 초과 발생, UCL: 675, 측정값: 720',
      status: 'completed',
      causes: {
        spec: 'OFC 수치 UCL 초과로 공정 능력 저하',
        material: '원료 순도 99.999% 확인 (정상)',
        equipment: '정류탑 온도 센서 노후화 확인, 교체 필요',
        method: '기존 분석법 적정',
        environment: '클린룸 환경 정상 (온도 23°C, 습도 38%)',
      },
      assignee: '노민석',
      reviewDept: '품질관리팀',
      isReviewed: true,
      history: [
        { date: '2026-01-14 10:15', action: '[SPC] Total OFC 항목 OOC 발생 (LOT: 26Y3C10B3M)', by: '시스템' },
      ]
    },
    {
      id: 'SPC-2026-003',
      date: '2026-01-12',
      matNo: '26Y3G16I3U',
      matName: 'CF4',
      item: 'N2',
      content: 'Rule2 위반: 연속 9점 중심선 한쪽 편향',
      status: 'pending',
      causes: {
        spec: '중심선 편향으로 인한 공정 이상 징후',
        material: '분석 중',
        equipment: '분석 중',
        method: '분석 중',
        environment: '분석 중',
      },
      assignee: '변상필',
      reviewDept: '공정기술팀',
      isReviewed: false,
      history: [
        { date: '2026-01-12 16:00', action: '[SPC] N2 항목 Rule2 위반 감지 (LOT: 26Y3G16I3U)', by: '시스템' },
      ]
    },
  ]);
  const [newReview, setNewReview] = useState('');
  const [newReviewTitle, setNewReviewTitle] = useState('');
  const [newReviewLot, setNewReviewLot] = useState('26Y3A61C3I');
  const [newReviewItem, setNewReviewItem] = useState('H2O');
  const [newReviewStatus, setNewReviewStatus] = useState('진행중');
  const [editingReview, setEditingReview] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [reviewFilter, setReviewFilter] = useState('전체 상태');
  const [reviewItemFilter, setReviewItemFilter] = useState('전체 항목');
  
  // 선택된 품명의 LOT 목록
  const productLots = PRODUCT_LOTS[selectedProduct] || [];
  
  // 품명 변경 시 LOT 초기화
  const handleProductChange = (product) => {
    setSelectedProduct(product);
    setSelectedLot('전체');
  };
  
  // 선택된 CoA에 해당하는 불순물 목록 가져오기
  const getImpuritiesByCoA = (coa) => {
    return Object.entries(LIMITS)
      .filter(([_, lim]) => lim.coa === coa)
      .map(([name, lim]) => ({ name, ...lim }));
  };

  const selectedImpurities = getImpuritiesByCoA(selectedCoA);
  
  // 검토의견 샘플 데이터
  const [reviews, setReviews] = useState([
    { id: 1, date: '2026-01-15', author: '김품질', item: 'H2O', lot: '26Y3A61C3I', title: 'H2O 농도 상승 검토', 
      content: '1. 충전 후 분석 결과에서 불순물 농도는 IQC 대비 감소 경향을 보였으나, 이전 충전 이력 대비 다소 높은 수준으로 확인.\n2. IQC 단계에서부터 불순물 농도가 상대적으로 높게 확인된 점을 고려할 때, 원료 특성에 따른 영향 가능성이 높은 것으로 판단', status: '완료' },
    { id: 2, date: '2026-01-12', author: '박엔지니어', item: 'O2+Ar', lot: '26Y3A5ZP31', title: 'O2+Ar 이상 발생 조치',
      content: '설비 점검 결과 밸브 이상 확인, 교체 조치 완료. 재발 방지를 위해 주간 점검 항목에 추가.', status: '완료' },
    { id: 3, date: '2026-01-10', author: '이분석', item: 'Total OFC', lot: '26Y3A5ZP31', title: 'OFC 경향 모니터링',
      content: 'OFC 수치가 평균 대비 상승 추세. 원료 입고 LOT 변경에 따른 영향으로 추정. 지속 모니터링 필요.', status: '진행중' },
  ]);

  const addReview = () => {
    if (!newReview.trim() || !newReviewTitle.trim()) return;
    const review = {
      id: Math.max(...reviews.map(r => r.id), 0) + 1,
      date: new Date().toISOString().split('T')[0],
      author: '관리자',
      item: newReviewItem,
      lot: newReviewLot,
      title: newReviewTitle,
      content: newReview,
      status: newReviewStatus
    };
    setReviews([review, ...reviews]);
    resetNewReviewForm();
  };
  
  const resetNewReviewForm = () => {
    setNewReview('');
    setNewReviewTitle('');
    setNewReviewLot('26Y3A61C3I');
    setNewReviewItem('H2O');
    setNewReviewStatus('진행중');
  };
  
  const startEditReview = (review) => {
    setEditingReview(review.id);
    setEditContent(review.content);
  };
  
  const saveEditReview = (id) => {
    setReviews(reviews.map(r => r.id === id ? { ...r, content: editContent } : r));
    setEditingReview(null);
    setEditContent('');
  };
  
  const cancelEditReview = () => {
    setEditingReview(null);
    setEditContent('');
  };
  
  const deleteReview = (id) => {
    if (confirm('이 검토의견을 삭제하시겠습니까?')) {
      setReviews(reviews.filter(r => r.id !== id));
    }
  };
  
  // 검토의견 필터링
  const filteredReviews = reviews.filter(r => {
    if (reviewFilter !== '전체 상태' && r.status !== reviewFilter) return false;
    if (reviewItemFilter !== '전체 항목' && r.item !== reviewItemFilter) return false;
    return true;
  });

  // 이상 감지 상세보기 클릭 핸들러 - 원인분석 탭으로 이동
  const handleAnomalyDetailClick = ({ item, product, lot, abnormalPoints }) => {
    // 해당 항목과 LOT에 맞는 SPC Notice 찾기
    const matchingNotice = causeAnalysisList.find(notice => 
      notice.item === item && (notice.matNo === lot || notice.matName === product)
    ) || causeAnalysisList.find(notice => notice.item === item);
    
    if (matchingNotice) {
      setSelectedNotice(matchingNotice);
    } else {
      // 매칭되는 Notice가 없으면 새로 생성 제안
      const newNoticeId = `SPC-2026-${String(causeAnalysisList.length + 1).padStart(3, '0')}`;
      const abnormalType = abnormalPoints[0]?.abnormalType || 'OOC';
      const abnormalRule = abnormalPoints[0]?.abnormalRule || '';
      const abnormalValue = abnormalPoints[0]?.y || '';
      const newNotice = {
        id: newNoticeId,
        date: new Date().toISOString().split('T')[0],
        matNo: lot || productLots[0]?.id || '26Y3A61C3I',
        matName: product,
        item: item,
        content: `${product} 제품 ${item} 항목 이상 발생 - ${abnormalType} (${abnormalRule}, 측정값: ${abnormalValue})`,
        status: 'pending',
        causes: { spec: '분석 중', material: '분석 중', equipment: '분석 중', method: '분석 중', environment: '분석 중' },
        assignee: '',
        reviewDept: '',
        isReviewed: false,
        history: [{ date: new Date().toISOString().replace('T', ' ').substring(0, 16), action: `[SPC] ${item} 항목 ${abnormalType} 발생 (LOT: ${lot || '자동감지'})`, by: '시스템' }]
      };
      setCauseAnalysisList([newNotice, ...causeAnalysisList]);
      setSelectedNotice(newNotice);
    }
    
    // 원인분석 탭으로 이동
    setActiveTab('causeAnalysis');
  };

  return (
    <div className="space-y-4">
      {/* 검색조건 */}
      <div className="bg-white rounded-lg p-4 border">
        <div className="flex items-center gap-2 mb-3"><Filter size={14}/><span className="font-medium text-sm">검색조건</span></div>
        <div className="grid grid-cols-5 gap-3 mb-4">
          <div>
            <label className="text-xs text-slate-500">시작일</label>
            <input type="date" defaultValue="2026-01-01" className="w-full px-2 py-1 border rounded text-sm"/>
          </div>
          <div>
            <label className="text-xs text-slate-500">종료일</label>
            <input type="date" defaultValue="2026-01-17" className="w-full px-2 py-1 border rounded text-sm"/>
          </div>
          <div>
            <label className="text-xs text-slate-500">품명</label>
            <select 
              className="w-full px-2 py-1 border rounded text-sm"
              value={selectedProduct}
              onChange={(e) => handleProductChange(e.target.value)}
            >
              {PRODUCTS.map(p=><option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">LOT ({productLots.length}개)</label>
            <select 
              className="w-full px-2 py-1 border rounded text-sm"
              value={selectedLot}
              onChange={(e) => setSelectedLot(e.target.value)}
            >
              <option value="전체">전체 ({productLots.reduce((a,b) => a + b.cyls, 0)} CYL)</option>
              {productLots.map(lot => (
                <option key={lot.id} value={lot.id}>
                  {lot.id} ({lot.date}, {lot.cyls}CYL)
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button className="px-3 py-1 border rounded text-sm" onClick={() => {setSelectedProduct('C4F8'); setSelectedLot('전체');}}>초기화</button>
            <button className="px-3 py-1 bg-blue-600 text-white rounded text-sm flex items-center gap-1"><Search size={12}/>검색</button>
          </div>
        </div>
        <div className="border-t pt-3">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-xs text-slate-600 w-16">Nelson:</span>
            <div className="flex flex-wrap gap-2">{NELSON_RULES.map(r=><label key={r.id} className="flex items-center gap-1 text-xs cursor-pointer"><input type="checkbox" checked={nr.includes(r.id)} onChange={()=>setNr(p=>p.includes(r.id)?p.filter(x=>x!==r.id):[...p,r.id])}/>{r.name}</label>)}</div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs w-16" style={{color:THEME.accent}}>고객Rule:</span>
            <div className="flex flex-wrap gap-2">{SEC_RULES.map(r=><label key={r.id} className="flex items-center gap-1 text-xs cursor-pointer"><input type="checkbox" checked={cr.includes(r.id)} onChange={()=>setCr(p=>p.includes(r.id)?p.filter(x=>x!==r.id):[...p,r.id])}/><span className={'px-1.5 py-0.5 rounded text-xs '+(r.sev==='critical'?'bg-red-100 text-red-700':r.sev==='warning'?'bg-yellow-100 text-yellow-700':'bg-cyan-100 text-cyan-700')}>{r.name}</span></label>)}</div>
          </div>
        </div>
      </div>

      {/* 탭 메뉴 */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="flex border-b">
          <button 
            onClick={() => setActiveTab('spc')}
            className={'px-6 py-3 text-sm font-medium flex items-center gap-2 ' + (activeTab === 'spc' ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:bg-slate-50')}
          >
            <BarChart3 size={16} />
            SPC 분석
          </button>
          <button 
            onClick={() => setActiveTab('histogram')}
            className={'px-6 py-3 text-sm font-medium flex items-center gap-2 ' + (activeTab === 'histogram' ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:bg-slate-50')}
          >
            <BarChart3 size={16} />
            히스토그램
          </button>
          <button 
            onClick={() => setActiveTab('causeAnalysis')}
            className={'px-6 py-3 text-sm font-medium flex items-center gap-2 ' + (activeTab === 'causeAnalysis' ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:bg-slate-50')}
          >
            <Search size={16} />
            원인분석
          </button>
          <button 
            onClick={() => setActiveTab('review')}
            className={'px-6 py-3 text-sm font-medium flex items-center gap-2 ' + (activeTab === 'review' ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:bg-slate-50')}
          >
            <FileText size={16} />
            검토의견
            <span className={'px-1.5 py-0.5 rounded-full text-xs ' + (activeTab === 'review' ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600')}>{reviews.length}</span>
          </button>
        </div>

        {/* SPC 분석 탭 */}
        {activeTab === 'spc' && (
          <div className="p-4">
            {/* 선택 정보 표시 */}
            <div className="mb-4 p-3 bg-blue-50 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-sm">
                  <span className="text-slate-500">품명:</span> <span className="font-medium">{selectedProduct}</span>
                </span>
                <span className="text-sm">
                  <span className="text-slate-500">LOT:</span> <span className="font-medium">{selectedLot === '전체' ? `전체 (${productLots.length}개)` : selectedLot}</span>
                </span>
                <span className="text-sm">
                  <span className="text-slate-500">실린더:</span> <span className="font-medium">
                    {selectedLot === '전체' 
                      ? productLots.reduce((a,b) => a + b.cyls, 0) 
                      : productLots.find(l => l.id === selectedLot)?.cyls || 0}개
                  </span>
                </span>
              </div>
            </div>

            {/* CoA 분류 선택 */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm font-medium text-slate-700">CoA 분류 선택:</span>
                <span className="text-xs text-slate-500">({selectedImpurities.length}개 항목)</span>
              </div>
              <div className="flex gap-2">
                {COA_LIST.map(coa => {
                  const count = getImpuritiesByCoA(coa).length;
                  return (
                    <button
                      key={coa}
                      onClick={() => setSelectedCoA(coa)}
                      className={'px-4 py-2 rounded-lg text-sm font-medium transition-colors ' + 
                        (selectedCoA === coa 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}
                    >
                      {coa}
                      <span className={'ml-1.5 text-xs ' + (selectedCoA === coa ? 'text-blue-200' : 'text-slate-400')}>
                        ({count})
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 선택된 CoA의 불순물 차트들 */}
            <div className={'grid gap-4 ' + (selectedImpurities.length <= 2 ? 'grid-cols-1' : 'grid-cols-2')}>
              {selectedImpurities.map(imp => (
                <ChartWithLot 
                  key={imp.name + selectedProduct + selectedLot + nr.join(',') + cr.join(',')} 
                  title={imp.name} 
                  lim={imp}
                  product={selectedProduct}
                  selectedLot={selectedLot}
                  lots={productLots}
                  nelsonRules={nr}
                  secRules={cr}
                  onDetailClick={handleAnomalyDetailClick}
                />
              ))}
            </div>

            {/* 통계 테이블 */}
            <div className="mt-4">
              <div className="bg-white rounded-lg border">
                <div className="px-4 py-2 border-b bg-slate-50 flex items-center justify-between">
                  <span className="font-medium text-sm">{selectedCoA} 분류 통계 요약 - {selectedProduct}</span>
                  <span className="text-xs text-slate-500">{selectedImpurities.length}개 항목</span>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      {['항목', 'USL', 'UCL', '평균', 'μ+3σ', 'Cpk', '판정'].map(h => (
                        <th key={h} className="px-3 py-2 text-xs text-slate-600 text-left">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedImpurities.map(imp => {
                      const cpk = ((imp.USL - imp.avg) / (imp.mu3sigma - imp.avg)).toFixed(2);
                      const isOk = parseFloat(cpk) > 1.33;
                      return (
                        <tr key={imp.name} className="border-t hover:bg-slate-50">
                          <td className="px-3 py-2 font-medium">{imp.name}</td>
                          <td className="px-3 py-2">{imp.USL}</td>
                          <td className="px-3 py-2">{imp.UCL}</td>
                          <td className="px-3 py-2">{imp.avg}</td>
                          <td className="px-3 py-2">{imp.mu3sigma}</td>
                          <td className="px-3 py-2 text-blue-600 font-medium">{cpk}</td>
                          <td className="px-3 py-2">
                            <span className={'px-2 py-0.5 rounded text-xs font-medium ' + 
                              (isOk ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700')}>
                              {isOk ? 'OK' : 'NG'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* 히스토그램 탭 */}
        {activeTab === 'histogram' && (
          <div className="p-4">
            {/* 항목 선택 */}
            <div className="mb-4 flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-600">분석 항목:</label>
                <select 
                  value={histogramItem} 
                  onChange={e => setHistogramItem(e.target.value)}
                  className="px-3 py-1.5 border rounded text-sm"
                >
                  {Object.keys(LIMITS).map(item => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
              </div>
              <div className="text-sm text-slate-500">
                품명: <span className="font-medium text-slate-700">{selectedProduct}</span> | 
                LOT: <span className="font-medium text-slate-700">{selectedLot === '전체' ? '전체' : selectedLot}</span>
              </div>
            </div>

            {(() => {
              const lim = LIMITS[histogramItem] || { USL: 10, UCL: 7.5, avg: 2, mu3sigma: 5 };
              const lots = PRODUCT_LOTS[selectedProduct] || [];
              const chartData = genChartDataWithAbnormal(lots, lim, selectedProduct, histogramItem);
              const allData = chartData.map(d => d.rawY);
              
              if (allData.length === 0) {
                for (let i = 0; i < 100; i++) {
                  allData.push(lim.avg + (Math.random() - 0.5) * (lim.UCL - lim.avg) * 1.5);
                }
              }
              
              const mean = allData.reduce((a, b) => a + b, 0) / allData.length;
              const variance = allData.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / allData.length;
              const sigma = Math.sqrt(variance);
              const min = Math.min(...allData);
              const max = Math.max(...allData);
              const USL = lim.USL;
              const LSL = lim.LSL || 0;
              
              const Cp = (USL - LSL) / (6 * sigma);
              const Cpu = (USL - mean) / (3 * sigma);
              const Cpl = (mean - LSL) / (3 * sigma);
              const Cpk = Math.min(Cpu, Cpl);
              
              const sampleVar = allData.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / (allData.length - 1);
              const sampleSigma = Math.sqrt(sampleVar);
              const Pp = (USL - LSL) / (6 * sampleSigma);
              const Ppu = (USL - mean) / (3 * sampleSigma);
              const Ppl = (mean - LSL) / (3 * sampleSigma);
              const Ppk = Math.min(Ppu, Ppl);
              
              const binCount = 12;
              const binWidth = (max - min) / binCount || 1;
              const bins = [];
              for (let i = 0; i < binCount; i++) {
                const binStart = min + i * binWidth;
                const binEnd = binStart + binWidth;
                const count = allData.filter(v => v >= binStart && v < binEnd).length;
                bins.push({
                  range: `${binStart.toFixed(2)}`,
                  count,
                  binStart,
                  binEnd
                });
              }
              
              const abnormalCount = allData.filter(v => v > USL || v < LSL).length;
              const lotCount = lots.length || 1;
              
              return (
                <div className="space-y-4">
                  <div className="bg-white rounded-lg border p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-medium flex items-center gap-2">
                        <BarChart3 size={18} className="text-blue-600"/>
                        Histogram - {histogramItem}
                      </h3>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span>LSL: {LSL}</span>
                        <span className="text-red-500 font-medium">USL: {USL}</span>
                      </div>
                    </div>
                    
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={bins} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                          <XAxis 
                            dataKey="range" 
                            tick={{ fontSize: 10 }}
                            label={{ value: 'X-Axis Interval', position: 'bottom', offset: 20, fontSize: 11 }}
                          />
                          <YAxis 
                            tick={{ fontSize: 10 }}
                            label={{ value: 'Count', angle: -90, position: 'insideLeft', fontSize: 11 }}
                          />
                          <Tooltip 
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const d = payload[0].payload;
                                return (
                                  <div className="bg-white p-2 border rounded shadow text-xs">
                                    <p>구간: {d.binStart.toFixed(2)} ~ {d.binEnd.toFixed(2)}</p>
                                    <p className="font-medium">빈도: {d.count}</p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <ReferenceLine x={mean.toFixed(2)} stroke="#2563eb" strokeWidth={2} strokeDasharray="5 5" label={{ value: 'μ', position: 'top', fontSize: 10 }}/>
                          <Bar dataKey="count" fill="#60a5fa" radius={[2, 2, 0, 0]}/>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    
                    <div className="mt-4 flex justify-between text-xs text-slate-500 px-4">
                      <span>{min.toFixed(2)}</span>
                      <span className="text-blue-600">{mean.toFixed(2)}</span>
                      <span>{max.toFixed(2)}</span>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg border overflow-hidden">
                    <div className="px-4 py-3 bg-slate-50 border-b">
                      <h4 className="font-medium text-sm">공정능력지수</h4>
                    </div>
                    <div className="p-4">
                      <table className="w-full text-sm">
                        <tbody>
                          <tr className="border-b">
                            <td className="py-2 px-3 bg-slate-50 font-medium w-24">평균</td>
                            <td className="py-2 px-3">{mean.toFixed(2)}</td>
                            <td className="py-2 px-3 bg-slate-50 font-medium w-24">시그마</td>
                            <td className="py-2 px-3">{sigma.toFixed(2)}</td>
                            <td className="py-2 px-3 bg-slate-50 font-medium w-24">샘플 크기</td>
                            <td className="py-2 px-3">{allData.length}</td>
                            <td className="py-2 px-3 bg-slate-50 font-medium w-24">Lot/자질 수</td>
                            <td className="py-2 px-3">{lotCount}</td>
                          </tr>
                          <tr className="border-b">
                            <td className="py-2 px-3 bg-slate-50 font-medium">최대값</td>
                            <td className="py-2 px-3">{max.toFixed(2)}</td>
                            <td className="py-2 px-3 bg-slate-50 font-medium">최소값</td>
                            <td className="py-2 px-3">{min.toFixed(2)}</td>
                            <td className="py-2 px-3 bg-slate-50 font-medium">이상발생건수</td>
                            <td className="py-2 px-3 text-red-600 font-medium">{abnormalCount}</td>
                            <td className="py-2 px-3 bg-slate-50 font-medium">이상발생율수</td>
                            <td className="py-2 px-3">{((abnormalCount / allData.length) * 100).toFixed(1)}%</td>
                          </tr>
                          <tr>
                            <td className="py-2 px-3 bg-slate-50 font-medium">Pp</td>
                            <td className="py-2 px-3 text-blue-600 font-medium">{isFinite(Pp) ? Pp.toFixed(2) : '-'}</td>
                            <td className="py-2 px-3 bg-slate-50 font-medium">Ppk</td>
                            <td className="py-2 px-3 text-blue-600 font-medium">{isFinite(Ppk) ? Ppk.toFixed(2) : '-'}</td>
                            <td className="py-2 px-3 bg-slate-50 font-medium">Cp</td>
                            <td className="py-2 px-3 text-blue-600 font-medium">{isFinite(Cp) ? Cp.toFixed(2) : '-'}</td>
                            <td className="py-2 px-3 bg-slate-50 font-medium">Cpk</td>
                            <td className="py-2 px-3">
                              <span className={'px-2 py-0.5 rounded text-xs font-medium ' + 
                                (isFinite(Cpk) && Cpk >= 1.33 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700')}>
                                {isFinite(Cpk) ? Cpk.toFixed(2) : '-'}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* 원인분석 탭 */}
        {activeTab === 'causeAnalysis' && (
          <div className="p-4">
            <div className="grid grid-cols-3 gap-4">
              {/* 좌측: SPC Notice 목록 */}
              <div className="col-span-1 border rounded-lg overflow-hidden">
                <div className="bg-blue-600 text-white px-4 py-2 font-medium text-sm">
                  SPC Notice
                </div>
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="px-2 py-1.5 text-left">통보</th>
                        <th className="px-2 py-1.5 text-left">MAT-NO</th>
                        <th className="px-2 py-1.5 text-left">MAT 내용</th>
                      </tr>
                    </thead>
                    <tbody>
                      {causeAnalysisList.map(notice => (
                        <tr 
                          key={notice.id}
                          onClick={() => setSelectedNotice(notice)}
                          className={'border-t cursor-pointer hover:bg-blue-50 ' + (selectedNotice?.id === notice.id ? 'bg-blue-100' : '')}
                        >
                          <td className="px-2 py-2">
                            <span className={'w-2 h-2 rounded-full inline-block mr-1 ' + 
                              (notice.status === 'completed' ? 'bg-green-500' : notice.status === 'inProgress' ? 'bg-yellow-500' : 'bg-red-500')}></span>
                            {notice.date}
                          </td>
                          <td className="px-2 py-2 font-medium">{notice.matNo}</td>
                          <td className="px-2 py-2 truncate max-w-32">{notice.content}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 우측: 상세 분석 내용 */}
              <div className="col-span-2 space-y-4">
                {selectedNotice ? (
                  <>
                    {/* 분석 내용 헤더 */}
                    <div className="bg-slate-50 rounded-lg border p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{selectedNotice.id}</span>
                        <span className={'px-2 py-0.5 rounded text-xs font-medium ' + 
                          (selectedNotice.status === 'completed' ? 'bg-green-100 text-green-700' : 
                           selectedNotice.status === 'inProgress' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700')}>
                          {selectedNotice.status === 'completed' ? '완료' : selectedNotice.status === 'inProgress' ? '진행중' : '대기'}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600">{selectedNotice.content}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                        <span>품명: <strong>{selectedNotice.matName}</strong></span>
                        <span>항목: <strong>{selectedNotice.item}</strong></span>
                        <span>LOT: <strong>{selectedNotice.matNo}</strong></span>
                      </div>
                    </div>

                    {/* 부서별 원인분석 */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-slate-100 px-4 py-2 font-medium text-sm border-b">
                        부서별 원인분석
                      </div>
                      <table className="w-full text-sm">
                        <tbody>
                          <tr className="border-b">
                            <td className="px-4 py-2 bg-slate-50 font-medium w-20">사양</td>
                            <td className="px-4 py-2">{selectedNotice.causes.spec}</td>
                            <td className="px-4 py-2 text-xs text-slate-500 w-24">{selectedNotice.date}</td>
                          </tr>
                          <tr className="border-b">
                            <td className="px-4 py-2 bg-slate-50 font-medium">자재</td>
                            <td className="px-4 py-2">{selectedNotice.causes.material}</td>
                            <td className="px-4 py-2 text-xs text-slate-500">{selectedNotice.date}</td>
                          </tr>
                          <tr className="border-b">
                            <td className="px-4 py-2 bg-slate-50 font-medium">설비</td>
                            <td className="px-4 py-2">{selectedNotice.causes.equipment}</td>
                            <td className="px-4 py-2 text-xs text-slate-500">{selectedNotice.date}</td>
                          </tr>
                          <tr className="border-b">
                            <td className="px-4 py-2 bg-slate-50 font-medium">방법</td>
                            <td className="px-4 py-2">{selectedNotice.causes.method}</td>
                            <td className="px-4 py-2 text-xs text-slate-500">{selectedNotice.date}</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-2 bg-slate-50 font-medium">환경</td>
                            <td className="px-4 py-2">{selectedNotice.causes.environment}</td>
                            <td className="px-4 py-2 text-xs text-slate-500">{selectedNotice.date}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* 담당자 및 검토부서 */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="border rounded-lg p-3">
                        <label className="block text-xs text-slate-500 mb-1">담당자</label>
                        <p className="font-medium">{selectedNotice.assignee}</p>
                      </div>
                      <div className="border rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="block text-xs text-slate-500 mb-1">검토부서</label>
                            <p className="font-medium">{selectedNotice.reviewDept}</p>
                          </div>
                          <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={selectedNotice.isReviewed} readOnly className="rounded"/>
                            최종검토 확인
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* 버튼 */}
                    <div className="flex items-center gap-2">
                      <button onClick={() => {
                        setCauseFormData({ matNo: '', matName: 'C4F8', item: 'H2O', content: '', causes: { spec: '', material: '', equipment: '', method: '', environment: '' }, assignee: '', reviewDept: '' });
                        setShowCauseRegisterModal(true);
                      }} className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">등록</button>
                      <button onClick={() => {
                        setCauseFormData({
                          matNo: selectedNotice.matNo, matName: selectedNotice.matName, item: selectedNotice.item, content: selectedNotice.content,
                          causes: { ...selectedNotice.causes }, assignee: selectedNotice.assignee, reviewDept: selectedNotice.reviewDept
                        });
                        setShowCauseEditModal(true);
                      }} className="px-4 py-2 border rounded text-sm hover:bg-slate-50">수정</button>
                      <button onClick={() => setShowCauseDeleteModal(true)} className="px-4 py-2 border text-red-600 rounded text-sm hover:bg-red-50">삭제</button>
                      <button onClick={() => alert('메일 보내기 기능은 준비 중입니다.')} className="px-4 py-2 border rounded text-sm hover:bg-slate-50">메일 보내기</button>
                    </div>

                    {/* 처리 이력 */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-slate-100 px-4 py-2 font-medium text-sm border-b flex items-center justify-between">
                        <span>처리 이력</span>
                        <button onClick={() => { setHistoryFormData({ action: '', by: '' }); setShowHistoryModal(true); }} className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">+ 이력 추가</button>
                      </div>
                      <div className="p-3 max-h-32 overflow-y-auto">
                        {selectedNotice.history.map((h, idx) => (
                          <div key={idx} className="flex items-center gap-3 text-xs py-1 border-b last:border-0">
                            <span className="text-slate-500 w-32">{h.date}</span>
                            <span className="font-medium flex-1">{h.action}</span>
                            <span className="text-slate-400">by {h.by}</span>
                            {idx > 0 && (
                              <button onClick={() => { setHistoryDeleteIdx(idx); setShowHistoryDeleteModal(true); }} className="text-red-500 hover:text-red-700 px-1">✕</button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* 관련 차트 미리보기 */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-slate-100 px-4 py-2 font-medium text-sm border-b flex items-center justify-between">
                        <span>관련 SPC 차트</span>
                        <div className="flex items-center gap-3 text-xs">
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-600"></span>OOS</span>
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"></span>OOC</span>
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500"></span>OOT</span>
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-500"></span>SEC</span>
                        </div>
                      </div>
                      <div className="p-3 h-48">
                        {(() => {
                          const lim = LIMITS[selectedNotice.item] || { USL: 10, UCL: 7.5, avg: 2, mu3sigma: 5 };
                          const lots = PRODUCT_LOTS[selectedNotice.matName] || PRODUCT_LOTS['C4F8'];
                          const chartData = genChartDataWithAbnormal(lots, lim, selectedNotice.matName, selectedNotice.item);
                          const getAbnormalColor = (type) => {
                            switch(type) {
                              case 'OOS': return '#DC2626';
                              case 'OOC': return '#F59E0B';
                              case 'OOT': return '#3B82F6';
                              case 'SEC': return '#8B5CF6';
                              default: return '#2563eb';
                            }
                          };
                          const CustomDot = (props) => {
                            const { cx, cy, payload } = props;
                            if (!cx || !cy) return null;
                            const isAbnormal = payload.abnormalType;
                            const color = isAbnormal ? getAbnormalColor(payload.abnormalType) : '#2563eb';
                            const r = isAbnormal ? 5 : 2;
                            return (
                              <circle cx={cx} cy={cy} r={r} fill={color} stroke={isAbnormal ? '#fff' : 'none'} strokeWidth={isAbnormal ? 1 : 0}/>
                            );
                          };
                          return (
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                                <XAxis dataKey="idx" tick={{ fontSize: 9 }}/>
                                <YAxis domain={[0, lim.USL * 1.2]} tick={{ fontSize: 9 }}/>
                                <ReferenceLine y={lim.USL} stroke="#DC2626" strokeDasharray="5 5" label={{ value: 'USL', position: 'right', fontSize: 9 }}/>
                                <ReferenceLine y={lim.UCL} stroke="#F59E0B" strokeDasharray="3 3" label={{ value: 'UCL', position: 'right', fontSize: 9 }}/>
                                <Line type="monotone" dataKey="rawY" stroke="#2563eb" strokeWidth={1.5} dot={<CustomDot/>}/>
                              </LineChart>
                            </ResponsiveContainer>
                          );
                        })()}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-64 text-slate-400">
                    <div className="text-center">
                      <Search size={40} className="mx-auto mb-2 text-slate-300"/>
                      <p>좌측 목록에서 SPC Notice를 선택하세요</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 등록 모달 */}
            {showCauseRegisterModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-[600px] max-h-[80vh] overflow-y-auto shadow-xl">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <FileText className="text-blue-600"/>
                    원인분석 신규 등록
                  </h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">LOT No</label>
                        <input type="text" value={causeFormData.matNo} onChange={e => setCauseFormData({...causeFormData, matNo: e.target.value})} className="w-full px-3 py-2 border rounded text-sm" placeholder="예: 26Y3A61C3I"/>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">품명</label>
                        <select value={causeFormData.matName} onChange={e => setCauseFormData({...causeFormData, matName: e.target.value})} className="w-full px-3 py-2 border rounded text-sm">
                          <option>C4F8</option><option>CHF3</option><option>CF4</option><option>C3F6</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">분석 항목</label>
                        <select value={causeFormData.item} onChange={e => setCauseFormData({...causeFormData, item: e.target.value})} className="w-full px-3 py-2 border rounded text-sm">
                          {Object.keys(LIMITS).map(item => <option key={item}>{item}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">이상 내용</label>
                      <textarea value={causeFormData.content} onChange={e => setCauseFormData({...causeFormData, content: e.target.value})} className="w-full px-3 py-2 border rounded text-sm" rows={2} placeholder="이상 발생 내용 입력"/>
                    </div>
                    <div className="border-t pt-4">
                      <p className="font-medium text-sm mb-3">부서별 원인분석</p>
                      <div className="space-y-2">
                        {['spec', 'material', 'equipment', 'method', 'environment'].map((key, idx) => (
                          <div key={key} className="flex items-center gap-2">
                            <span className="w-12 text-xs font-medium text-slate-600">{['사양', '자재', '설비', '방법', '환경'][idx]}</span>
                            <input type="text" value={causeFormData.causes[key]} onChange={e => setCauseFormData({...causeFormData, causes: {...causeFormData.causes, [key]: e.target.value}})} className="flex-1 px-3 py-1.5 border rounded text-sm" placeholder={`${['사양', '자재', '설비', '방법', '환경'][idx]} 분석 내용`}/>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">담당자</label>
                        <input type="text" value={causeFormData.assignee} onChange={e => setCauseFormData({...causeFormData, assignee: e.target.value})} className="w-full px-3 py-2 border rounded text-sm" placeholder="담당자 이름"/>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">검토부서</label>
                        <input type="text" value={causeFormData.reviewDept} onChange={e => setCauseFormData({...causeFormData, reviewDept: e.target.value})} className="w-full px-3 py-2 border rounded text-sm" placeholder="검토부서"/>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-6">
                    <button onClick={() => setShowCauseRegisterModal(false)} className="flex-1 px-4 py-2 border rounded hover:bg-slate-50">취소</button>
                    <button onClick={() => {
                      const newId = 'SPC-2026-' + String(causeAnalysisList.length + 1).padStart(3, '0');
                      const newItem = {
                        id: newId, date: new Date().toISOString().split('T')[0], ...causeFormData,
                        status: 'pending', isReviewed: false, 
                        history: [{ date: new Date().toLocaleString('ko-KR'), action: `[SPC] ${causeFormData.item} 항목 이상 발생 (LOT: ${causeFormData.matNo})`, by: '시스템' }]
                      };
                      setCauseAnalysisList([newItem, ...causeAnalysisList]);
                      setShowCauseRegisterModal(false);
                      setSelectedNotice(newItem);
                    }} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">등록</button>
                  </div>
                </div>
              </div>
            )}

            {/* 수정 모달 */}
            {showCauseEditModal && selectedNotice && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-[600px] max-h-[80vh] overflow-y-auto shadow-xl">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <Settings className="text-yellow-600"/>
                    원인분석 수정 - {selectedNotice.id}
                  </h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">LOT No</label>
                        <input type="text" value={causeFormData.matNo} onChange={e => setCauseFormData({...causeFormData, matNo: e.target.value})} className="w-full px-3 py-2 border rounded text-sm"/>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">품명</label>
                        <select value={causeFormData.matName} onChange={e => setCauseFormData({...causeFormData, matName: e.target.value})} className="w-full px-3 py-2 border rounded text-sm">
                          <option>C4F8</option><option>CHF3</option><option>CF4</option><option>C3F6</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">분석 항목</label>
                        <select value={causeFormData.item} onChange={e => setCauseFormData({...causeFormData, item: e.target.value})} className="w-full px-3 py-2 border rounded text-sm">
                          {Object.keys(LIMITS).map(item => <option key={item}>{item}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">이상 내용</label>
                      <textarea value={causeFormData.content} onChange={e => setCauseFormData({...causeFormData, content: e.target.value})} className="w-full px-3 py-2 border rounded text-sm" rows={2}/>
                    </div>
                    <div className="border-t pt-4">
                      <p className="font-medium text-sm mb-3">부서별 원인분석</p>
                      <div className="space-y-2">
                        {['spec', 'material', 'equipment', 'method', 'environment'].map((key, idx) => (
                          <div key={key} className="flex items-center gap-2">
                            <span className="w-12 text-xs font-medium text-slate-600">{['사양', '자재', '설비', '방법', '환경'][idx]}</span>
                            <input type="text" value={causeFormData.causes[key]} onChange={e => setCauseFormData({...causeFormData, causes: {...causeFormData.causes, [key]: e.target.value}})} className="flex-1 px-3 py-1.5 border rounded text-sm"/>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">담당자</label>
                        <input type="text" value={causeFormData.assignee} onChange={e => setCauseFormData({...causeFormData, assignee: e.target.value})} className="w-full px-3 py-2 border rounded text-sm"/>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">검토부서</label>
                        <input type="text" value={causeFormData.reviewDept} onChange={e => setCauseFormData({...causeFormData, reviewDept: e.target.value})} className="w-full px-3 py-2 border rounded text-sm"/>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-6">
                    <button onClick={() => setShowCauseEditModal(false)} className="flex-1 px-4 py-2 border rounded hover:bg-slate-50">취소</button>
                    <button onClick={() => {
                      const updated = causeAnalysisList.map(item => {
                        if (item.id === selectedNotice.id) {
                          return { ...item, ...causeFormData };
                        }
                        return item;
                      });
                      setCauseAnalysisList(updated);
                      setSelectedNotice({ ...selectedNotice, ...causeFormData });
                      setShowCauseEditModal(false);
                    }} className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600">수정 저장</button>
                  </div>
                </div>
              </div>
            )}

            {/* 삭제 확인 모달 */}
            {showCauseDeleteModal && selectedNotice && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-red-600">
                    <AlertTriangle/>
                    삭제 확인
                  </h3>
                  <p className="text-sm text-slate-600 mb-4">
                    다음 원인분석 항목을 삭제하시겠습니까?
                  </p>
                  <div className="p-3 bg-red-50 rounded border border-red-200 mb-4">
                    <p className="font-medium">{selectedNotice.id}</p>
                    <p className="text-sm text-slate-600">{selectedNotice.content}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setShowCauseDeleteModal(false)} className="flex-1 px-4 py-2 border rounded hover:bg-slate-50">취소</button>
                    <button onClick={() => {
                      setCauseAnalysisList(causeAnalysisList.filter(item => item.id !== selectedNotice.id));
                      setSelectedNotice(null);
                      setShowCauseDeleteModal(false);
                    }} className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">삭제</button>
                  </div>
                </div>
              </div>
            )}

            {/* 처리 이력 추가 모달 */}
            {showHistoryModal && selectedNotice && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-[450px] shadow-xl">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <Clock className="text-blue-600"/>
                    처리 이력 추가
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">처리 내용</label>
                      <select value={historyFormData.action} onChange={e => setHistoryFormData({...historyFormData, action: e.target.value})} className="w-full px-3 py-2 border rounded text-sm mb-2">
                        <option value="">-- 선택 또는 직접 입력 --</option>
                        <option value="원인 분석 진행">원인 분석 진행</option>
                        <option value="설비 점검 완료">설비 점검 완료</option>
                        <option value="조치 완료">조치 완료</option>
                        <option value="재발 방지 대책 수립">재발 방지 대책 수립</option>
                        <option value="검토 완료">검토 완료</option>
                        <option value="최종 승인">최종 승인</option>
                      </select>
                      <input type="text" value={historyFormData.action} onChange={e => setHistoryFormData({...historyFormData, action: e.target.value})} className="w-full px-3 py-2 border rounded text-sm" placeholder="처리 내용 직접 입력"/>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">처리자</label>
                      <input type="text" value={historyFormData.by} onChange={e => setHistoryFormData({...historyFormData, by: e.target.value})} className="w-full px-3 py-2 border rounded text-sm" placeholder="처리자 이름"/>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-6">
                    <button onClick={() => setShowHistoryModal(false)} className="flex-1 px-4 py-2 border rounded hover:bg-slate-50">취소</button>
                    <button onClick={() => {
                      if (!historyFormData.action || !historyFormData.by) { alert('처리 내용과 처리자를 입력해주세요.'); return; }
                      const newHistory = { date: new Date().toLocaleString('ko-KR'), action: historyFormData.action, by: historyFormData.by };
                      const updated = causeAnalysisList.map(item => {
                        if (item.id === selectedNotice.id) {
                          return { ...item, history: [...item.history, newHistory] };
                        }
                        return item;
                      });
                      setCauseAnalysisList(updated);
                      setSelectedNotice({ ...selectedNotice, history: [...selectedNotice.history, newHistory] });
                      setShowHistoryModal(false);
                    }} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">추가</button>
                  </div>
                </div>
              </div>
            )}

            {/* 처리 이력 삭제 모달 */}
            {showHistoryDeleteModal && selectedNotice && historyDeleteIdx !== null && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-80 shadow-xl">
                  <h3 className="text-lg font-bold mb-4 text-center">삭제하시겠습니까?</h3>
                  <div className="flex gap-2">
                    <button onClick={() => setShowHistoryDeleteModal(false)} className="flex-1 px-4 py-2 border rounded hover:bg-slate-50">취소</button>
                    <button onClick={() => {
                      const newHistory = selectedNotice.history.filter((_, i) => i !== historyDeleteIdx);
                      const updated = causeAnalysisList.map(item => item.id === selectedNotice.id ? { ...item, history: newHistory } : item);
                      setCauseAnalysisList(updated);
                      setSelectedNotice({ ...selectedNotice, history: newHistory });
                      setShowHistoryDeleteModal(false);
                      setHistoryDeleteIdx(null);
                    }} className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">삭제</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 검토의견 탭 */}
        {activeTab === 'review' && (
          <div className="p-4">
            {/* 새 의견 등록 */}
            <div className="mb-6 p-4 bg-slate-50 rounded-lg border">
              <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                <FileText size={14} />
                새 검토의견 등록
              </h4>
              <div className="grid grid-cols-4 gap-3 mb-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">LOT</label>
                  <select value={newReviewLot} onChange={e => setNewReviewLot(e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm">
                    <option>26Y3A61C3I</option>
                    <option>26Y3A5ZP31</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">분석 항목</label>
                  <select value={newReviewItem} onChange={e => setNewReviewItem(e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm">
                    <option>H2O</option>
                    <option>Total OFC</option>
                    <option>O2+Ar</option>
                    <option>N2</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">제목</label>
                  <input type="text" value={newReviewTitle} onChange={e => setNewReviewTitle(e.target.value)} placeholder="검토의견 제목" className="w-full px-2 py-1.5 border rounded text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">상태</label>
                  <select value={newReviewStatus} onChange={e => setNewReviewStatus(e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm">
                    <option>진행중</option>
                    <option>완료</option>
                    <option>보류</option>
                  </select>
                </div>
              </div>
              <textarea 
                className="w-full p-3 border rounded text-sm mb-3" 
                rows={3} 
                placeholder="검토의견 내용을 입력하세요..."
                value={newReview}
                onChange={(e) => setNewReview(e.target.value)}
              />
              <div className="flex justify-end gap-2">
                <button onClick={resetNewReviewForm} className="px-4 py-2 border rounded text-sm hover:bg-slate-100">취소</button>
                <button 
                  onClick={addReview}
                  className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                >
                  등록
                </button>
              </div>
            </div>

            {/* 검토의견 목록 */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-sm">검토의견 이력 ({filteredReviews.length}건)</h4>
                <div className="flex gap-2">
                  <select value={reviewFilter} onChange={e => setReviewFilter(e.target.value)} className="px-2 py-1 text-xs border rounded">
                    <option>전체 상태</option>
                    <option>진행중</option>
                    <option>완료</option>
                    <option>보류</option>
                  </select>
                  <select value={reviewItemFilter} onChange={e => setReviewItemFilter(e.target.value)} className="px-2 py-1 text-xs border rounded">
                    <option>전체 항목</option>
                    <option>H2O</option>
                    <option>Total OFC</option>
                    <option>O2+Ar</option>
                  </select>
                </div>
              </div>

              <div className="space-y-3">
                {filteredReviews.map(review => (
                  <div key={review.id} className="border rounded-lg overflow-hidden">
                    <div className="px-4 py-2 bg-slate-50 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className={'px-2 py-0.5 rounded text-xs font-medium ' + 
                          (review.status === '완료' ? 'bg-green-100 text-green-700' : 
                           review.status === '진행중' ? 'bg-yellow-100 text-yellow-700' : 'bg-slate-100 text-slate-600')}>
                          {review.status}
                        </span>
                        <span className="font-medium text-sm">{review.title}</span>
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">{review.item}</span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span>LOT: {review.lot}</span>
                        <span>{review.author}</span>
                        <span>{review.date}</span>
                      </div>
                    </div>
                    <div className="px-4 py-3">
                      {editingReview === review.id ? (
                        <div>
                          <textarea 
                            value={editContent} 
                            onChange={e => setEditContent(e.target.value)} 
                            className="w-full p-2 border rounded text-sm mb-2" 
                            rows={3}
                          />
                          <div className="flex gap-2">
                            <button onClick={() => saveEditReview(review.id)} className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">저장</button>
                            <button onClick={cancelEditReview} className="px-3 py-1 text-xs border rounded hover:bg-slate-50">취소</button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-slate-700 whitespace-pre-line">{review.content}</p>
                      )}
                    </div>
                    {editingReview !== review.id && (
                      <div className="px-4 py-2 bg-slate-50 flex justify-end gap-2 border-t">
                        <button onClick={() => startEditReview(review)} className="px-3 py-1 text-xs border rounded hover:bg-white">수정</button>
                        <button onClick={() => deleteReview(review.id)} className="px-3 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50">삭제</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// 품질 집계 컴포넌트
const Summary = () => {
  const [activeTab, setActiveTab] = useState('period');
  const [period, setPeriod] = useState('daily');
  const [exporting, setExporting] = useState(false);
  const summaryRef = useRef(null);
  
  // 일별 집계 데이터
  const dailyData = [
    { date: '2026-01-17', lots: 5, cyls: 42, pass: 40, fail: 2, passRate: 95.2, ooc: 1, oos: 1 },
    { date: '2026-01-16', lots: 6, cyls: 51, pass: 51, fail: 0, passRate: 100, ooc: 0, oos: 0 },
    { date: '2026-01-15', lots: 4, cyls: 35, pass: 34, fail: 1, passRate: 97.1, ooc: 1, oos: 0 },
    { date: '2026-01-14', lots: 7, cyls: 58, pass: 57, fail: 1, passRate: 98.3, ooc: 0, oos: 1 },
    { date: '2026-01-13', lots: 5, cyls: 44, pass: 44, fail: 0, passRate: 100, ooc: 0, oos: 0 },
  ];

  // 주별 집계 데이터
  const weeklyData = [
    { week: '2026-W03', lots: 27, cyls: 230, pass: 226, fail: 4, passRate: 98.3, ooc: 2, oos: 2 },
    { week: '2026-W02', lots: 32, cyls: 275, pass: 272, fail: 3, passRate: 98.9, ooc: 2, oos: 1 },
    { week: '2026-W01', lots: 29, cyls: 248, pass: 245, fail: 3, passRate: 98.8, ooc: 1, oos: 2 },
  ];

  // 월별 집계 데이터
  const monthlyData = [
    { month: '2026-01', lots: 88, cyls: 753, pass: 743, fail: 10, passRate: 98.7, ooc: 5, oos: 5 },
    { month: '2025-12', lots: 102, cyls: 876, pass: 869, fail: 7, passRate: 99.2, ooc: 4, oos: 3 },
    { month: '2025-11', lots: 95, cyls: 812, pass: 801, fail: 11, passRate: 98.6, ooc: 6, oos: 5 },
  ];

  // 품목별 집계
  const productData = [
    { product: 'C4F8', lots: 15, pass: 98.5, avgCpk: 4.82 },
    { product: 'C3F6', lots: 12, pass: 99.1, avgCpk: 5.21 },
    { product: 'CHF3', lots: 18, pass: 97.8, avgCpk: 3.95 },
    { product: 'CH2F2', lots: 14, pass: 100, avgCpk: 6.12 },
    { product: 'CF4', lots: 16, pass: 98.2, avgCpk: 4.15 },
    { product: 'PF3', lots: 8, pass: 99.5, avgCpk: 5.67 },
  ];

  // CoA별 이상 현황
  const coaIssues = [
    { coa: 'Air', total: 150, ooc: 2, oos: 1, rate: 98.0 },
    { coa: '수분', total: 150, ooc: 3, oos: 2, rate: 96.7 },
    { coa: '순도', total: 150, ooc: 1, oos: 1, rate: 98.7 },
    { coa: '산도', total: 150, ooc: 0, oos: 0, rate: 100 },
    { coa: 'Metal', total: 150, ooc: 1, oos: 1, rate: 98.7 },
    { coa: 'Sulfur', total: 150, ooc: 0, oos: 0, rate: 100 },
  ];

  const currentData = period === 'daily' ? dailyData : period === 'weekly' ? weeklyData : monthlyData;
  const dateKey = period === 'daily' ? 'date' : period === 'weekly' ? 'week' : 'month';

  // Excel 내보내기
  const exportToExcel = () => {
    setExporting(true);
    try {
      const wb = XLSX.utils.book_new();
      
      // 기간별 데이터 시트
      const periodHeaders = [period === 'daily' ? '일자' : period === 'weekly' ? '주차' : '월', 'LOT 수', '실린더 수', '합격', '불합격', '합격률(%)', 'OOC', 'OOS'];
      const periodRows = currentData.map(row => [row[dateKey], row.lots, row.cyls, row.pass, row.fail, row.passRate, row.ooc, row.oos]);
      const ws1 = XLSX.utils.aoa_to_sheet([periodHeaders, ...periodRows]);
      XLSX.utils.book_append_sheet(wb, ws1, '기간별집계');
      
      // 품목별 데이터 시트
      const productHeaders = ['품목', 'LOT 수', '합격률(%)', '평균 Cpk'];
      const productRows = productData.map(row => [row.product, row.lots, row.pass, row.avgCpk]);
      const ws2 = XLSX.utils.aoa_to_sheet([productHeaders, ...productRows]);
      XLSX.utils.book_append_sheet(wb, ws2, '품목별집계');
      
      // CoA별 데이터 시트
      const coaHeaders = ['분류', '검사 건수', 'OOC', 'OOS', '합격률(%)'];
      const coaRows = coaIssues.map(row => [row.coa, row.total, row.ooc, row.oos, row.rate]);
      const ws3 = XLSX.utils.aoa_to_sheet([coaHeaders, ...coaRows]);
      XLSX.utils.book_append_sheet(wb, ws3, 'CoA별현황');
      
      // 파일 다운로드
      const fileName = `품질집계_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error('Excel export error:', error);
      alert('Excel 내보내기 중 오류가 발생했습니다.');
    }
    setExporting(false);
  };
  
  // PDF 출력
  const exportToPdf = async () => {
    if (!summaryRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(summaryRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`품질집계_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('PDF export error:', error);
      alert('PDF 출력 중 오류가 발생했습니다.');
    }
    setExporting(false);
  };

  // 부적합 집계용 상태들
  const [rejectSubTab, setRejectSubTab] = useState('status');
  
  // Reject 현황 데이터
  const rejectStatusData = [
    { product: 'C4F8 (C318EH)', lots: 45, cyls: 380, rejects: 2, rejectRate: 0.53, cp: 1.85, cpk: 1.72, status: 'good' },
    { product: 'CHF3 (23EH)', lots: 38, cyls: 320, rejects: 1, rejectRate: 0.31, cp: 2.15, cpk: 1.98, status: 'good' },
    { product: 'CH2F2 (HFC-32EH)', lots: 32, cyls: 270, rejects: 0, rejectRate: 0, cp: 2.45, cpk: 2.32, status: 'excellent' },
    { product: 'C2H2F4 (HFC-134aEH)', lots: 28, cyls: 235, rejects: 1, rejectRate: 0.43, cp: 1.92, cpk: 1.78, status: 'good' },
    { product: 'C3F6 (C3F6EH)', lots: 25, cyls: 210, rejects: 0, rejectRate: 0, cp: 2.28, cpk: 2.15, status: 'excellent' },
    { product: 'CF4', lots: 22, cyls: 185, rejects: 1, rejectRate: 0.54, cp: 1.78, cpk: 1.65, status: 'good' },
    { product: 'COS (COSEH)', lots: 18, cyls: 150, rejects: 0, rejectRate: 0, cp: 2.55, cpk: 2.42, status: 'excellent' },
    { product: 'C4F6', lots: 15, cyls: 125, rejects: 1, rejectRate: 0.8, cp: 1.65, cpk: 1.52, status: 'good' },
  ];

  // 상품 부적합 데이터
  const goodsRejectData = [
    { date: '2026-01-17', product: 'C4F8 (C318EH)', lot: '26Y3A61C3I', cyl: '21ENKE25839', item: 'H2O', spec: '≤6.0', result: 6.85, type: 'OOS', handler: '장시현' },
    { date: '2026-01-15', product: 'CHF3 (23EH)', lot: '26Y3A5ZP31', cyl: '21ENKE25728', item: 'N2', spec: '≤10', result: 11.2, type: 'OOS', handler: '김연재' },
    { date: '2026-01-12', product: 'CF4', lot: '26Y3C10B3M', cyl: '21ENKE26010', item: 'Total OFC', spec: '≤200', result: 215, type: 'OOS', handler: '김승현' },
    { date: '2026-01-10', product: 'C4F6', lot: '26Y3G16I3U', cyl: '21ENKE25835', item: 'CO2', spec: '≤0.5', result: 0.62, type: 'OOS', handler: '노민석' },
  ];

  // 원료 부적합 데이터
  const sourceRejectData = [
    { date: '2026-01-16', supplier: 'Dongyue', material: 'C4F8 원료', lot: 'DY2601-A23', item: 'H2O', spec: '≤3.0', result: 3.8, type: 'OOS', action: '반품처리' },
    { date: '2026-01-13', supplier: 'DNCS', material: 'CHF3 원료', lot: 'DC2601-B15', item: '순도', spec: '≥99.99%', result: 99.95, type: 'OOC', action: '특채' },
    { date: '2026-01-08', supplier: 'DILY', material: 'CF4 원료', lot: 'DL2601-C08', item: 'N2', spec: '≤5.0', result: 5.5, type: 'OOS', action: '반품처리' },
  ];

  // 출하 부적합 데이터
  const outgoingRejectData = [
    { date: '2026-01-14', customer: 'Samsung', product: 'C4F8 (C318EH)', lot: '26Y3A5ZP31', defect: '라벨불량', qty: 2, action: '재작업' },
    { date: '2026-01-11', customer: 'SK Hynix', product: 'CHF3 (23EH)', lot: '26Y3C10B3M', defect: '포장불량', qty: 1, action: '재포장' },
  ];

  // 월별 Reject 추이 데이터
  const monthlyRejectTrend = [
    { month: '2025-10', goods: 3, source: 2, outgoing: 1, total: 6 },
    { month: '2025-11', goods: 4, source: 1, outgoing: 2, total: 7 },
    { month: '2025-12', goods: 2, source: 3, outgoing: 1, total: 6 },
    { month: '2026-01', goods: 4, source: 3, outgoing: 2, total: 9 },
  ];

  return (
    <div className="space-y-4" ref={summaryRef}>
      {/* 내부 탭 메뉴 */}
      <div className="flex border-b bg-white rounded-t-lg">
        <button
          onClick={() => setActiveTab('period')}
          className={'px-6 py-3 text-sm font-medium ' + (activeTab === 'period' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:text-slate-700')}
        >
          기간별 집계
        </button>
        <button
          onClick={() => setActiveTab('reject')}
          className={'px-6 py-3 text-sm font-medium ' + (activeTab === 'reject' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:text-slate-700')}
        >
          부적합 집계
        </button>
        <button
          onClick={() => setActiveTab('rejectDetail')}
          className={'px-6 py-3 text-sm font-medium ' + (activeTab === 'rejectDetail' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:text-slate-700')}
        >
          부적합집계 상세
        </button>
      </div>

      {/* 기간별 집계 탭 */}
      {activeTab === 'period' && (
        <>
          {/* 요약 카드 */}
          <div className="grid grid-cols-5 gap-4">
            <div className="bg-white rounded-lg p-4 border">
              <p className="text-sm text-slate-500">이번 달 LOT</p>
              <p className="text-2xl font-bold text-slate-800">88</p>
              <p className="text-xs text-green-600">+12% vs 전월</p>
            </div>
            <div className="bg-white rounded-lg p-4 border">
              <p className="text-sm text-slate-500">총 실린더</p>
              <p className="text-2xl font-bold text-slate-800">753</p>
              <p className="text-xs text-slate-400">이번 달</p>
            </div>
            <div className="bg-white rounded-lg p-4 border">
              <p className="text-sm text-slate-500">합격률</p>
              <p className="text-2xl font-bold text-green-600">98.7%</p>
              <p className="text-xs text-slate-400">743/753</p>
            </div>
            <div className="bg-white rounded-lg p-4 border">
              <p className="text-sm text-slate-500">OOC 건수</p>
              <p className="text-2xl font-bold text-yellow-600">5</p>
              <p className="text-xs text-slate-400">이번 달</p>
            </div>
            <div className="bg-white rounded-lg p-4 border">
              <p className="text-sm text-slate-500">OOS 건수</p>
              <p className="text-2xl font-bold text-red-600">5</p>
              <p className="text-xs text-slate-400">이번 달</p>
            </div>
          </div>

      {/* 기간별 집계 */}
      <div className="bg-white rounded-lg border">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <span className="font-medium">기간별 품질 집계</span>
          <div className="flex gap-2">
            {[['daily', '일별'], ['weekly', '주별'], ['monthly', '월별']].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setPeriod(key)}
                className={'px-3 py-1 rounded text-sm ' + (period === key ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600')}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {[period === 'daily' ? '일자' : period === 'weekly' ? '주차' : '월', 'LOT 수', '실린더 수', '합격', '불합격', '합격률', 'OOC', 'OOS'].map(h => (
                <th key={h} className="px-4 py-2 text-left text-xs font-medium text-slate-600">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentData.map((row, i) => (
              <tr key={i} className="border-t hover:bg-slate-50">
                <td className="px-4 py-2 font-medium">{row[dateKey]}</td>
                <td className="px-4 py-2">{row.lots}</td>
                <td className="px-4 py-2">{row.cyls}</td>
                <td className="px-4 py-2 text-green-600">{row.pass}</td>
                <td className="px-4 py-2 text-red-600">{row.fail}</td>
                <td className="px-4 py-2">
                  <span className={'px-2 py-0.5 rounded text-xs ' + (row.passRate >= 99 ? 'bg-green-100 text-green-700' : row.passRate >= 95 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700')}>
                    {row.passRate}%
                  </span>
                </td>
                <td className="px-4 py-2">{row.ooc > 0 ? <span className="text-yellow-600">{row.ooc}</span> : '-'}</td>
                <td className="px-4 py-2">{row.oos > 0 ? <span className="text-red-600">{row.oos}</span> : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 품목별 / CoA별 집계 */}
      <div className="grid grid-cols-2 gap-4">
        {/* 품목별 집계 */}
        <div className="bg-white rounded-lg border">
          <div className="px-4 py-3 border-b">
            <span className="font-medium">품목별 집계 (이번 달)</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                {['품목', 'LOT 수', '합격률', '평균 Cpk'].map(h => (
                  <th key={h} className="px-4 py-2 text-left text-xs font-medium text-slate-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {productData.map((row, i) => (
                <tr key={i} className="border-t hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium">{row.product}</td>
                  <td className="px-4 py-2">{row.lots}</td>
                  <td className="px-4 py-2">
                    <span className={'px-2 py-0.5 rounded text-xs ' + (row.pass >= 99 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700')}>
                      {row.pass}%
                    </span>
                  </td>
                  <td className="px-4 py-2 text-blue-600 font-medium">{row.avgCpk}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* CoA별 이상 현황 */}
        <div className="bg-white rounded-lg border">
          <div className="px-4 py-3 border-b">
            <span className="font-medium">CoA 분류별 현황 (이번 달)</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                {['분류', '검사 건수', 'OOC', 'OOS', '합격률'].map(h => (
                  <th key={h} className="px-4 py-2 text-left text-xs font-medium text-slate-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {coaIssues.map((row, i) => (
                <tr key={i} className="border-t hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium">{row.coa}</td>
                  <td className="px-4 py-2">{row.total}</td>
                  <td className="px-4 py-2">{row.ooc > 0 ? <span className="text-yellow-600">{row.ooc}</span> : '-'}</td>
                  <td className="px-4 py-2">{row.oos > 0 ? <span className="text-red-600">{row.oos}</span> : '-'}</td>
                  <td className="px-4 py-2">
                    <span className={'px-2 py-0.5 rounded text-xs ' + (row.rate >= 99 ? 'bg-green-100 text-green-700' : row.rate >= 95 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700')}>
                      {row.rate}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

          {/* 내보내기 */}
          <div className="flex justify-end gap-2">
            <button 
              onClick={exportToPdf}
              disabled={exporting}
              className="px-4 py-2 border rounded text-sm hover:bg-slate-50 flex items-center gap-2 disabled:opacity-50"
            >
              <FileText size={14} />
              {exporting ? '처리중...' : 'PDF 출력'}
            </button>
            <button 
              onClick={exportToExcel}
              disabled={exporting}
              className="px-4 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
            >
              <Download size={14} />
              {exporting ? '처리중...' : 'Excel 내보내기'}
            </button>
          </div>
        </>
      )}

      {/* 부적합 집계 탭 */}
      {activeTab === 'reject' && (
        <>
          {/* 서브탭 */}
          <div className="flex gap-2 mb-4">
            {[
              ['status', 'Reject 현황'],
              ['goods', '상품 부적합'],
              ['source', '원료 부적합'],
              ['outgoing', '출하 부적합']
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setRejectSubTab(key)}
                className={'px-4 py-2 rounded-lg text-sm font-medium ' + (rejectSubTab === key ? 'bg-blue-600 text-white' : 'bg-white border text-slate-600 hover:bg-slate-50')}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Reject 현황 탭 */}
          {rejectSubTab === 'status' && (
            <div className="space-y-4">
              {/* 요약 카드 */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-4 border">
                  <p className="text-sm text-slate-500">총 부적합</p>
                  <p className="text-2xl font-bold text-red-600">6</p>
                  <p className="text-xs text-slate-400">이번 달</p>
                </div>
                <div className="bg-white rounded-lg p-4 border">
                  <p className="text-sm text-slate-500">상품 부적합</p>
                  <p className="text-2xl font-bold text-orange-600">4</p>
                  <p className="text-xs text-slate-400">이번 달</p>
                </div>
                <div className="bg-white rounded-lg p-4 border">
                  <p className="text-sm text-slate-500">원료 부적합</p>
                  <p className="text-2xl font-bold text-yellow-600">3</p>
                  <p className="text-xs text-slate-400">이번 달</p>
                </div>
                <div className="bg-white rounded-lg p-4 border">
                  <p className="text-sm text-slate-500">평균 Cpk</p>
                  <p className="text-2xl font-bold text-blue-600">1.94</p>
                  <p className="text-xs text-green-600">양호</p>
                </div>
              </div>

              {/* Cp/Cpk 테이블 */}
              <div className="bg-white rounded-lg border">
                <div className="px-4 py-3 border-b">
                  <span className="font-medium">제품별 Reject 현황 및 공정능력</span>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      {['제품', 'LOT 수', '실린더 수', 'Reject', 'Reject율(%)', 'Cp', 'Cpk', '상태'].map(h => (
                        <th key={h} className="px-4 py-2 text-left text-xs font-medium text-slate-600">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rejectStatusData.map((row, i) => (
                      <tr key={i} className="border-t hover:bg-slate-50">
                        <td className="px-4 py-2 font-medium">{row.product}</td>
                        <td className="px-4 py-2">{row.lots}</td>
                        <td className="px-4 py-2">{row.cyls}</td>
                        <td className="px-4 py-2">{row.rejects > 0 ? <span className="text-red-600">{row.rejects}</span> : '-'}</td>
                        <td className="px-4 py-2">{row.rejectRate > 0 ? <span className="text-red-600">{row.rejectRate}%</span> : '-'}</td>
                        <td className="px-4 py-2 text-blue-600">{row.cp}</td>
                        <td className="px-4 py-2 font-medium text-blue-700">{row.cpk}</td>
                        <td className="px-4 py-2">
                          <span className={'px-2 py-0.5 rounded text-xs ' + (row.status === 'excellent' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700')}>
                            {row.status === 'excellent' ? '우수' : '양호'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 월별 추이 차트 */}
              <div className="bg-white rounded-lg border p-4">
                <div className="font-medium mb-4">월별 부적합 추이</div>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={monthlyRejectTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="goods" name="상품" fill="#F97316" />
                    <Bar dataKey="source" name="원료" fill="#EAB308" />
                    <Bar dataKey="outgoing" name="출하" fill="#6366F1" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* 상품 부적합 탭 */}
          {rejectSubTab === 'goods' && (
            <div className="bg-white rounded-lg border">
              <div className="px-4 py-3 border-b">
                <span className="font-medium">상품 부적합 내역</span>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {['일자', '제품', 'LOT', 'CYL', '항목', '규격', '결과', '유형', '담당자'].map(h => (
                      <th key={h} className="px-4 py-2 text-left text-xs font-medium text-slate-600">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {goodsRejectData.map((row, i) => (
                    <tr key={i} className="border-t hover:bg-slate-50">
                      <td className="px-4 py-2">{row.date}</td>
                      <td className="px-4 py-2 font-medium">{row.product}</td>
                      <td className="px-4 py-2 font-mono text-xs">{row.lot}</td>
                      <td className="px-4 py-2 font-mono text-xs">{row.cyl}</td>
                      <td className="px-4 py-2">{row.item}</td>
                      <td className="px-4 py-2 text-slate-500">{row.spec}</td>
                      <td className="px-4 py-2 text-red-600 font-medium">{row.result}</td>
                      <td className="px-4 py-2">
                        <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">{row.type}</span>
                      </td>
                      <td className="px-4 py-2">{row.handler}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* 원료 부적합 탭 */}
          {rejectSubTab === 'source' && (
            <div className="bg-white rounded-lg border">
              <div className="px-4 py-3 border-b">
                <span className="font-medium">원료 부적합 내역</span>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {['일자', '공급사', '원료', 'LOT', '항목', '규격', '결과', '유형', '조치'].map(h => (
                      <th key={h} className="px-4 py-2 text-left text-xs font-medium text-slate-600">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sourceRejectData.map((row, i) => (
                    <tr key={i} className="border-t hover:bg-slate-50">
                      <td className="px-4 py-2">{row.date}</td>
                      <td className="px-4 py-2 font-medium">{row.supplier}</td>
                      <td className="px-4 py-2">{row.material}</td>
                      <td className="px-4 py-2 font-mono text-xs">{row.lot}</td>
                      <td className="px-4 py-2">{row.item}</td>
                      <td className="px-4 py-2 text-slate-500">{row.spec}</td>
                      <td className="px-4 py-2 text-red-600 font-medium">{row.result}</td>
                      <td className="px-4 py-2">
                        <span className={'px-2 py-0.5 rounded text-xs ' + (row.type === 'OOS' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700')}>{row.type}</span>
                      </td>
                      <td className="px-4 py-2">
                        <span className={'px-2 py-0.5 rounded text-xs ' + (row.action === '반품처리' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700')}>{row.action}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* 출하 부적합 탭 */}
          {rejectSubTab === 'outgoing' && (
            <div className="bg-white rounded-lg border">
              <div className="px-4 py-3 border-b">
                <span className="font-medium">출하 부적합 내역</span>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {['일자', '고객사', '제품', 'LOT', '불량유형', '수량', '조치'].map(h => (
                      <th key={h} className="px-4 py-2 text-left text-xs font-medium text-slate-600">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {outgoingRejectData.map((row, i) => (
                    <tr key={i} className="border-t hover:bg-slate-50">
                      <td className="px-4 py-2">{row.date}</td>
                      <td className="px-4 py-2 font-medium">{row.customer}</td>
                      <td className="px-4 py-2">{row.product}</td>
                      <td className="px-4 py-2 font-mono text-xs">{row.lot}</td>
                      <td className="px-4 py-2">{row.defect}</td>
                      <td className="px-4 py-2">{row.qty}</td>
                      <td className="px-4 py-2">
                        <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">{row.action}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* 부적합집계 상세 탭 */}
      {activeTab === 'rejectDetail' && (
        <RejectManagement />
      )}
    </div>
  );
};

// 품질경향분석 컴포넌트
const TrendAnalysis = () => {
  const [selectedProduct, setSelectedProduct] = useState('C4F8');
  const [selectedItem, setSelectedItem] = useState('H2O');
  const [period, setPeriod] = useState('weekly');
  const [comparisonMode, setComparisonMode] = useState(false);
  const [compareItems, setCompareItems] = useState(['H2O', 'N2']);

  // 경향 분석 데이터 (주별/월별 추이)
  const trendData = {
    weekly: [
      { period: 'W01', H2O: 1.15, N2: 2.3, CO2: 0.22, 'Total OFC': 145, avg_H2O: 1.24, ucl_H2O: 6 },
      { period: 'W02', H2O: 1.22, N2: 2.5, CO2: 0.25, 'Total OFC': 152, avg_H2O: 1.24, ucl_H2O: 6 },
      { period: 'W03', H2O: 1.35, N2: 2.4, CO2: 0.21, 'Total OFC': 148, avg_H2O: 1.24, ucl_H2O: 6 },
      { period: 'W04', H2O: 1.28, N2: 2.6, CO2: 0.28, 'Total OFC': 155, avg_H2O: 1.24, ucl_H2O: 6 },
      { period: 'W05', H2O: 1.42, N2: 2.8, CO2: 0.24, 'Total OFC': 162, avg_H2O: 1.24, ucl_H2O: 6 },
      { period: 'W06', H2O: 1.38, N2: 2.5, CO2: 0.26, 'Total OFC': 158, avg_H2O: 1.24, ucl_H2O: 6 },
      { period: 'W07', H2O: 1.55, N2: 2.9, CO2: 0.29, 'Total OFC': 168, avg_H2O: 1.24, ucl_H2O: 6 },
      { period: 'W08', H2O: 1.48, N2: 2.7, CO2: 0.27, 'Total OFC': 165, avg_H2O: 1.24, ucl_H2O: 6 },
    ],
    monthly: [
      { period: '2025-09', H2O: 1.12, N2: 2.2, CO2: 0.20, 'Total OFC': 140, avg_H2O: 1.24, ucl_H2O: 6 },
      { period: '2025-10', H2O: 1.18, N2: 2.3, CO2: 0.22, 'Total OFC': 145, avg_H2O: 1.24, ucl_H2O: 6 },
      { period: '2025-11', H2O: 1.25, N2: 2.4, CO2: 0.24, 'Total OFC': 150, avg_H2O: 1.24, ucl_H2O: 6 },
      { period: '2025-12', H2O: 1.32, N2: 2.5, CO2: 0.25, 'Total OFC': 155, avg_H2O: 1.24, ucl_H2O: 6 },
      { period: '2026-01', H2O: 1.45, N2: 2.7, CO2: 0.28, 'Total OFC': 162, avg_H2O: 1.24, ucl_H2O: 6 },
    ],
  };

  const currentData = trendData[period];

  // 경향 분석 통계
  const calcTrendStats = (data, item) => {
    const values = data.map(d => d[item]);
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const first = values[0];
    const last = values[values.length - 1];
    const change = ((last - first) / first * 100).toFixed(1);
    const trend = last > first ? 'up' : last < first ? 'down' : 'stable';
    return { avg: avg.toFixed(2), min: min.toFixed(2), max: max.toFixed(2), change, trend };
  };

  const stats = calcTrendStats(currentData, selectedItem);

  // 패턴 감지
  const detectPattern = (data, item) => {
    const values = data.map(d => d[item]);
    let increasing = 0, decreasing = 0;
    for (let i = 1; i < values.length; i++) {
      if (values[i] > values[i-1]) increasing++;
      else if (values[i] < values[i-1]) decreasing++;
    }
    if (increasing >= values.length - 2) return { type: 'increasing', desc: '지속적 상승 추세', severity: 'warning' };
    if (decreasing >= values.length - 2) return { type: 'decreasing', desc: '지속적 하락 추세', severity: 'info' };
    if (Math.abs(increasing - decreasing) <= 1) return { type: 'zigzag', desc: '변동 패턴 (지그재그)', severity: 'info' };
    return { type: 'stable', desc: '안정적', severity: 'ok' };
  };

  const pattern = detectPattern(currentData, selectedItem);

  return (
    <div className="space-y-4">
      {/* 검색 조건 */}
      <div className="bg-white rounded-lg p-4 border">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp size={14} />
          <span className="font-medium text-sm">경향 분석 조건</span>
        </div>
        <div className="grid grid-cols-5 gap-3">
          <div>
            <label className="text-xs text-slate-500">품명</label>
            <select 
              className="w-full px-2 py-1 border rounded text-sm"
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
            >
              {PRODUCTS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">분석 항목</label>
            <select 
              className="w-full px-2 py-1 border rounded text-sm"
              value={selectedItem}
              onChange={(e) => setSelectedItem(e.target.value)}
            >
              <option value="H2O">H2O</option>
              <option value="N2">N2</option>
              <option value="CO2">CO2</option>
              <option value="Total OFC">Total OFC</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">기간 단위</label>
            <select 
              className="w-full px-2 py-1 border rounded text-sm"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              <option value="weekly">주별</option>
              <option value="monthly">월별</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">비교 모드</label>
            <div className="flex items-center gap-2 mt-1">
              <input 
                type="checkbox" 
                checked={comparisonMode}
                onChange={(e) => setComparisonMode(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">항목 비교</span>
            </div>
          </div>
          <div className="flex items-end">
            <button className="px-4 py-1 bg-blue-600 text-white rounded text-sm flex items-center gap-1">
              <Search size={12} />분석
            </button>
          </div>
        </div>
      </div>

      {/* 경향 요약 카드 */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-white rounded-lg p-4 border">
          <p className="text-xs text-slate-500">평균값</p>
          <p className="text-xl font-bold" style={{color: THEME.primaryDark}}>{stats.avg}</p>
          <p className="text-xs text-slate-400">{selectedItem}</p>
        </div>
        <div className="bg-white rounded-lg p-4 border">
          <p className="text-xs text-slate-500">최소값</p>
          <p className="text-xl font-bold text-green-600">{stats.min}</p>
        </div>
        <div className="bg-white rounded-lg p-4 border">
          <p className="text-xs text-slate-500">최대값</p>
          <p className="text-xl font-bold text-red-600">{stats.max}</p>
        </div>
        <div className="bg-white rounded-lg p-4 border">
          <p className="text-xs text-slate-500">변화율</p>
          <p className={'text-xl font-bold ' + (parseFloat(stats.change) > 0 ? 'text-red-600' : 'text-green-600')}>
            {parseFloat(stats.change) > 0 ? '+' : ''}{stats.change}%
          </p>
          <p className="text-xs text-slate-400">기간 대비</p>
        </div>
        <div className="bg-white rounded-lg p-4 border">
          <p className="text-xs text-slate-500">패턴 감지</p>
          <p className={'text-sm font-bold ' + 
            (pattern.severity === 'warning' ? 'text-yellow-600' : 
             pattern.severity === 'ok' ? 'text-green-600' : 'text-blue-600')}>
            {pattern.desc}
          </p>
          {pattern.type === 'increasing' && <TrendingUp size={16} className="text-yellow-500 mt-1" />}
        </div>
      </div>

      {/* 경향 차트 */}
      <div className="bg-white rounded-lg border">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp size={16} className="text-blue-600" />
            <span className="font-medium">{selectedItem} 경향 분석</span>
            <span className="text-xs text-slate-500">({period === 'weekly' ? '주별' : '월별'})</span>
          </div>
          {pattern.severity === 'warning' && (
            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs flex items-center gap-1">
              <AlertTriangle size={12} />
              {pattern.desc}
            </span>
          )}
        </div>
        <div className="p-4">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={currentData} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="period" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              
              {/* 기준선 */}
              <ReferenceLine y={currentData[0]?.['avg_' + selectedItem]} stroke="#EAB308" strokeDasharray="5 5" label={{ value: 'AVG', position: 'right', fontSize: 10 }} />
              
              {/* 메인 데이터 */}
              <Line 
                type="monotone" 
                dataKey={selectedItem} 
                stroke="#1E40AF" 
                strokeWidth={3}
                dot={{ r: 5, fill: '#1E40AF' }}
                activeDot={{ r: 8 }}
              />
              
              {/* 비교 모드일 때 추가 라인 */}
              {comparisonMode && compareItems.filter(i => i !== selectedItem).map((item, idx) => (
                <Line 
                  key={item}
                  type="monotone" 
                  dataKey={item} 
                  stroke={idx === 0 ? '#10B981' : '#F59E0B'}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  strokeDasharray={idx === 0 ? '' : '5 5'}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        
        {/* 범례 */}
        <div className="px-4 pb-3 flex justify-center gap-6 text-xs border-t pt-2">
          <span className="flex items-center gap-1">
            <span className="w-4 h-0.5 bg-blue-800"></span>{selectedItem}
          </span>
          <span className="flex items-center gap-1">
            <span className="w-4 h-0.5 bg-yellow-500" style={{borderStyle: 'dashed'}}></span>AVG
          </span>
          {comparisonMode && compareItems.filter(i => i !== selectedItem).map((item, idx) => (
            <span key={item} className="flex items-center gap-1">
              <span className={'w-4 h-0.5 ' + (idx === 0 ? 'bg-green-500' : 'bg-yellow-500')}></span>{item}
            </span>
          ))}
        </div>
      </div>

      {/* 기간별 상세 데이터 테이블 */}
      <div className="bg-white rounded-lg border">
        <div className="px-4 py-3 border-b">
          <span className="font-medium">기간별 상세 데이터</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-slate-600">{period === 'weekly' ? '주차' : '월'}</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-slate-600">H2O</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-slate-600">N2</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-slate-600">CO2</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-slate-600">Total OFC</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-slate-600">전기 대비</th>
            </tr>
          </thead>
          <tbody>
            {currentData.map((row, i) => {
              const prevValue = i > 0 ? currentData[i-1][selectedItem] : row[selectedItem];
              const change = ((row[selectedItem] - prevValue) / prevValue * 100).toFixed(1);
              return (
                <tr key={row.period} className="border-t hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium">{row.period}</td>
                  <td className={'px-4 py-2 ' + (selectedItem === 'H2O' ? 'font-bold text-blue-600' : '')}>{row.H2O}</td>
                  <td className={'px-4 py-2 ' + (selectedItem === 'N2' ? 'font-bold text-blue-600' : '')}>{row.N2}</td>
                  <td className={'px-4 py-2 ' + (selectedItem === 'CO2' ? 'font-bold text-blue-600' : '')}>{row.CO2}</td>
                  <td className={'px-4 py-2 ' + (selectedItem === 'Total OFC' ? 'font-bold text-blue-600' : '')}>{row['Total OFC']}</td>
                  <td className="px-4 py-2">
                    {i > 0 && (
                      <span className={parseFloat(change) > 0 ? 'text-red-500' : parseFloat(change) < 0 ? 'text-green-500' : 'text-slate-400'}>
                        {parseFloat(change) > 0 ? '↑' : parseFloat(change) < 0 ? '↓' : '−'} {Math.abs(parseFloat(change))}%
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 분석 의견 */}
      {pattern.severity === 'warning' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-yellow-600 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800">경향 이상 감지</p>
              <p className="text-sm text-yellow-700 mt-1">
                {selectedItem} 항목이 {pattern.desc}를 보이고 있습니다. 
                최근 {currentData.length}개 기간 동안 {stats.change}% 변화하였으며, 
                현재 추세가 지속될 경우 관리 한계에 도달할 수 있습니다.
                원인 분석 및 조치가 필요합니다.
              </p>
              <button className="mt-2 px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700">
                개선대책 등록
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 개선대책 컴포넌트
const Improvement = () => {
  const [activeTab, setActiveTab] = useState('list');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    type: 'OOC',
    item: 'H2O',
    product: 'C4F8',
    lot: '',
    cyl: '',
    value: '',
    limit: '',
    priority: 'medium',
    title: '',
    cause: '',
    action: '',
    owner: '',
    dueDate: ''
  });

  // 신규 등록
  const handleAddNew = () => {
    const newId = `IMP-2026-${String(improvements.length + 1).padStart(3, '0')}`;
    const newItem = {
      id: newId,
      date: new Date().toISOString().split('T')[0],
      type: formData.type,
      item: formData.item,
      product: formData.product,
      lot: formData.lot || '-',
      cyl: formData.cyl || '-',
      value: formData.value || '-',
      limit: formData.limit || '-',
      status: 'pending',
      priority: formData.priority,
      title: formData.title,
      cause: formData.cause,
      action: formData.action,
      owner: formData.owner || '관리자',
      dueDate: formData.dueDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      effect: '',
      history: [
        { date: new Date().toLocaleString('ko-KR'), action: '등록', by: formData.owner || '관리자' }
      ]
    };
    setImprovements([newItem, ...improvements]);
    setShowModal(false);
    resetForm();
  };
  
  const resetForm = () => {
    setFormData({
      type: 'OOC', item: 'H2O', product: 'C4F8', lot: '', cyl: '', value: '', limit: '', priority: 'medium', title: '', cause: '', action: '', owner: '', dueDate: ''
    });
  };
  
  // 수정 모드
  const startEdit = () => {
    setFormData({
      type: selectedItem.type,
      item: selectedItem.item,
      product: selectedItem.product,
      lot: selectedItem.lot,
      cyl: selectedItem.cyl,
      value: selectedItem.value,
      limit: selectedItem.limit,
      priority: selectedItem.priority,
      title: selectedItem.title,
      cause: selectedItem.cause,
      action: selectedItem.action,
      owner: selectedItem.owner,
      dueDate: selectedItem.dueDate
    });
    setEditMode(true);
  };
  
  const saveEdit = () => {
    const updated = improvements.map(item => {
      if (item.id === selectedItem.id) {
        return {
          ...item,
          ...formData,
          history: [...item.history, { date: new Date().toLocaleString('ko-KR'), action: '수정', by: formData.owner }]
        };
      }
      return item;
    });
    setImprovements(updated);
    setEditMode(false);
    setSelectedItem({ ...selectedItem, ...formData });
  };
  
  // 완료 처리
  const markComplete = () => {
    const updated = improvements.map(item => {
      if (item.id === selectedItem.id) {
        return {
          ...item,
          status: 'completed',
          effect: '조치 완료 확인',
          history: [...item.history, { date: new Date().toLocaleString('ko-KR'), action: '완료 처리', by: item.owner }]
        };
      }
      return item;
    });
    setImprovements(updated);
    setSelectedItem({ ...selectedItem, status: 'completed', effect: '조치 완료 확인' });
  };

  // 개선대책 데이터
  const [improvements, setImprovements] = useState([
    {
      id: 'IMP-2026-001',
      date: '2026-01-15',
      type: 'OOS',
      item: 'H2O',
      product: 'C4F8',
      lot: '26Y3A61C3I',
      cyl: '21ENKE25728',
      value: 8.5,
      limit: 8.0,
      status: 'inProgress',
      priority: 'high',
      title: 'H2O 규격 초과 발생',
      cause: '원료 입고 LOT 변경으로 인한 수분 함량 증가',
      action: '1. 원료 입고 검사 강화\n2. 건조 공정 시간 연장 (2시간 → 3시간)\n3. 충전 전 퍼지 횟수 증가',
      owner: '이석진',
      dueDate: '2026-01-22',
      effect: '',
      history: [
        { date: '2026-01-15 14:32', action: '이상 발생', by: '시스템' },
        { date: '2026-01-15 15:00', action: '원인 분석 시작', by: '이석진' },
        { date: '2026-01-16 09:00', action: '대책 수립 완료', by: '이석진' },
      ]
    },
    {
      id: 'IMP-2026-002',
      date: '2026-01-14',
      type: 'OOC',
      item: 'Total OFC',
      product: 'CHF3',
      lot: '26Y3C10B3M',
      cyl: '21ENKE26010',
      value: 720,
      limit: 675,
      status: 'completed',
      priority: 'medium',
      title: 'OFC UCL 초과',
      cause: '정류탑 온도 제어 불안정',
      action: '1. 정류탑 온도 센서 교체\n2. PID 파라미터 재조정',
      owner: '노민석',
      dueDate: '2026-01-18',
      effect: '조치 후 OFC 수치 정상 범위 유지 확인 (3일간 모니터링 완료)',
      history: [
        { date: '2026-01-14 10:15', action: '이상 발생', by: '시스템' },
        { date: '2026-01-14 11:00', action: '현장 점검', by: '노민석' },
        { date: '2026-01-15 14:00', action: '센서 교체 완료', by: '노민석' },
        { date: '2026-01-18 09:00', action: '효과 확인 완료', by: '노민석' },
      ]
    },
    {
      id: 'IMP-2026-003',
      date: '2026-01-12',
      type: 'OOT',
      item: 'N2',
      product: 'CF4',
      lot: '26Y3G16I3U',
      cyl: '-',
      value: '-',
      limit: '-',
      status: 'pending',
      priority: 'low',
      title: 'N2 중심선 편향 경향',
      cause: '분석 중',
      action: '',
      owner: '변상필',
      dueDate: '2026-01-25',
      effect: '',
      history: [
        { date: '2026-01-12 16:00', action: 'Rule2 위반 감지', by: '시스템' },
        { date: '2026-01-13 09:00', action: '담당자 배정', by: '관리자' },
      ]
    },
    {
      id: 'IMP-2026-004',
      date: '2026-01-10',
      type: 'SEC',
      item: 'H2O',
      product: 'C4F8',
      lot: '26Y3A5ZP31',
      cyl: '21ENKE25835',
      value: 5.2,
      limit: 5.4,
      status: 'completed',
      priority: 'medium',
      title: '규격 90% 근접 경고 (SEC-R3)',
      cause: '계절 변화로 인한 환경 습도 증가',
      action: '1. 클린룸 습도 관리 기준 강화 (40% → 35%)\n2. 제습기 추가 가동',
      owner: '박병욱',
      dueDate: '2026-01-15',
      effect: '습도 관리 후 H2O 수치 안정화',
      history: [
        { date: '2026-01-10 11:00', action: 'SEC-R3 경고', by: '시스템' },
        { date: '2026-01-11 10:00', action: '환경 데이터 분석', by: '박병욱' },
        { date: '2026-01-12 14:00', action: '제습기 가동', by: '박병욱' },
        { date: '2026-01-15 09:00', action: '완료 처리', by: '박병욱' },
      ]
    },
  ]);

  // 필터링된 데이터
  const filteredData = improvements.filter(item => {
    if (statusFilter !== 'all' && item.status !== statusFilter) return false;
    if (typeFilter !== 'all' && item.type !== typeFilter) return false;
    return true;
  });

  // 통계
  const stats = {
    total: improvements.length,
    inProgress: improvements.filter(i => i.status === 'inProgress').length,
    pending: improvements.filter(i => i.status === 'pending').length,
    completed: improvements.filter(i => i.status === 'completed').length,
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'completed': return <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">완료</span>;
      case 'inProgress': return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">진행중</span>;
      case 'pending': return <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">대기</span>;
      default: return <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{status}</span>;
    }
  };

  const getTypeBadge = (type) => {
    switch(type) {
      case 'OOS': return <span className="px-2 py-0.5 bg-red-500 text-white rounded text-xs">OOS</span>;
      case 'OOC': return <span className="px-2 py-0.5 bg-yellow-500 text-white rounded text-xs">OOC</span>;
      case 'OOT': return <span className="px-2 py-0.5 bg-blue-500 text-white rounded text-xs">OOT</span>;
      case 'SEC': return <span className="px-2 py-0.5 bg-cyan-500 text-white rounded text-xs">SEC</span>;
      default: return <span className="px-2 py-0.5 bg-slate-500 text-white rounded text-xs">{type}</span>;
    }
  };

  const getPriorityBadge = (priority) => {
    switch(priority) {
      case 'high': return <span className="px-1.5 py-0.5 bg-red-100 text-red-600 rounded text-xs">긴급</span>;
      case 'medium': return <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-600 rounded text-xs">보통</span>;
      case 'low': return <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">낮음</span>;
      default: return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* 통계 카드 */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 border">
          <p className="text-sm text-slate-500">전체 건수</p>
          <p className="text-2xl font-bold" style={{color: THEME.primaryDark}}>{stats.total}</p>
        </div>
        <div className="bg-white rounded-lg p-4 border">
          <p className="text-sm text-slate-500">진행중</p>
          <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
        </div>
        <div className="bg-white rounded-lg p-4 border">
          <p className="text-sm text-slate-500">대기</p>
          <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
        </div>
        <div className="bg-white rounded-lg p-4 border">
          <p className="text-sm text-slate-500">완료</p>
          <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
        </div>
      </div>

      {/* 필터 및 등록 버튼 */}
      <div className="bg-white rounded-lg p-4 border flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">상태:</span>
            <select 
              className="px-2 py-1 border rounded text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">전체</option>
              <option value="pending">대기</option>
              <option value="inProgress">진행중</option>
              <option value="completed">완료</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">유형:</span>
            <select 
              className="px-2 py-1 border rounded text-sm"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="all">전체</option>
              <option value="OOS">OOS</option>
              <option value="OOC">OOC</option>
              <option value="OOT">OOT</option>
              <option value="SEC">SEC</option>
            </select>
          </div>
        </div>
        <button 
          onClick={() => setShowModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 flex items-center gap-2"
        >
          <Activity size={14} />
          신규 등록
        </button>
      </div>

      {/* 탭 */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="flex border-b">
          <button 
            onClick={() => setActiveTab('list')}
            className={'px-6 py-3 text-sm font-medium ' + (activeTab === 'list' ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:bg-slate-50')}
          >
            목록 ({filteredData.length})
          </button>
          <button 
            onClick={() => setActiveTab('timeline')}
            className={'px-6 py-3 text-sm font-medium ' + (activeTab === 'timeline' ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:bg-slate-50')}
          >
            타임라인
          </button>
        </div>

        {/* 목록 탭 */}
        {activeTab === 'list' && (
          <div className="divide-y">
            {filteredData.map(item => (
              <div 
                key={item.id} 
                className="p-4 hover:bg-slate-50 cursor-pointer"
                onClick={() => setSelectedItem(item)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="flex flex-col gap-1">
                      {getTypeBadge(item.type)}
                      {getPriorityBadge(item.priority)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{item.title}</span>
                        <span className="text-xs text-slate-400">{item.id}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span>품목: {item.product}</span>
                        <span>항목: {item.item}</span>
                        <span>LOT: {item.lot}</span>
                        {item.value !== '-' && <span className="text-red-500">측정값: {item.value}</span>}
                      </div>
                      {item.cause && (
                        <p className="text-sm text-slate-600 mt-1">원인: {item.cause}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    {getStatusBadge(item.status)}
                    <p className="text-xs text-slate-400 mt-1">등록: {item.date}</p>
                    <p className="text-xs text-slate-400">담당: {item.owner}</p>
                    <p className="text-xs text-slate-400">완료예정: {item.dueDate}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 타임라인 탭 */}
        {activeTab === 'timeline' && (
          <div className="px-6 py-4">
            <div className="relative max-w-4xl mx-auto">
              {improvements.flatMap(item => 
                item.history.map((h, i) => ({ ...h, itemId: item.id, title: item.title, type: item.type }))
              ).sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 15).map((event, idx) => (
                <div key={idx} className="flex gap-3 mb-3">
                  <div className="flex flex-col items-center shrink-0">
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-500 mt-1"></div>
                    {idx < 14 && <div className="w-0.5 flex-1 bg-slate-200 mt-1"></div>}
                  </div>
                  <div className="flex-1 pb-3 border-b border-slate-100 last:border-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getTypeBadge(event.type)}
                        <span className="text-sm font-medium">{event.action}</span>
                      </div>
                      <span className="text-xs text-slate-400">{event.date}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{event.title} • {event.by}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 신규등록 모달 */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => { setShowModal(false); resetForm(); }}>
          <div className="bg-white rounded-lg w-[600px] max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <span className="font-medium text-lg">개선대책 신규 등록</span>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">유형</label>
                  <select value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})} className="w-full px-3 py-2 border rounded">
                    <option value="OOS">OOS (규격 초과)</option>
                    <option value="OOC">OOC (관리한계 초과)</option>
                    <option value="OOT">OOT (경향 이상)</option>
                    <option value="SEC">SEC (고객규격)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">우선순위</label>
                  <select value={formData.priority} onChange={e => setFormData({...formData, priority: e.target.value})} className="w-full px-3 py-2 border rounded">
                    <option value="high">긴급</option>
                    <option value="medium">보통</option>
                    <option value="low">낮음</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">제목</label>
                <input type="text" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} className="w-full px-3 py-2 border rounded" placeholder="이상 발생 제목" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">품목</label>
                  <select value={formData.product} onChange={e => setFormData({...formData, product: e.target.value})} className="w-full px-3 py-2 border rounded">
                    <option>C4F8</option>
                    <option>CHF3</option>
                    <option>CF4</option>
                    <option>C3F6</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">분석항목</label>
                  <select value={formData.item} onChange={e => setFormData({...formData, item: e.target.value})} className="w-full px-3 py-2 border rounded">
                    <option>H2O</option>
                    <option>N2</option>
                    <option>CO2</option>
                    <option>Total OFC</option>
                    <option>O2+Ar</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">LOT</label>
                  <input type="text" value={formData.lot} onChange={e => setFormData({...formData, lot: e.target.value})} className="w-full px-3 py-2 border rounded" placeholder="26Y3A61C3I" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">CYL</label>
                  <input type="text" value={formData.cyl} onChange={e => setFormData({...formData, cyl: e.target.value})} className="w-full px-3 py-2 border rounded" placeholder="21ENKE25728" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">측정값</label>
                  <input type="text" value={formData.value} onChange={e => setFormData({...formData, value: e.target.value})} className="w-full px-3 py-2 border rounded" placeholder="8.5" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">한계값</label>
                  <input type="text" value={formData.limit} onChange={e => setFormData({...formData, limit: e.target.value})} className="w-full px-3 py-2 border rounded" placeholder="8.0" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">원인 분석</label>
                <textarea value={formData.cause} onChange={e => setFormData({...formData, cause: e.target.value})} className="w-full px-3 py-2 border rounded" rows={2} placeholder="원인 분석 내용" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">조치 내용</label>
                <textarea value={formData.action} onChange={e => setFormData({...formData, action: e.target.value})} className="w-full px-3 py-2 border rounded" rows={2} placeholder="조치 내용 입력" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">담당자</label>
                  <input type="text" value={formData.owner} onChange={e => setFormData({...formData, owner: e.target.value})} className="w-full px-3 py-2 border rounded" placeholder="담당자명" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">완료예정일</label>
                  <input type="date" value={formData.dueDate} onChange={e => setFormData({...formData, dueDate: e.target.value})} className="w-full px-3 py-2 border rounded" />
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-slate-50 flex justify-end gap-2">
              <button onClick={() => { setShowModal(false); resetForm(); }} className="px-4 py-2 border rounded text-sm hover:bg-white">취소</button>
              <button onClick={handleAddNew} disabled={!formData.title} className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50">등록</button>
            </div>
          </div>
        </div>
      )}

      {/* 상세 모달 */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => { setSelectedItem(null); setEditMode(false); }}>
          <div className="bg-white rounded-lg w-[700px] max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getTypeBadge(selectedItem.type)}
                <span className="font-medium text-lg">{selectedItem.title}</span>
              </div>
              <button onClick={() => { setSelectedItem(null); setEditMode(false); }} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <div className="p-6 space-y-4">
              {/* 기본 정보 */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">관리번호</p>
                  <p className="font-medium">{selectedItem.id}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">상태</p>
                  <p>{getStatusBadge(selectedItem.status)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">우선순위</p>
                  <p>{getPriorityBadge(selectedItem.priority)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">품목 / 항목</p>
                  <p className="font-medium">{selectedItem.product} / {selectedItem.item}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">LOT / CYL</p>
                  <p className="font-medium">{selectedItem.lot} / {selectedItem.cyl}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">측정값 / 한계</p>
                  <p className="font-medium text-red-600">{selectedItem.value} / {selectedItem.limit}</p>
                </div>
              </div>

              {/* 원인 분석 */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">원인 분석</p>
                <div className="p-3 bg-yellow-50 rounded border border-yellow-200">
                  <p className="text-sm">{selectedItem.cause || '분석 중...'}</p>
                </div>
              </div>

              {/* 조치 내용 */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">조치 내용</p>
                <div className="p-3 bg-blue-50 rounded border border-blue-200">
                  <pre className="text-sm whitespace-pre-wrap">{selectedItem.action || '조치 계획 수립 중...'}</pre>
                </div>
              </div>

              {/* 효과 확인 */}
              {selectedItem.effect && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">효과 확인</p>
                  <div className="p-3 bg-green-50 rounded border border-green-200">
                    <p className="text-sm">{selectedItem.effect}</p>
                  </div>
                </div>
              )}

              {/* 이력 */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">처리 이력</p>
                <div className="border rounded">
                  {selectedItem.history.map((h, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 border-b last:border-b-0 text-sm">
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-400">{h.date}</span>
                        <span>{h.action}</span>
                      </div>
                      <span className="text-xs text-slate-500">{h.by}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 담당/일정 */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-sm">
                  <span className="text-slate-500">담당자:</span> <span className="font-medium">{selectedItem.owner}</span>
                </div>
                <div className="text-sm">
                  <span className="text-slate-500">완료예정:</span> <span className="font-medium">{selectedItem.dueDate}</span>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-slate-50 flex justify-end gap-2">
              {editMode ? (
                <>
                  <button onClick={saveEdit} className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">저장</button>
                  <button onClick={() => setEditMode(false)} className="px-4 py-2 border rounded text-sm hover:bg-white">취소</button>
                </>
              ) : (
                <>
                  <button onClick={startEdit} className="px-4 py-2 border rounded text-sm hover:bg-white">수정</button>
                  {selectedItem.status !== 'completed' && (
                    <button onClick={markComplete} className="px-4 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700">완료 처리</button>
                  )}
                  <button onClick={() => { setSelectedItem(null); setEditMode(false); }} className="px-4 py-2 bg-slate-600 text-white rounded text-sm hover:bg-slate-700">닫기</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 고객요구사항 컴포넌트
const CustomerRequirement = () => {
  const [customers] = useState([
    { id: 'SEC', name: '삼성전자', specs: 12 },
    { id: 'SKH', name: 'SK하이닉스', specs: 8 },
  ]);
  const [selectedCustomer, setSelectedCustomer] = useState('SEC');
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [formData, setFormData] = useState({ customer: 'SEC', title: '', desc: '', status: 'pending' });

  const customerSpecs = {
    'SEC': [
      { item: 'H2O', usl: 5, lsl: 0, rule: 'SEC-R1,R3', note: '수분 관리 강화' },
      { item: 'Total OFC', usl: 500, lsl: 0, rule: 'SEC-R5', note: 'Cpk 1.67 이상 요구' },
      { item: 'N2', usl: 15, lsl: 0, rule: '-', note: '표준 규격' },
      { item: 'O2+Ar', usl: 5, lsl: 0, rule: 'SEC-R2', note: '2σ 경고' },
      { item: 'Metal Total', usl: 10, lsl: 0, rule: '-', note: '금속 불순물 총량' },
    ],
    'SKH': [
      { item: 'H2O', usl: 8, lsl: 0, rule: '-', note: '표준 규격 적용' },
      { item: 'Total OFC', usl: 900, lsl: 0, rule: '-', note: '표준 규격' },
      { item: 'HF', usl: 0.5, lsl: 0, rule: 'SEC-R3', note: '규격 근접 경고' },
    ],
  };

  const [requirements, setRequirements] = useState([
    { id: 'REQ-001', customer: 'SEC', date: '2026-01-10', title: 'H2O 규격 강화 요청', status: 'completed', desc: 'USL 8 → 5로 변경 요청' },
    { id: 'REQ-002', customer: 'SEC', date: '2026-01-12', title: 'Cpk 목표값 상향', status: 'inProgress', desc: '1.33 → 1.67 상향 요청' },
    { id: 'REQ-003', customer: 'SKH', date: '2026-01-15', title: 'HF 항목 추가', status: 'pending', desc: 'CoA에 HF 항목 추가 요청' },
  ]);

  const handleRegister = () => {
    if (!formData.title.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }
    const newId = `REQ-${String(requirements.length + 1).padStart(3, '0')}`;
    const newReq = {
      id: newId,
      customer: formData.customer,
      date: new Date().toISOString().split('T')[0],
      title: formData.title,
      status: formData.status,
      desc: formData.desc
    };
    setRequirements([newReq, ...requirements]);
    setFormData({ customer: 'SEC', title: '', desc: '', status: 'pending' });
    setShowRegisterModal(false);
  };

  return (
    <div className="space-y-4">
      {/* 고객사 선택 */}
      <div className="bg-white rounded-lg p-4 border">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium">고객사 선택:</span>
          <div className="flex gap-2">
            {customers.map(c => (
              <button
                key={c.id}
                onClick={() => setSelectedCustomer(c.id)}
                className={'px-4 py-2 rounded-lg text-sm ' + 
                  (selectedCustomer === c.id ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}
              >
                {c.name}
                <span className="ml-2 text-xs opacity-70">({c.specs})</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* 고객 규격 */}
        <div className="bg-white rounded-lg border">
          <div className="px-4 py-3 border-b">
            <span className="font-medium">{customers.find(c => c.id === selectedCustomer)?.name} 규격</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                {['항목', 'USL', 'LSL', '적용 Rule', '비고'].map(h => (
                  <th key={h} className="px-3 py-2 text-left text-xs font-medium text-slate-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(customerSpecs[selectedCustomer] || []).map((spec, i) => (
                <tr key={i} className="border-t hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium">{spec.item}</td>
                  <td className="px-3 py-2">{spec.usl}</td>
                  <td className="px-3 py-2">{spec.lsl}</td>
                  <td className="px-3 py-2">
                    {spec.rule !== '-' && <span className="px-1.5 py-0.5 bg-cyan-100 text-cyan-700 rounded text-xs">{spec.rule}</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">{spec.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 요구사항 이력 */}
        <div className="bg-white rounded-lg border">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <span className="font-medium">요구사항 이력</span>
            <button onClick={() => setShowRegisterModal(true)} className="px-3 py-1 bg-blue-600 text-white rounded text-xs">등록</button>
          </div>
          <div className="divide-y">
            {requirements.map(req => (
              <div key={req.id} className="p-3 hover:bg-slate-50">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">{req.id}</span>
                    <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{req.customer}</span>
                  </div>
                  <span className={'px-2 py-0.5 rounded text-xs ' + 
                    (req.status === 'completed' ? 'bg-green-100 text-green-700' : 
                     req.status === 'inProgress' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700')}>
                    {req.status === 'completed' ? '완료' : req.status === 'inProgress' ? '진행중' : '대기'}
                  </span>
                </div>
                <p className="text-sm font-medium">{req.title}</p>
                <p className="text-xs text-slate-500">{req.desc}</p>
                <p className="text-xs text-slate-400 mt-1">{req.date}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 등록 모달 */}
      {showRegisterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[500px] shadow-xl">
            <h3 className="text-lg font-bold mb-4">고객 요구사항 등록</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-600 mb-1">고객사</label>
                <select 
                  value={formData.customer} 
                  onChange={e => setFormData({...formData, customer: e.target.value})}
                  className="w-full px-3 py-2 border rounded text-sm"
                >
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-slate-600 mb-1">제목 <span className="text-red-500">*</span></label>
                <input 
                  type="text"
                  value={formData.title}
                  onChange={e => setFormData({...formData, title: e.target.value})}
                  className="w-full px-3 py-2 border rounded text-sm"
                  placeholder="요구사항 제목 입력"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-600 mb-1">내용</label>
                <textarea 
                  value={formData.desc}
                  onChange={e => setFormData({...formData, desc: e.target.value})}
                  className="w-full px-3 py-2 border rounded text-sm"
                  rows={3}
                  placeholder="상세 내용 입력"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-600 mb-1">상태</label>
                <select 
                  value={formData.status} 
                  onChange={e => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border rounded text-sm"
                >
                  <option value="pending">대기</option>
                  <option value="inProgress">진행중</option>
                  <option value="completed">완료</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowRegisterModal(false)} className="px-4 py-2 border rounded text-sm hover:bg-slate-50">취소</button>
              <button onClick={handleRegister} className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">등록</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 사용자 권한관리 컴포넌트
const Permission = () => {
  return (
    <div className="flex items-center justify-center h-96">
      <div className="bg-white rounded-lg p-12 border text-center shadow-sm">
        <Users size={64} className="mx-auto text-slate-300 mb-4"/>
        <h2 className="text-xl font-bold text-slate-700 mb-2">사용자 권한관리</h2>
        <p className="text-slate-500">현재 준비중입니다.</p>
        <p className="text-sm text-slate-400 mt-2">추후 업데이트 예정입니다.</p>
      </div>
    </div>
  );
};

const ACCESS_PASSWORD = 'damk2026!';

const LockScreen = ({ onUnlock }) => {
  const [pw, setPw] = useState('');
  const [error, setError] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (pw === ACCESS_PASSWORD) {
      sessionStorage.setItem('damk_unlocked', 'true');
      onUnlock();
    } else {
      setError(true);
      setPw('');
      setTimeout(() => setError(false), 2000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0C1A3A 0%, #1E3A6E 50%, #0C1A3A 100%)' }}>
      <div className="w-full max-w-sm">
        {/* 로고 영역 */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-4" style={{ background: 'rgba(255,255,255,0.1)' }}>
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <rect x="2" y="18" width="36" height="20" rx="3" fill="#4A9FE0" opacity="0.9"/>
              <rect x="10" y="10" width="20" height="12" rx="2" fill="none" stroke="#4A9FE0" strokeWidth="2.5"/>
              <circle cx="20" cy="28" r="3" fill="white"/>
              <rect x="19" y="28" width="2" height="5" fill="white"/>
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white tracking-wide">DAMK SPC System</h1>
          <p className="text-blue-300 text-sm mt-1">SPC Trend 관리 시스템</p>
        </div>

        {/* 로그인 카드 */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <h2 className="text-lg font-bold text-slate-800 mb-1">시스템 접속</h2>
          <p className="text-sm text-slate-400 mb-6">접근 권한이 있는 사용자만 이용 가능합니다.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">비밀번호</label>
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'}
                  value={pw}
                  onChange={e => setPw(e.target.value)}
                  placeholder="비밀번호를 입력하세요"
                  className={'w-full px-4 py-3 border rounded-lg text-sm pr-10 outline-none transition-all ' +
                    (error ? 'border-red-400 bg-red-50 shake' : 'border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100')}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPw ? (
                    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
              {error && <p className="text-xs text-red-500 mt-1.5">비밀번호가 올바르지 않습니다.</p>}
            </div>

            <button
              type="submit"
              className="w-full py-3 rounded-lg text-white font-medium text-sm transition-all hover:opacity-90 active:scale-95"
              style={{ background: 'linear-gradient(90deg, #1E3A6E, #2563EB)' }}
            >
              접속하기
            </button>
          </form>

          <div className="mt-6 pt-4 border-t text-center">
            <p className="text-xs text-slate-400">© 2026 DAIKIN Advanced Materials Korea</p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-6px); }
          80% { transform: translateX(6px); }
        }
        .shake { animation: shake 0.4s ease; }
      `}</style>
    </div>
  );
};

export default function App() {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem('damk_unlocked') === 'true');
  const [menu, setMenu] = useState('dashboard');
  const [interlockLogs, setInterlockLogs] = useState([
    { id: 1, datetime: '2026-01-17 09:15:32', type: 'OOC', item: 'H2O', lot: '26Y3A5ZP31', cyl: '21ENKE25728', value: 6.85, action: '승인해제', approvalType: '파트장', approver: '김부장', handler: '이석진', reason: '분석 오류 확인' },
    { id: 2, datetime: '2026-01-16 14:22:18', type: 'OOS', item: 'Total OFC', lot: '26Y3C10B3M', cyl: '21ENKE26010', value: 720, action: '조치해제', approvalType: '', approver: '', handler: '노민석', reason: '재분석 후 정상 확인' },
  ]);

  if (!unlocked) return <LockScreen onUnlock={() => setUnlocked(true)} />;
  
  const addInterlockLog = (log) => {
    setInterlockLogs([{ id: Date.now(), datetime: new Date().toLocaleString('ko-KR'), ...log }, ...interlockLogs]);
  };
  
  const titles = {
    dashboard:'대시보드',
    master:'기본정보관리',
    monitoring:'모니터링',
    spc:'SPC 분석',
    trend:'품질경향분석',
    summary:'품질 집계',
    improve:'개선대책',
    customer:'고객요구사항',
    permission:'사용자 권한관리'
  };
  const render = () => {
    switch(menu) {
      case 'dashboard': return <Dashboard onNavigate={setMenu} onInterlockLog={addInterlockLog}/>;
      case 'master': return <Master/>;
      case 'monitoring': return <Monitoring interlockLogs={interlockLogs}/>;
      case 'spc': return <SPC/>;
      case 'trend': return <TrendAnalysis/>;
      case 'summary': return <Summary/>;
      case 'improve': return <Improvement/>;
      case 'customer': return <CustomerRequirement/>;
      case 'permission': return <Permission/>;
      default: return <div className="bg-white rounded-lg p-8 border text-center"><Settings size={40} className="mx-auto text-slate-400 mb-2"/><p className="font-medium">준비 중</p></div>;
    }
  };
  return (
    <div className="flex min-h-screen bg-slate-100" style={globalStyle}>
      <Sidebar menu={menu} setMenu={setMenu}/>
      <div className="flex-1 flex flex-col">
        <Header title={titles[menu]||'대시보드'}/>
        <main className="flex-1 p-4 overflow-auto">{render()}</main>
      </div>
    </div>
  );
}
